/**
 * i18n.js — lightweight internationalization for the V2 visualizer.
 *
 * Three locales: en (English), pt (Brazilian Portuguese), es (Spanish).
 * The choice is persisted in localStorage ('kv2_lang') and exposed through a
 * React context. `useI18n()` returns { t, lang, setLang, flag, otherFlag }.
 *
 * Key terms stay UNIVERSAL across languages (File, Ball, Cone, Target, Zone,
 * Player, Home, Away, Orbit, Pan, Zoom, Play, AUTO, ITEM, etc.) so navigation
 * stays intuitive — only the action labels and descriptions are translated.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const en = {
  'lang.name': 'English',
  'lang.flag': '🇬🇧',

  'menu.new': '＋ New',
  'menu.save': '💾 Save',
  'menu.saveAs': '💾 Save As…',
  'menu.load': '📂 Open & manage scenarios',
  'menu.loadPublic': '🌍 Load from Public',
  'scenario.manageHint': 'Tap a card to open it · ✎ rename · 🌍 share · 🗑 delete',
  'menu.account': 'Account:',
  'menu.upgrade': '⬆ Upgrade',
  'menu.upgradePrice': '⬆ Upgrade — {price}/mo',
  'account.upgradePrice': '⬆ Upgrade to Premium — {price}/mo',
  'menu.logout': '⎋ Logout',
  'menu.login': '🔑 Login / Sign up',
  'menu.language': 'Language',
  'menu.mobileApps': 'Get the app',
  'menu.androidApp': 'Android app',
  'menu.iosApp': 'iOS app',
  'menu.iosComingSoon': 'Coming soon',
  'menu.account.open': '👤 Account…',

  'account.title': 'Account',
  'account.nameTitle': 'Display name',
  'account.save': 'Save',
  'account.nameDone': 'Name updated',
  'account.passwordTitle': 'Change password',
  'account.currentPw': 'Current password',
  'account.newPw': 'New password (min 6)',
  'account.confirmPw': 'Confirm new password',
  'account.changePw': 'Change password',
  'account.shortPw': 'New password must be at least 6 characters',
  'account.pwMismatch': 'New passwords do not match',
  'account.pwDone': 'Password changed',
  'account.subscription': 'Subscription',
  'account.premiumOn': 'Premium ⭐',
  'account.freePlan': 'Free plan',
  'account.manage': 'Manage subscription',
  'account.upgrade': '⬆ Upgrade to Premium',
  'account.signOut': '⎋ Sign out',
  'account.deleteTitle': 'Delete account',
  'account.deleteWarn': 'Your account and data are permanently deleted after a 30-day grace period. Store subscriptions must be cancelled in Apple/Google.',
  'account.deletePw': 'Current password',
  'account.deleteConfirm': 'Type DELETE to confirm',
  'account.deleteBtn': 'Request deletion',
  'account.deleteCancel': 'Cancel',
  'account.deleteDone': 'Deletion requested — scheduled for',
  'menu.teams': '👥 Teams…',
  'teams.title': 'Teams',
  'teams.new': '+ New team',
  'teams.none': 'No teams in this account yet.',
  'confirm.cancel': 'Cancel',
  'confirm.deleteScenario': 'Delete scenario',
  'confirm.deleteScenarioBody': '“{name}” and every branch inside it are removed for good. Undo does not reach a saved file.',
  'confirm.deleteTeam': 'Delete team',
  'confirm.deleteTeamBody': '“{name}” — its squad, colours, badge and kit textures are removed for good. Scenarios you already built keep their player slots.',
  'confirm.discardTitle': 'Unsaved changes',
  'confirm.discardBody': 'Leave this team and lose the changes you have not saved?',
  'confirm.discard': 'Discard changes',
  'name.saveCopy': 'Save copy',
  'name.saveAsHint': 'The original stays untouched — this becomes a new scenario in your account.',
  'name.renameScenario': 'Rename scenario',
  'name.renameHint': 'The name you see in Load scenario and in the File menu.',
  'scenario.rename': 'Rename',
  'scenario.makePublic': 'Make public',
  'scenario.makePrivate': 'Make private',
  'scenario.loadTitle': 'Load scenario',
  'scenario.refresh': 'Refresh',
  'scenario.refreshHint': 'Re-read the scenarios saved in your account',
  'scenario.loading': 'Loading…',
  'scenario.noneYet': 'Nothing saved in this account yet.',
  'scenario.newOne': 'New scenario',
  'scenario.publicBadge': 'public',
  'login.signIn': 'Sign In',
  'login.register': 'Register',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.newPassword': 'New Password',
  'login.orgName': 'Account / Organization Name',
  'login.orgPlaceholder': 'e.g. Barcelona FC',
  'login.forgot': 'Forgot password?',
  'login.forgotTitle': 'Forgot Password',
  'login.forgotBody': "Enter your email and we'll send you a reset link.",
  'login.sendLink': 'Send Reset Link',
  'login.sending': 'Sending…',
  'login.back': 'Back to Sign In',
  'login.resetTitle': 'Reset Password',
  'login.resetBody': 'Enter your new password below.',
  'login.reset': 'Reset Password',
  'login.resetting': 'Resetting…',
  'login.create': 'Create Account',
  'login.creating': 'Creating…',
  'login.signingIn': 'Signing in…',
  'login.emailRequired': 'Email is required',
  'login.nameRequired': 'Name is required',
  'login.pwRequired': 'New password is required',
  'login.pwShort': 'Password must be at least 6 characters',
  'login.resetSent': 'If an account exists for that email, a reset link has been sent.',
  'login.guides': 'Read the guides',
  'login.close': 'Close',
  'info.ok': 'OK',
  'info.continue': 'Continue',
  'info.adFree': 'Go ad-free with Premium',
  'teams.playersCount': '{n} players',
  'teams.shared': 'shared',
  'teams.selectHome': 'Select Home Team',
  'teams.selectAway': 'Select Away Team',
  'teams.delete': 'Delete team',
  'teams.save': '💾 Save team',
  'teams.unsaved': 'unsaved changes',
  'teams.name': 'Team name',
  'teams.shirt': 'Shirt',
  'teams.shorts': 'Shorts',
  'teams.socks': 'Socks',
  'teams.badge': 'Badge',
  'teams.textures': 'Kit textures (custom shirt/shorts/socks)',
  'teams.logo': 'Upload logo',
  'teams.player': 'Player name',
  'teams.number': 'Number',
  'teams.position': 'Position',
  'teams.skin': 'Skin / face',
  'teams.hair': 'Hair',
  'teams.applyHome': 'Apply to Home',
  'teams.applyAway': 'Apply to Away',
  'teams.saved': 'Team saved',
  'teams.hint': 'Pick a team to edit. Apply it to a side to use it on the pitch.',
  'teams.numberColor': 'Number colour',
  'teams.numberColorHint': 'The number on the back of the shirt — kit colours come from the textures.',
  'teams.templates': 'Kit templates',
  'teams.templatesHint': 'Download the print layout, design on it, upload it back as the matching texture.',
  'teams.download': '↓ {name}',
  'teams.customTex': 'custom',
  'teams.defaultTex': 'default',
  'teams.addPlayer': '+ Add player',
  'teams.removePlayer': 'Remove this player',
  'teams.squadCount': '{n} of {max}',
  'teams.squadFull': 'Squad is full — {max} players is the maximum',
  'teams.playerAdded': '{name} added — {n} in the squad',
  'teams.playerRemoved': '{name} removed — {n} left · the ↩ button undoes',
  'teams.squadMin': 'The squad needs at least one player',
  'look.title': 'Edit look',
  'look.loading': 'Loading the figure…',
  'look.hint': 'Pick a skin and a hairstyle — the figure wears this team’s kit.',
  'look.edit': '✎ Edit look',
  'look.editAria': 'Edit the look of {name}',
  'look.current': 'Now: {skin} · {hair}',
  'look.apply': 'Apply',
  'look.cancel': 'Cancel',
  'look.none': 'No styles loaded',
  'look.dragRotate': 'Drag the figure to turn it',
  'look.noPreview': 'Preview unavailable — the styles still apply.',
  'look.swatchAria': 'Choose {label}',
  'look.needSave': 'Applied — press Save to keep it',
  'menu.help': '❓ Help & Tutorials',
  'help.title': 'Help & Tutorials',
  'help.scSpace': 'play the chosen path / pause',
  'help.scPlayTo': 'play to next branch point',
  'help.scBranchPlay': 'play / pause current branch only',
  'help.scCam': 'camera: default · top · low',
  'help.scPlace': 'arm any placeable object',
  'help.scLineRepeat': 'drop another one the same step along (after two clicks)',
  'help.scTurnPlace': 'turn the row 90° at the same spacing',
  'help.tFollow': 'drag a player ONTO an OPPONENT — they mark them: tap the pitch where they should stand, or pick a spot and a gap',
  'help.tGroupMark': 'drag a GROUP onto an OPPONENT — everyone in it marks him, each from where they are standing · or open the menu on the group: 🎯 Mark an opponent… · 📏 one dial sets every gap',
  'help.tRide': 'drag a cone, target, zone or ball ONTO a player — it starts following him; drop it on open pitch to let go',
  'help.tHandles': 'tap a movement transition in the timeline to change its curve or loft',
  'help.scMirrorPlace': 'mirror the last placement to the other half',
  'help.scMirrorPlaceZ': 'mirror it to the other side instead',
  'help.scPlacePlayer': 'arm next benched home / away player',
  'help.scReArm': 're-arm last placed object type',
  'help.scCycle': 'cycle selection (⌥: camera jumps to it)',
  'help.scgPlayback': 'Playback',
  'help.scgPlacement': 'Placement',
  'help.scgTiming': 'Timing',
  'help.scgItems': 'Items',
  'help.scgBranches': 'Branches',
  'help.scgEditing': 'Editing',
  'help.scFocus': 'camera to selection',
  'help.scView': 'timeline ⇄ branches panel',
  'help.scHide': 'hide panels — full pitch',
  'help.scHideBall': 'hide / show the selected ball',
  'help.scArrows': 'step playhead (Shift = 1 s)',
  'help.scJump': 'jump to prev / next keyframe',
  'help.scNudge': 'shift nearest key in time',
  'help.guides': 'guides',
  'help.guideLoading': 'Loading the guide…',
  'help.guideFallback': 'The guide could not be loaded here — showing the full documentation site instead.',
  'help.guideOpenSite': 'full site ↗',
  'help.guideOpenSiteHint': 'Open the documentation site in a browser — it also carries the video walkthroughs',
  'help.videos': 'walkthroughs',
  'help.videosHint': 'Short video walkthroughs — hosted on YouTube',
  'help.videoNote': 'Short walkthroughs, hosted on YouTube. They load only when you press play.',
  'help.videoPlay': 'Play {title}',
  'help.videoWatch': 'on YouTube ↗',
  'help.scLandSame': "land this key on the nearest second another item keys · or ⇢ Land on… inside the key editor",
  'help.scHeight': 'key height ±0.5 m (⇧: 0.1)',
  'help.scKey': 'key current position at playhead',
  'help.scEnterEdit': 'edit key/segment at playhead',
  'help.coreLoop': 'The loop: select an item (N cycles) · , . walk its keys · Enter edits whatever is under the playhead · Esc backs out',
  'help.scAction': 'action picker for selection',
  'help.scRepeatAction': 'apply the action you just used (a group = every member)',
  'help.scAuto': 'toggle AUTO key recording',
  'help.scAttach': 'ball/zone/player ↔ nearest partner (⇧: detach)',
  'help.scShot': 'screenshot — share or save the current view',
  'help.scRec': 'record video — ⇧P again to stop',
  'help.scSeg': 'bend segment at playhead',
  'help.scMirror': 'mirror the selection flank ↔ flank (group · M then ⇧M turns it 180°)',
  'help.scMirrorEnd': 'mirror end ↔ end, across the halfway line',
  'help.scMirrorAll': 'mirror EVERY item in the branch, flank ↔ flank',
  'help.scMirrorAllEnd': 'mirror every item end ↔ end, across the halfway line',
  'help.scSelectKind': 'select every item of this kind (all cones, one team)',
  'help.scBendAmount': 'bend amount ±0.1 (⇧: 0.02)',
  'help.scLoft': 'toggle ball loft (arch)',
  'help.scLoftDial': 'loft height ±0.5 m',
  'help.scPeakDial': 'move the loft PEAK along the flight (⇧ = fine)',
  'help.scBend': 'drag orange handle to bend path',
  'help.scArch': 'drag violet handle to loft pass',
  'help.scArrow': 'toggle movement arrow',
  'help.scArrowColor': 'cycle arrow colour',
  'help.scZone': 'zone shape window · T again grabs vertex 1 (a group = every zone)',
  'help.scRadius': 'zone radius ±0.5 m · any other item: slide its spot ±0.5 m (⇧: 0.1)',
  'help.scVertexNudge': 'nudge grabbed zone vertex 0.25 m',
  'help.scVertexAdd': 'add a zone vertex at the grabbed edge (Del removes it)',
  'help.scZoneDel': 'delete zone window at playhead (a group = every zone)',
  'help.scBranch': 'add sub-branch to current branch',
  'help.scRename': 'rename current branch (focuses name)',
  'help.scClip': 'clip branch to last keyframe',
  'help.scSibling': 'add sibling branch',
  'help.scEnter': 'open current branch timeline',
  'help.scDelBranch': 'delete current branch (root protected)',
  'help.scDupBranch': 'duplicate branch + timeline',
  'help.scPick': 'open Nth sub-branch (⌥↑ parent)',
  'help.scSibs': 'walk to previous / next sibling branch',
  'help.scWalk': 'walk to parent / first child branch',
  'help.scPriority': 'raise / lower branch priority',
  'help.scHelp': 'open help & shortcuts',
  'help.scEsc': 'close prompt · editor · group · selection',
  // The master bar's HOLD used to be listed here, under Double-tap, even though it is
  // not a double-tap. A coach scanning the sheet for "hold" never found it, and the
  // sheet gives every other hold its own row (a key, a track, the name, empty pitch).
  // It lives on help.tMasterHold now; this row keeps only what a double-tap gets.
  'help.scTap': 'double-tap an item: the camera flies to it · double-tap open turf: back to the whole play · on the master bar: play from there',
  'help.scParent': 'jump to the parent branch',
  'help.scGroup': 'group players & items — drag one, all move',
  'help.scgTouch': 'Touch (phone / tablet)',
  'help.tTap': 'select · then drag to move it (AUTO records the key)',
  'help.tLane': 'tap a key, or the bar under it: that key / transition editor — the key side: Height · ◂ ▸ ▴ ▾ step the spot 0.5 m · ◂◂ 1 s to 1 s ▸▸ step the time · ⇢ Land on another item’s instant · The steps move… (just this moment, or this and everything after) · Delete key — the transition side: Style Straight / Left / Right · Curve · on a ball’s flight also Arch and Arch Peak',
  'help.tHold': 'hold an unselected item: add it to the group',
  // The scenario bar's hold is armed only for pointerType 'touch'. The fact was
  // already in the sheet — but filed under Double-tap, which is not the gesture, so a
  // coach scanning for "hold" walked past it. Every other hold has its own row; this
  // gives the master bar one. It names the HOLD and nothing else: the bar's tap and
  // double-tap doors belong to the rows that own those gestures (help.scTap), and a
  // fact written in two rows is a fact that can drift out of agreement.
  'help.tMasterHold': 'the master bar: press and HOLD a segment to play just that branch',
  'help.tMenu': 'tap a selected item again: its menu — only the verbs THIS thing can do: ✚ key here · ➜ arrows · ↝ bend · ⤴ loft · ↑↓ height · 🎯 mark · ✕ stop marking · 📏 all gaps · 📎 attach · 👻 hide the ball · ↻ again · ⧉ Select a group… · ⧉ Select this line · ⧉ Select all of this kind · ⧉ Next in line · ⧉ Turn the line · ⇄ mirror (one, the side, the whole pitch) · ✕ delete this action · 📋 copy · 📌 paste · ⧉ duplicate · 🗑 delete all (or hold it)',
  // The step-counting fact about the card, and no label on any entry says it: the
  // card does NOT close after the verbs that work "here". One card, several
  // instants — which is what animating one thing along a run actually needs.
  'help.tStay': 'the card stays open after ✚ key here, ↻ again, ⇄ mirror and keying an action — move the ruler and tap again; ✚ names the new instant',
  'help.tBall': 'hold a ball: select · hold again for its menu — 📎 attach is one entry in it',
  'help.tZone': 'hold a zone: its shape editor · drag a corner to move it · hold again to grab vertex 1 · + Add a corner (tap the pitch), − Remove one (tap a corner) · tap a player or the ball to attach that point',
  'help.tRuler': 'drag the ruler to move through time — it lands on a key when you get close · hold it: this branch’s timing — slower · quicker · ↔ open or close a gap after the playhead · end at the last key',
  'help.tKeyDel': 'hold a key on the strip: delete it — the twin of double-click, for a thumb',
  'help.tKeyMove': 'drag a key along its track: it moves in time — and lines up with a key it gets close to',
  'help.tUndo': 'the ↩ at the top of the pitch undoes the last change · the ↩ inside a message undoes just that one · ↪ puts it back',
  'help.tKeyAdd': 'hold an empty stretch of a track: that track’s key, written at the instant you touched',
  'help.tRename': 'hold the branch name in the strip header: rename it',
  'help.tPitch': 'hold an empty spot on the pitch: the view — only what THIS scene has: 🎥 from above · from the touchline · default · ➜ arrows for every item · ◼ zone shapes for every zone · 🎯 stop every mark · 📏 set every gap · ⤴⤵ all flights up or down',
  'help.tBranch': 'the branch tree on the left: tap a branch to select it · tap it again — or ✎ — to open its timeline · ⧉ duplicate a branch — its timeline, name and place, opened straight away · ⧉ copy this branch’s timeline · ⎘ paste it into another · + a sub-branch · + a sibling · ✂ clip · ▲▼ order · ✕ remove — the FIRST branch has no sibling and nothing to remove',
  'help.tEdge': 'drag the ruler’s right edge: stretch or trim the window',
  'help.tSpeed': 'tap the × chip on the playhead: cycle playback speed',
  'help.tPlace': 'hold the armed pitch: place a line of items',
  'help.tLineRepeat': 'lay another one the same step along — tap it again to walk the row, the card stays open',
  'help.tGroup': 'the card can gather the LINE this player is standing in, or the whole side — then ↑ · ↝ · 🗑 act on every one of them at once',
  'help.tAuto': 'the switch in the strip header: ON, moving anything records a position key at the current second · OFF, you move things and leave no keys behind',
  'help.tToolbar': 'the item +: arm a cone, target, ball or zone, then tap the pitch to put it down · a ✓ marks the type already armed — tap it again to cancel · a squad +: switch team, pick a formation, or arm a benched player · the − removes the selected item',
  'help.scSpeed': 'playback speed',
  'help.scSpeedReset': 'speed back to 1×',
  'help.scStretch': 'stretch / compress branch timing',
  'help.scPadBranch': 'pad / trim the branch length (keys stay put)',
  'help.scRipple': 'slide every key after the playhead (⌥drag · ⌥⇧ = 1 s)',
  'help.scCopy': 'copy / paste (route + state)',
  'help.scSaveOpen': 'save scenario / open browser',
  'help.scNew': 'new scenario (Ctrl+Z reverts)',
  'help.scBranchCopy': 'copy / paste branch timeline',
  'help.scDup': 'duplicate item',
  'help.scDelete': 'delete selection',
  'help.scDelGroup': 'delete whole group',
  'help.scUndo': 'undo / redo',

  'add.ball': '⚽ Ball',
  'add.cone': '🟠 Cone',
  'add.target': '🎯 Target',
  'add.polyZone': '▱ Polygon Zone',
  'add.circleZone': '⭕ Circle Zone',
  'add.dynamicZone': '◈ Dynamic Zone',
  'add.duplicate': '⧉ Duplicate selected',
  'tb.file': 'File',
  'tb.addHome': 'Add a home player',
  'tb.addAway': 'Add an away player',
  'tb.placingHome': 'Placing a home player — tap the pitch · tap the name again to cancel',
  'tb.placingAway': 'Placing an away player — tap the pitch · tap the name again to cancel',
  'tb.addItem': 'Add an item',
  'tb.armedItem': '{what} armed — tap the pitch · tap it again to cancel',
  'tb.removeItem': 'Remove the selected item',
  'tb.removeItemFirst': 'Remove the selected item — select something first',
  'tb.homeLogo': 'Home team logo',
  'tb.awayLogo': 'Away team logo',
  'tb.publicOffline': 'Connect to the internet to browse public scenarios',
  'tb.duplicateHint': 'Duplicate the selected item, laid out at an offset — tap it again for another',
  'tb.duplicatePlayer': 'Players sit in fixed squad slots — copy one and paste onto another slot',
  'tb.duplicateNeedItem': 'Duplicate — select a cone, target, zone or ball first',
  // The four line-layout verbs used to be toolbar entries here; they now live
  // in the item's own Action menu as itm.nextLineStep / itm.turnLine /
  // itm.mirrorHalf / itm.mirrorSide, where the spacing can be printed and the
  // target cannot be ambiguous.

  'modal.welcome.title': 'Welcome to KickViz',
  'modal.welcome.msg': "Welcome! Every account — Free included — has access to ALL features: teams, saving and loading scenarios, public scenarios, sharing and more.\n\nTo support the service, Free accounts see a short ad when saving or loading a scenario.\n\nPremium removes all ads — upgrade anytime from the File menu.",
  'modal.gotIt': 'Got it',
  'modal.ad.saveMsg': 'Thanks for using KickViz! Here is a quick ad — your scenario will be saved right after.',
  'modal.ad.loadMsg': 'Thanks for using KickViz! Here is a quick ad — then you can load your scenario.',
  'modal.ad.loadPublicMsg': 'Thanks for using KickViz! Here is a quick ad — then you can browse public scenarios.',
  'prompt.saveAsCopy': 'Name for the new scenario copy:',
  'view.2d': '2D',
  'view.3d': '3D',
  'view.orbit': 'Orbit',
  'view.pan': 'Pan',
  'view.zoomTo': 'Zoom to selected item — key: F · double-tap on touch',
  'view.names': 'Names',
  'view.namesTitle': 'Show player names over each player',
  'branch.from': 'FROM',
  'branch.current': 'CURRENT · SIBLINGS',
  'branch.possible': 'POSSIBLE NEXT',
  'branch.rootNothingBefore': 'Root — nothing before it',
  'branch.nothingBefore': 'Nothing before this branch',
  'branch.addSub': 'Add sub-branch',
  'branch.addSubHint': 'Continue from this branch’s end — key: B',
  'branch.addSibling': 'Add sibling branch',
  'branch.addSiblingHint': 'Create a new branch that continues from the same point as this one',
  'branch.duplicate': 'Duplicate this branch',
  'branch.duplicateHint': 'A copy of this branch — its timeline, its name and its place — opened and selected so you are editing it straight away',
  'branch.copy': 'Copy this branch’s timeline',
  'branch.paste': 'Paste the copied timeline into this branch',
  'branch.pasteEmpty': 'Copy a branch’s timeline first — the ⧉ button on its row',
  'branch.pasteSame': 'This is the branch you copied — choose another one to paste into',
  'branch.copiedToast': 'Copied the timeline of',
  'branch.pastedToast': 'Pasted timeline into “{name}” — sub-branches re-derived from its new end · the ↩ button reverts',
  'branch.edit': 'Open this branch’s timeline',
  'branch.up': 'Move up in priority',
  'branch.down': 'Move down in priority',
  'branch.remove': 'Remove this branch',
  'branch.masterPlay': 'Master Play',
  'branch.playToNext': 'Play to next branch',
  'branch.order': 'Priority',
  'branch.orderHint': 'Priority within its siblings — 1 plays first and is the default continuation',
  'branch.name': 'Branch name',
  'branch.defaultTag': 'DEFAULT',
  'branch.siblingNote': 'Order 1 is the default continuation when this split is reached.',
  'branch.noChildren': 'No sub-branches yet — add one to split the story at the end of this timeline',
  'branch.back': 'BACK',
  'branch.backTitle': 'Back to the scenario tree',
  'branch.lockedKey': 'Inherited from the previous branch — read only',
  'branch.lockedKeyToast': 'This branch starts where the previous one ended — its first key is read only. Drag the ruler (or press →) to step off 00:00 first.',
  'branch.lockedDragToast': 'This branch starts where the last one ended — step the playhead off 00:00, then drag',
  'branch.addedToast': 'Sub-branch added: “{name}” — now editing it · Add sub-branch puts another under “{parent}”',
  'branch.siblingAddedToast': 'Sibling branch added: “{name}” — now editing it · “+ Add sibling branch” makes another',
  'branch.samePoint': 'the same point',
  'branch.rowTitle': 'Select · a double-click, or a second tap, opens its timeline',
  'branch.chosen': 'Your pick for this split — part of the path the master timeline plays',
  'branch.subBranches': 'sub-branches',
  'branch.inTimeline': 'Currently open in the timeline',
  'branch.resize': 'Drag to resize the scenario tree panel',
  'branch.clipTime': 'Clip time — set this branch’s timeline length to its last keyframe',
  'branch.clippedTo': 'Timeline clipped to',
  'branch.clipNothing': 'This branch has no keyframes to clip to yet',
  'branch.masterPlaying': 'Master play',
  'master.title': 'Master timeline — the whole scenario',
  // The hold is armed ONLY for pointerType 'touch' (line: MasterTimeline startDrag),
  // and this title renders only where a hover exists — so the two never meet. The
  // chord stays primary for the device that reads this, and the touchscreen's door is
  // named as the other device's, not as an instruction this reader can follow.
  'master.hint': 'One segment per branch. Pull the handle to walk the whole scenario: it moves the tree and normalises each branch to its own length (no times involved). Double-tap or double-click a segment to play the path from there — hold ⇧ while double-clicking to play just that branch (on a touchscreen, press and hold the segment instead).',
  'master.defaultPick': 'The default (order 1) — you have not picked a way here · open the tree to choose one',
  'master.yourPick': 'Your pick at this fork — the master path follows it · picks live in THIS session only',
  'ball.title': 'Ball',
  'ball.colour': 'Colour',
  'ball.visible': 'Visible',
  'ball.close': 'Close ball settings',
  'ball.hiddenHint': 'Hidden — not drawn, cannot be tapped or clicked · tick “Visible” above to bring it back',
  'ball.attachHint': '📎 Attach to… is in the ball’s menu · drag the ball onto a player to pass',

 // What the ↩ button and the undo toast name the change they are about to put
 // back. These are DICTIONARY KEYS, not English words: the label travels on the
 // undo stack and is read out in whatever language the coach is using, so a
 // Portuguese screen can never say "Desfazer: position key". It also means
 // switching language mid-session cannot split one gesture into two undo steps,
 // because the stack compares keys, not display text.
  'undoVerb.edit': 'edit',
  'undoVerb.positionKey': 'position key',
  'undoVerb.attachKey': 'attachment key',
  'undoVerb.visualKey': 'visuals key',
  'undoVerb.actionKey': 'action key',
  'undoVerb.keyHeight': 'key height',
  'undoVerb.keyPosition': 'key position',
  'undoVerb.arrowKey': 'arrow key',
  'undoVerb.transition': 'transition',
  'undoVerb.arrowColour': 'arrow colour',
  'undoVerb.zoneShape': 'zone shape',
  'undoVerb.addBranch': 'add branch',
  'undoVerb.deleteBranch': 'delete branch',
  'undoVerb.clipBranchTime': 'clip branch time',
  'undoVerb.renameBranch': 'rename branch',
  'undoVerb.branchOrder': 'branch order',
  'undoVerb.branchPriority': 'branch priority',
  'undoVerb.pasteBranchTimeline': 'paste branch timeline',
  'undoVerb.addSiblingBranch': 'add sibling branch',
  'undoVerb.duplicateBranch': 'duplicate branch',
  'undoVerb.branchDuration': 'branch duration',
  'undoVerb.placeFormation': 'place formation',
  'undoVerb.itemHeight': 'item height',
  'undoVerb.zoneColour': 'zone colour',
  'undoVerb.ballProperties': 'ball properties',
  'undoVerb.zoneOpacity': 'zone opacity',
  'undoVerb.zoneMode': 'zone mode',
  'undoVerb.zoneRadius': 'zone radius',
  'undoVerb.zoneOpenClosed': 'zone open/closed',
  'undoVerb.zoneThickness': 'zone thickness',
  'undoVerb.zoneVertices': 'zone vertices',
  'undoVerb.zoneVertexLink': 'zone vertex link',
  'undoVerb.pasteRoute': 'paste route',
  'undoVerb.pasteItem': 'paste item',
  'undoVerb.newScenario': 'new scenario',
  'undoVerb.deleteKey': 'delete key',
  'undoVerb.deleteMoment': 'delete moment',
  'undoVerb.archPeak': 'arch peak',
  'undoVerb.attachBall': 'attach ball',
  'undoVerb.attachZone': 'attach zone',
  'undoVerb.attachObj': 'attach item',
  'undoVerb.attachGroup': 'attach group',
  'undoVerb.bendAmount': 'bend amount',
  'undoVerb.bendPath': 'bend path',
  'undoVerb.deleteGroup': 'delete group',
  'undoVerb.deleteZoneWindow': 'delete zone window',
  'undoVerb.deleteZoneWindows': 'delete zone windows',
  'undoVerb.zoneVisualScene': 'zone shapes',
  'undoVerb.detach': 'detach',
  'undoVerb.duplicateGroup': 'duplicate group',
  'undoVerb.duplicateItem': 'duplicate item',
  'undoVerb.groupArrowColour': 'group arrow colour',
  'undoVerb.groupArrows': 'group arrows',
  'undoVerb.groupHeight': "group height",
  'undoVerb.groupBendPath': 'group bend path',
  'undoVerb.groupPositionKeys': 'group position keys',
  'undoVerb.loftHeight': 'loft height',
  'undoVerb.loftToggle': 'loft toggle',
  'undoVerb.loftGroup': 'loft group',
  'undoVerb.markPlayer': 'mark player',
  'undoVerb.markGroup': 'mark group',
  'undoVerb.unmarkGroup': 'stop group marking',
  'undoVerb.markGroupGap': 'marker gaps',
  'undoVerb.mirrorEnd': 'mirror end',
  'undoVerb.mirrorFlank': 'mirror flank',
  'undoVerb.moveKeyInTime': 'move key in time',
  'undoVerb.pass': 'pass',
  'undoVerb.rippleKeys': 'ripple keys',
  'undoVerb.setAction': 'set action',
  'undoVerb.setActions': 'set actions',
  'undoVerb.stretchTimeline': 'stretch timeline',
  'undoVerb.zoneVertexAdd': 'add zone vertex',
  'undoVerb.zoneVertexRemove': 'remove zone vertex',
  'undo.title': 'Undo the last change',
  'vp.undoWhat': 'Undo: {what}',
  'vp.undoHintOne': '{what} · 1 step back{limit}',
  'vp.undoHintMany': '{what} · {n} steps back{limit}',
  'vp.undoLimit': ' (the limit — older edits are gone)',
  'vp.redoWhat': 'Redo: {what}',
  'vp.redoHintOne': '{what} · 1 step forward',
  'vp.redoHintMany': '{what} · {n} steps forward',
  'redo.title': 'Redo the change you undid',

  'tl.timeline': 'TIMELINE',
  'tl.pause': 'Pause',
  'tl.durationHint': 'Timeline length in seconds — click to edit',
  'tl.hint': 'Scroll or click the ruler to move through time · drag its edge to resize',
  'tl.keyMovement': 'Key movement — pin where this item stands at the playhead',
  'tl.keyMovementPlayer': 'Key movement — pin where this player stands at the playhead',
  'tl.keyAction': 'Key action — add an action at the playhead',
  'cap.button': 'Capture',
  'cap.hint': 'Capture a still, or record the pitch as video',
  'cap.screenshot': 'Screenshot',
  'cap.record': 'Record video',
  'cap.stop': 'Stop recording',
  'cap.saving': 'Saving…',
  'cap.recordingBody': 'Recording the pitch',
  'cap.recordingToast': 'Recording — tap ● the camera button again to stop',
  'seg.currentColour': 'Current colour — tap to change',
  'seg.defaultColour': 'Set to the default arrow colour',
  'seg.defaultColourAria': 'Set default colour',
  'teams.enterNext': 'Enter — next player',
  'teams.pickTitle': 'Select a team',
  'zv.bindHint': 'Drag a vertex dot straight onto a player or the ball and it follows them. Drop a bound vertex back on the zone to free it.',
  'ad.label': 'Advertisement',
  'ad.close': 'Close advertisement',
  'tl.keyGraphic': 'Key arrows — turn the path arrows on or off at the playhead',
  'tl.keyAttachment': 'Key attachment — attach this to a player at the playhead',
  'tl.keyVisual': 'Key visuals — start a zone-visuals window at the playhead',
  'tl.posKey': 'Position key at {t} — tap to set its height · drag to move it in time',
  'tl.keyHoldDelete': 'Hold the key to delete it',
  'tl.noPlayers': 'No players yet.',
  'tl.addPlayersHint': 'Use ADD PLAYER HOME / AWAY to place players.',

  'itm.item': 'ITEM',
  'itm.ball': 'Ball',
  'itm.cone': 'Cone',
  'itm.target': 'Target',
  'itm.zone': 'Zone',
  'itm.player': 'Player',
  'itm.remove': 'Remove',
  'itm.copy': 'Copy',
  'itm.paste': 'Paste',
  'itm.attach': 'Attach',
  'itm.detach': 'Detach',
  'itm.height': 'Height',
  'itm.arcType': 'Path',
  'itm.color': 'Colour',
  'itm.attachedTo': 'Attached:',

  // ── The item's long-press menu: the verbs a coach reaches for ON the pitch.
  // Every entry states the instant or the amount it acts on, so the wording
  // carries parameters rather than being glued together at the call site.
  'itm.arrowsShow': '➜ Arrows on ({t})',
  'itm.arrowColour': '➜ Arrow colour…',
  'itm.arrowColourAll': '➜ Colour all {n} arrows alike…',
  'itm.arrowsHide': '➜ Arrows off ({t})',
  // The GROUP forms. The verb already acts on every member the menu speaks for,
  // so the label has to count them: read off the selected player alone it could
  // say "off" while turning two others ON.
  'itm.arrowsShowMany': '➜ Arrows on for all {n} ({t})',
  'itm.arrowsHideMany': '➜ Arrows off for all {n} ({t})',
  'itm.arrowsSomeOn': ' — {k} already on',
  'itm.arrowsNoLane': ' — {n} has no arrow lane',
  'itm.bendRun': '↝ Bend this run exactly…',
  'itm.bendAll': '↝ Bend all {n} runs alike…',
  'itm.bendPass': '↝ Bend this pass exactly…',
  'itm.copyIt': '📋 Copy',
  // In a card that speaks for several members, an entry that touches ONE has to
  // say so — every other line in that card says "all 2".
  'itm.onlyThis': ' — this item only',
  'itm.allOfThese': ' — all {n}',
  'itm.pasteHere': '📌 Paste {name} here',
  'itm.duplicateIt': '⧉ Duplicate',
  'itm.nextLineStep': '⧉ Next in line ({m} m)',
  'itm.turnLine': '⧉ Turn the line 90° ({m} m)',
  'itm.mirrorHalf': '⇄ Copy other half',
  'itm.mirrorSide': '⇄ Copy other side',
  'itm.mirrorFlank': '⇄ Mirror flank',
  'itm.mirrorEnd': '⇄ Mirror end ↔ end',
  'itm.mirrorPitch': '⇄ Whole pitch ({n})',
  'itm.mirrorMenu': '⇄ Mirror… ({n})',
  'itm.mirrorMenuTitle': 'Turn it over — which way?',
  'itm.mirrorMenuHint': 'Copy lays a NEW item down on the other side · mirror moves what is already there · tap one and the choices stay, so a pair takes two taps',
  'itm.selectAllKind': '⧉ Select all {kind} ({n})',
  'itm.selectLine': '⧉ Select this line ({n})',
  'itm.selectGroupMenu': '⧉ Select a group… ({n})',
  'itm.selectGroupTitle': 'Which group?',
  'itm.selectGroupHint': 'the line is the players standing in the same row as this one · the side is everyone on the team sheet · tap one and its card opens on the group',
  'itm.deleteAll': '🗑 Delete all {n}',
  'itm.menuHint': 'Pick an action — or the item verbs at the top of this menu.',

  // FOLLOW: a player shadowing another player. Their own movement keys stay
  // exactly where they were authored; the leader's travel, measured from the
  // moment the follow was requested, is added on top as an extra offset.
  'itm.markAsk': '🎯 Mark an opponent…',
  'itm.markAskMany': '🎯 Mark an opponent… ({n} players)',
  'itm.markGapMany': '📏 Set all gaps… ({n} markers)',
  'itm.keyHere': '✚ Key here ({t})',
  // The GROUP forms: the count is what the verb will write, and the short suffix
  // says why it is fewer than the group (the toast carries the full way out).
  'itm.keyMany': '✚ Key all {n} here ({t})',
  'itm.keyFollow': ' — {n} follow a carrier',
  'itm.keyFollowOne': ' — {n} follows a carrier',
  'itm.markChange': '🎯 Marking {who}',
  'itm.attachAsk': '📎 Attach to…',
  'itm.attachChange': '📎 On {who}',
  'itm.attachMany': '📎 Attach to… ({n} items)',
  'mark.title': 'Who does {who} mark?',
  'mark.changeTitle': '{who} marks {leader} — change the marked player?',
  'mark.pickHint': 'Tap an OPPONENT on the pitch — marking works across the divide',
  'mark.stop': 'Stop marking ({t})',
  'mark.self': '{who} cannot mark themselves — pick an opponent',
  'mark.cycle': '{who} already marks {leader} — one of the two has to stop first',
  'mark.teammate': 'Marking crosses the divide — {who} is a teammate',
  'mark.started': '{who} marks {leader} · position: {where} · {d} m out · from {t}',
  'mark.turning': '{who} moves from the {from} to the {where} around {leader} · arriving at {t}',
  'mark.stopped': '{who} stops marking at {t} · they keep the ground they gained and carry on from there',
  'mark.pickTitle': 'Mark {who} — from where?',
  'mark.positionTitle': 'Mark position',
  'mark.compassHint': 'Tap or drag inside the circle to place the marker. The slider sets the outer range (0.5–20 m). Front is their goal-side.',
  'mark.rangeAria': 'Outer ring range in metres',
  'mark.placeAria': 'Marking position — drag to place; left and right adjust angle, up and down adjust distance',
  'mark.distAria': 'Marking distance in metres',
  'mark.pickConfirm': 'Apply',
  'mark.pickOther': 'Change player',
  'mark.pickStop': 'Stop marking',
  'mark.pickDelete': 'Delete key',
  'mark.deleted': 'Mark key removed at {t} · {who} goes back to wherever the run around them already puts them',
  'attach.deleteKey': '✕ Remove this attach key ({t})',
  'attach.keyDeleted': 'Attach key removed at {t} · the {what} goes back to wherever its own path already puts it',
  'mark.stopMenu': '✕ Stop marking {who} ({t})',
  'mark.sameSpot': "Already marking {leader} at {t} — {where}, {d} m out · pick another spot or gap, then “Mark from here”",
  'mark.dir.front': 'front',
  'mark.dir.frontRight': 'front-right',
  'mark.dir.right': 'right',
  'mark.dir.backRight': 'back-right',
  'mark.dir.back': 'back',
  'mark.dir.backLeft': 'back-left',
  'mark.dir.left': 'left',
  'mark.dir.frontLeft': 'front-left',
  'mark.dragged': 'Marked from {where}, {d} m off {leader} — drag again to move the mark',
  'mark.dragNowhere': '{leader} is right there — a mark needs a gap to stand at · drop a step away',
  'mark.groupSet': '{n} marking {leader} — each from where they are standing',
  'mark.groupSetSome': '{n} marking {leader}, {skipped} skipped — same side, marking back, or standing on top',
  'mark.groupNone': 'Nobody in this group can mark {leader} — same side, marking back, or standing on top of them',
  'mark.groupAskMany': 'Who do these {n} mark?',
  'mark.groupPickHint': 'Tap an OPPONENT on the pitch — each marks from where they are standing · at {t}',
  'mark.groupOne': 'Only one player in this group — use Mark on them directly',
  'mark.stopMenuMany': '✕ Stop marking ({n} players, {t})',
  'mark.groupStopped': '{n} stop marking at {t} · each keeps the ground they gained',
  'mark.gapAskMany': 'How far out do these {n} press?',
  'mark.gapSetMany': '{n} markers now {d} m out · {where}',
  'mark.gapSkipped': ' ({n} not marking anyone here)',
  'mark.gapFew': 'Only one of these is marking someone — use 🎯 on them directly',
  'mark.groupNoneStop': 'Nobody in this group is marking anyone',
  'undoVerb.markDrag': 'mark position',
  // Benching a player releases what they were carrying AND who was following.
  'bench.benched': '{who} benched — {what} dropped at {t} · the ↩ button brings everything back',
  'bench.markerOne': 'a marker',
  'bench.markerMany': '{n} markers',
  'bench.carriedOne': 'the carried item',
  'bench.carriedMany': '{n} carried items',

  // ATTACH / PASS — the ball-and-zone carrier verbs, and the flight choice.
  // These prompts used to be hard-coded English sitting inside an otherwise
  // translated app: the same screen spoke two languages.
  'attach.follows': '{what} now follows {who} at {t} · the ↩ button undoes',
  'attach.release': 'Release here ({t})',
  'attach.released': '{what} released at {t} — it stands on its own · the ↩ button brings the hold back',
  'attach.title': 'Attach {name}?',
  'attach.changeTitle': '{name} follows {who} — hand it over?',
  'attach.pickHint': 'Tap the new carrier on the pitch · Release stops the follow at {t}',
  'attach.pickAny': 'Pick player or ball',
  'attach.groupTitle': 'Attach {n} items to…?',
  'attach.groupChangeTitle': '{n} items follow others — hand them over?',
  'attach.groupRelease': 'Release all ({n}) at {t}',
  'attach.groupFollows': '{n} now follow {who} at {t} · the ↩ button undoes',
  'attach.groupReleased': '{n} released at {t} — they stand on their own · the ↩ button brings the hold back',
  'attach.groupNone': 'Nothing in this group can attach — players use Follow',
  'attach.pickPlayer': 'Pick player',
  'attach.ballReleased': 'Ball released',
  'attach.ballTo': 'Ball attached to {who}',
  'attach.zoneAlready': 'Zone already follows {who}',
  'attach.zoneNobody': 'Zone follows nobody — drop it on a player to attach',
  'attach.zoneTo': 'Zone now follows {who}',
  'attach.zoneOff': 'Zone released — it stays where it is on the pitch',
  'attach.objAlready': '{what} already follows {who}',
  'attach.objNobody': '{what} follows nobody — drop it on a player to attach',
  'attach.objTo': '{what} now follows {who}',
  'attach.objOff': '{what} released — it stays where it is on the pitch',
  'detach.fromOne': 'Detached from {who} — it is selected now, drag it anywhere',
  'detach.fromMany': '{n} detached from {who}',
  'zoneVertex.bound': 'V{n} → {who} — the zone reshapes as they move',
  'zoneVertex.freed': 'V{n} freed — it stays where you put it',
  'pass.title': 'Pass to {who}?',
  'pass.attachTitle': 'Attach to {who}?',
  'pass.detachTitle': 'Detach?',
  'pass.flightHint': 'Released now · pick how long the flight takes',
  'pass.quick': 'Quick · 0.5 s',
  'pass.normal': 'Normal · 1 s',
  'pass.lofted': 'Lofted · 2 s',
  'pass.loftSuffix': ", in the air",
  'pass.moveOnly': 'Move only',
  'pass.to': 'Pass to {who} — caught {flight} s later{how} · drag the catch key to retune',
  'pass.stretched': ' · branch stretched to {t} for the flight',
  'pass.sameCarrier': 'Ball already follows {who} — drop it on someone else to pass, or on open pitch to release',
  'pass.hasBall': '{who} already has the ball — drop it on someone else',
  'pass.giveTitle': 'Give ball to {who}?',
  'pass.giveHint': '{carrier} releases now · pick how long the flight takes',
  'pass.takeHint': '{who} takes control',

  // ZONE VERTICES — the shape editor. The keyboard strings name the chords that
  // chain the edit; the touch strings name the pitch gesture instead, because a
  // phone has no Ctrl key and the ↩ button is on screen.
  'vertex.nudged': 'Vertex {n} nudged {step} m · drag it on the pitch for free shaping',
  'vertex.noWindow': 'No visuals window at the playhead — ✚ in the zone’s Visual lane keys one',
  'vertex.minTwo': 'A zone keeps at least 2 vertices — this one is already a line',
  'vertex.removed': 'Vertex {n} removed — {count} left · Ctrl+Z brings it back',
  'vertex.added': 'Vertex {n} added at the edge midpoint — Ctrl+←→ nudges it · ⇧A adds another',
  'vertex.grabbed': 'Grabbed vertex 1 — drag it on the pitch · + Add puts another · − Remove takes one away',
  'vertex.grabbedOf': 'Grabbed vertex 1 of {count} — drag it on the pitch · + Add puts another · − Remove takes one away',
  'vertex.grabFirst': 'Grab a vertex first — T twice grabs vertex 1 · ⇧A then adds one at its edge',
  'vertex.addedTouch': 'Vertex {n} added — {count} in the shape · drag it on the pitch to place it',
  'vertex.removedTouch': 'Vertex {n} removed — {count} left · the ↩ button puts it back',

  // ── The pitch view menu (hold an empty spot on the pitch).
  'view.title': 'View — the pitch',
  'view.hint': 'Where do you want to look from?',
  'view.top': '🎥 From above',
  'view.low': '🎥 From the touchline',
  'view.reset': '🎥 Back to the default view',
  'view.arrowsOn': '➜ Show every arrow — {n} on the pitch',
  'view.arrowsOff': '➜ Hide every arrow ({n} on)',
  'view.shapesOn': '◼ Show every zone shape ({n} zones)',
  'view.shapesOff': '◼ Hide every zone shape ({n} on)',
  'view.unmarkAll': '🎯 Stop all marking ({n} players)',
  'view.loftOn': '⤴ Arc every flight ({n} balls)',
  'view.loftOff': '⤵ Bring every flight down ({n} balls)',
  'view.gapAll': '📏 Set every marker gap ({n} markers)',
  'view.autoOff': 'AUTO off — drags move the base pose, no keys',
  'view.autoOn': 'AUTO on — every drag keys at the playhead',
  'view.branches': 'Branches view — open a branch to get back to its timeline',
  'view.timeline': 'Timeline view — ‹ BACK goes back to the branch tree',
  'view.ballHidden': 'Ball hidden — it is not drawn · this same menu brings it back, or tap its row in the strip',
  'view.ballShown': 'Ball visible again',
  // The ball vanished while its menu was open (removed by another door). No
  // chord, no panel — just what happened.
  'toast.hideGone': 'That ball is no longer on the pitch — nothing to hide or show',
  'view.panelsBack': 'Panels back — ▼ hides them for a full-pitch view',
  'view.panelsHidden': 'Panels hidden — ▲ brings them back · the bar stays',
  'view.panelsShowAria': 'Show timeline panels',
  'view.panelsHideAria': 'Hide timeline panels',
  'speed.at': 'Playback {r}× — tap the {r}× by the time to change it',
  'speed.top': 'Top speed 4× — tap the 4× to go down',
  'speed.floor': 'Slowest 0.5× — tap the 0.5× to go up',
  'tl.speedChipTitle': 'Playback rate — click to cycle 0.5× → 1× → 2× → 4×',
  'tl.rowSelected': 'Selected — ⇄ Mirror is in its Action menu · tap another row to group · tap again to deselect',
  'tl.rowUnselected': 'Tap to select this player',
  'tl.scrubHint': 'Drag the ruler to move through time — it lands on a key when you get close · hold it for the branch menu · drag its right edge to stretch time',
  'tl.branchName': 'Branch name',
  'tl.stretchHint': 'Drag to stretch or shorten this branch — snaps to ¼s · never past the last key · the ↩ button puts the length back',
  'tl.resizeHandle': 'Drag to resize',
  'tl.autoKeyHint': 'Auto-key: when ON, moving anything records a position keyframe at the current second',
  'tl.playBranch': 'Play this branch',
  'tl.pauseBranch': 'Pause this branch',
  'tl.playBranchHint': 'Play this branch — ▶ Master Play runs the WHOLE path',
  'tl.visualKey': 'Visual key at {t} — tap to edit the shape · drag to move it in time',
  'tl.pauseBranchHint': 'Pause this branch — ▶ Master Play runs the WHOLE path',
  'tl.midAttach': 'Attach: {what} @ {t} — tap: re-pick who · drag: move · double-tap: delete',
  'tl.midAction': 'Action: {what} @ {t} — tap: re-pick the action · drag: move · double-tap: delete',
  'tl.keyMark': 'Mark — key who this player marks at the playhead',
  'tl.tnMovement': 'Movement track',
  'tl.tnAction': 'Action track',
  'tl.tnGraphic': 'Arrows track',
  'tl.tnMark': 'Mark track',
  'tl.tnVisual': 'Visuals track',
  'tl.tnAttachment': 'Attachment track',
  'tl.laneHold': ' — hold this track to add a key right here',
  'tl.segBar': 'Transition {a} → {b}: {style}{arch} — tap to shape the flight',
  'tl.arrowBar': 'Path arrows {a} → {b} — tap to change the colour',
  'tl.zoneVisBar': 'Zone visuals {a} → {b} — tap to edit radius, shape and corners',
  'tl.branchRename': '{name} — hold the name to rename it',
  'tl.markKey': '🎯 Marking {who} at {t} — tap to change the spot · drag to move it in time',
  'tl.markKeyOff': '🎯 Mark released at {t} — tap to mark again · drag to move it in time',
  'tl.actionKey': '{what} at {t} — tap to pick a different action · drag to move it in time',
  'tl.graphicKey': 'Arrows {value} at {t} — tap to switch them on or off · drag to move it in time',
  'tl.on': 'on',
  'tl.off': 'off',
  'tl.detached': 'Detached',
  'timing.title': 'Timing — this branch · 0–{t}',
  'timing.desc': 'Every verb here changes WHEN things happen, never where they are. Drag the ruler edge to change the window.',
  'timing.slower': '⤢ Slower — every key ×1.25',
  'timing.quicker': '⤡ Quicker — every key ×0.8',
  'timing.clip': '✂ End at the last key ({t})',
  'timing.rippleOpen': '↔ Open a gap after {t} (+0.5s)',
  'timing.rippleClose': '↔ Close the gap after {t} (−0.5s)',
  'act.title': 'Action — {what}',
  'act.section.actions': 'Actions',
  'act.section.time': 'Time',
  'act.section.visual': 'Visual',
  'act.now': '{what} · now',
  // A group is rarely all-or-nothing: the marker says HOW MANY of the members this
  // card speaks for are already doing it, over the same tracks the entry will write.
  'act.nowSome': '{what} · {n} of {m} now',
  'act.item': 'Item',
  'form.group': 'Place a formation',
  'form.place': 'Place {name} — the whole squad, from the keeper up',
  'form.none': 'No players in this team yet — add one from the list below first',
  'form.already': 'Already in {name} — nothing to place',
  'form.placed': '{name} — {n} placed',
  'form.placedShort': '{name} — {n} placed, {empty} slots left empty',
  'act.plain': 'Action',
  'act.groupMore': ' +{n} more',
  'act.titleAll': 'Action — {n} at once',
  'act.titleSome': 'Action — {n} of {total} (balls and zones have no actions)',
  'kf.deleteActionHere': '✕ Delete this action ({t})',

  // ── The floating editors: tap a key, tap the bar between two keys, tap an
  // arrow window. Only the LABELS are translated — the hints behind them are
  // hover tooltips (title attributes), which a finger never sees.
  'edit.key': 'Key',
  'edit.stepsMove': 'The steps move…',
  'edit.stepsHere': 'Just this moment',
  'edit.stepsAfter': 'This + everything after',
  'edit.deleteKey': 'Delete key',
  'edit.otherItem': 'another item',
  'edit.landOn': '⇢ Land on {t} — {who}',
  'edit.landOnHint': 'Moves this moment to the instant {who} keys, so the two arrive together',
  'edit.transition': 'Transition',
  'edit.style': 'Style',
  'edit.straight': 'Straight',
  'edit.left': 'Left',
  'edit.right': 'Right',
  'edit.curve': 'Curve',
  'edit.arch': 'Arch',
  'edit.archPeak': 'Arch Peak',
  'edit.curveHint': 'Pull the violet handle on the pitch to bend it the other way',
  'edit.archHint': 'Pull the violet handle to raise the loft, or flatten it',
  'edit.archPeakHint': 'Slide the peak of the flight along the path',
  'edit.spotAlongHint': 'Moves this spot 0.5 m along the pitch · the fine step is 0.1 m',
  'edit.spotAcrossHint': 'Moves this spot 0.5 m across the pitch · the fine step is 0.1 m',
  'edit.timeEarlier1': 'Moves this moment 1 s earlier',
  'edit.timeEarlierQ': 'Moves this moment 0.25 s earlier',
  'edit.timeLaterQ': 'Moves this moment 0.25 s later',
  'edit.timeLater1': 'Moves this moment 1 s later — the branch grows to fit',
  'edit.arrowColour': 'Arrow Colour',
  'view.follow': 'Follow',
  'view.followTitle': 'Follow an item on the pitch — tap the item to follow it · ⌥N just focuses the camera',
  // The banner the camera-follow picker shows while it waits for a tap.
  'view.pickHint': 'Tap the player, ball or item on the pitch to follow it.',
  'vp.loading': 'Loading scene…',

  // COPY / PASTE / ROUTE — the clipboard verbs, and the group delete.
  // The side words are lowercase on purpose: they sit inside a sentence
  // ("Only one home player on the pitch"), which is the only place they serve.
  'side.home': 'home',
  'side.away': 'away',
  'clip.deletedN': '{n} deleted — the ↩ button brings them back',
  'clip.onlyOne': 'Only one {what} on the pitch — hold another one to add it to the group',
  'clip.whatPlayer': '{side} player',
  'clip.copied': "Copied {name}{route} — 📌 Paste it from another {type}'s menu",
  'clip.route': ' + its run',
  'clip.routeSame': 'Route already runs identically on {label} — nothing changed',
  'clip.stateSame': '{label} already holds that exact state — nothing changed',
  'clip.pasted': 'Pasted onto {label}',

  // KEY HEIGHT / PEAK / BEND — the flight-shaping dials. These fire from a
  // chord, so the chord stays named: it is the door that works from there.
  'key.heightAt': 'Key @ {t}: height {h} m{hint}',
  'key.heightHint': ' — pull the violet handle to shape the flight',
  'key.peakAt': 'Peak at {pct}% of the flight · {from}–{to} — ⌥⌃[ ] moves it · ⌥[ ] the height',
  'key.bendAt': 'Bend {pct}% · {from}–{to} — C flips side',
  'key.zoneRadius': 'Zone radius {r} m from {t}',
  'key.arrowDeleted': 'Arrow window from {t} deleted — the ↩ button brings it back',

  // THE KEY EDITOR'S TIME STEPPER — refusals must explain themselves in the
  // coach's language, and never name a chord a phone cannot press.
  'kf.startZero': 'A branch starts at 0 — this moment is only {t} in',
  'kf.rippleStretched': 'Everything after {t} moved too · branch stretched to {end}',
  'kf.blockedAt': 'Blocked — a key already sits at {t} · step past it',
  'kf.blockedDrag': 'Blocked — a key already sits at {t} · drop it beside',
  'kf.movedStretched': 'Moment moved to {t} · branch stretched to fit',

  // THE BEND (C) AND LOFT (⇧C) VERBS — what the cycle just did, out loud.
  'bend.noneGroup': 'No member has a movement segment at the playhead — tap ✚ on a Movement track to key one',
  'bend.noneHere': 'No movement segment at the playhead — K keys a position first',
  'bend.cycled': 'Segments cycled — {n} of the group{skipped}',
  'bend.skipped': ' ({n} with no segment here)',
  // The same parenthetical style as bend.skipped, and the same noun as
  // act.ballsNoRepeat: a ball or a zone has no Action lane in the strip at all
  // (its middle lane is Attachment), so a group action writes the members that
  // CAN hold one and counts the rest out loud instead of dropping them.
  'act.noLaneSkipped': ' ({n} with no action keys)',
  'bend.cycledTo': '{from}–{to}: {name}',
  'loft.on': 'Loft {m} m · {from}–{to} — pull the violet handle',
  'loft.off': 'Flat pass · {from}–{to}',
  'loft.onGroup': '{n} flights now arc {m} m high{skipped}',
  'loft.offGroup': '{n} flights brought back to the ground{skipped}',
  'loft.skipped': ' ({n} not balls in flight)',
  'loft.noneGroup': 'No ball in this group has a flight at the playhead — loft writes between two position keys',

  // THE ARROW VERBS (G / ⇧G). ON and OFF are baked into each sentence rather
  // than slotted in: "seta ligada", "flecha activada" — the adjective agrees
  // with the noun, and a slot cannot do that.
  'arrow.on': 'Arrow on at {t} — {name}',
  'arrow.off': 'Arrow off at {t} — {name}',
  'arrow.groupOn': 'Arrows on at {t} — {n}{blocked}',
  'arrow.groupOff': 'Arrows off at {t} — {n}{blocked}',
  'arrow.blockedSeam': ' ({n} seam-locked)',
  'arrow.groupColour': 'Arrows {c} — {n} of the group{skipped}',
  'arrow.skippedColour': ' ({n} with no arrow to colour)',
  'arrow.onRed': 'Arrow ON red from {t} — ⇧G cycles colour',
  'arrow.colourSet': 'Arrow {c} from {t}',
  'color.red': 'red', 'color.orange': 'orange', 'color.yellow': 'yellow',
  'color.green': 'green', 'color.blue': 'blue', 'color.purple': 'purple',
  'color.default': 'default colour',

  // ARMING A PLACEMENT — the most-used touch path in the app, and its
  // announcements were English. "Colocación armada:" instead of "{name} armed"
  // because the adjective would have to agree with a noun we only know at
  // runtime (cone is masculine, zona feminine).
  'itm.circleZone': 'Circle zone',
  'itm.dynamicZone': 'Dynamic zone',
  'place.cancelled': '{name} placement cancelled',
  'place.armed': '{name} armed — tap the pitch · keep tapping for more · the same button again (or Esc) stops',
  'place.armedAgain': '{name} armed again — tap the pitch · keep tapping for more · the same button again (or Esc) stops',
  'bench.disarmed': '{who} disarmed — ⇧{key} arms the next benched',
  'bench.armedClick': '{who} armed — click the pitch · ⇧{key} again cancels',
  'bench.noBenchHome': 'No benched home players left — everyone on the roster is on the pitch',
  'bench.noBenchAway': 'No benched away players left — everyone on the roster is on the pitch',
  'bench.playerN': 'Player {n}',
  'bench.armedNext': '{who} next — tap the pitch · the same button again (or Esc) stops',

  // WHEN THE SERVER SAYS NO. The reason arrives in the server's own words, but
  // the sentence around it belongs to the coach's language — otherwise the app
  // goes silent into English exactly when something has gone wrong.
  'scenario.saveFailed': 'Save failed: {why}',
  'scenario.saveAsFailed': 'Save As failed: {why}',
  'scenario.renameFailed': 'Rename failed: {why}',
  'scenario.deleteFailed': 'Delete failed: {why}',
  'scenario.deletedNamed': '“{name}” deleted',
  'scenario.shareFailed': 'Share update failed: {why}',
  'scenario.checkoutFailed': 'Checkout failed: {why}',
  'scenario.checkoutPopupBlocked': 'Allow pop-ups to open secure checkout without leaving your pitch.',
  'scenario.portalFailed': 'Could not open the billing portal: {why}',
  'scenario.portalPopupBlocked': 'Allow pop-ups to manage billing without leaving your pitch.',
  'scenario.unknownError': 'unknown error',
  'vis.windowDeleted': 'Visual window at {t} deleted — T keys a new one',
  'vis.windowsDeletedN': '{n} visual windows deleted — ✚ in the Visual lane keys a new one',
  'vis.windowsDeletedOne': '{n} visual window deleted — ✚ in the Visual lane keys a new one',
  'vis.lockedAtSeam': ' · {n} locked at the seam',
  'vis.noZonesScene': 'No zones on the pitch — a shape overlay needs a zone',
  'vis.windowsOpenedScene': '{n} zone shapes shown at {t}',
  'group.deletedStill': '{name} deleted — {n} still grouped · long-press one to delete them all · the ↩ button brings it back',

  // THE LAST LITERAL TOASTS — the verbs that were still answering in English:
  // the shape-window editor, the lane stepper, branch duration and priority,
  // the attach chords, duplicating, and selecting a whole block.
  'team.switchTo': '{name} → {side}',
  'vis.windowOpenGrab': 'Visuals window @ {t} — editor open · long-press the zone again — or double-click — to grab vertex 1',
  'vis.windowOpen': 'Visuals window @ {t} — editor open · ✎ Edit Shape shows the dots · T again grabs vertex 1',
  'vis.windowGroup': 'Visuals window @ {t} for {n} zones — editor open for this one',
  'lane.move': 'move key', 'lane.arrow': 'arrow key', 'lane.shape': 'shape key',
  'lane.attach': 'attach key', 'lane.action': 'action key',
  'lane.keyAt': '{lane} @ {t} — Enter edits it',
  'branch.lastKeyAt': 'The last key is at {t} — “Clip time” on the branch row trims to it exactly',
  'branch.nowDur': 'Branch now 0–{t} — {grow}⌥+/− scales the KEYS · the ↩ button reverts',
  'branch.growNote': 'room to key later · ',
  'branch.priority': 'Priority {dir} — now #{n} of {m}{choice} · the ↩ button reverts',
  'branch.dirUp': 'up', 'branch.dirDown': 'down',
  'branch.choiceKept': ' · chosen path unchanged',
  'branch.choiceFollows': ' · master path follows priority here',
  'branch.edgeDrag': 'Branch {before} → {now} — edge drag · the ↩ button puts the length back',
  'attach.nothingAttached': 'Nothing is following {name} yet — the MARK compass on their lane asks who to follow',
  'attach.detached': 'Detached at {t} — the ↩ button brings it back',
  'prompt.answered': 'Prompt answered — nearest {type} · {d} m apart',
  'pass.tuneCatch': 'Pass to {name} — drag the ◆ on the Attachment track to move the catch',
  'pass.stretchedFor': ' · branch stretched to {t} for the flight',
  'group.allSelected': 'All {n} {label} selected — drag one moves them all · the menu mirrors / duplicates the block',
  'clip.duplicatedN': 'Duplicated {n} items — {names} · grouped, drag one moves them all',
  'clip.duplicatedOne': 'Duplicated as {names} — tap ⧉ Duplicate again for another {d} m along',
  'clip.skippedPlayers': ' · {n} player(s) skipped — they use copy/paste',

  // THE REFUSALS. Every one of these already named the way out — they were the
  // last sentences in the app that only a reader of English could hear.
  'branch.onlyNow': 'Branch only now — Space plays the whole path again',
  'act.ballsNoRepeat': 'Balls and zones have no action keys — ↻ Again needs a player, cone or target',
  'act.ballsNoPick': 'Balls and zones have no action keys — X is for players, cones and targets',
  'act.noActionYet': 'No action used yet — the ✚ on the Action lane keys one',
  'act.againAt': '{action} again at {t}',
  'act.againGroupBalls': ' — {n} of {m} (balls and zones have no actions)',
  'act.againGroupAll': ' — all {n}',
  'itm.repeat': '↻ Again: {action}',
  // No "· Esc exits placement": the verb already set action mode to none before this
  // sentence is read, so the clause described a mode the coach had LEFT. A sentence
  // that names an exit from a state that no longer exists teaches the wrong shape.
  'bench.squadComplete': 'Squad complete — every rostered player is on the pitch',
  'kf.noPosKey': 'No position key at or before this moment — tap a diamond on the track to land on one',
  'kf.noPosKeyMany': 'None of these have a position key at this moment — tap a diamond on the track to land on one',
  'kf.noSegment': 'No movement segment under the playhead — ✚ in the item menu keys a position first',
  'kf.noBallSegment': 'No ball segment under the playhead — ✚ in the item menu keys a position first',
  'kf.walkBackNone': 'No key before this moment — the next one is ahead · ✚ in the item menu keys one here',
  'kf.walkFwdNone': 'No key after this moment — ✚ in the item menu keys one here',
  'kf.nothingExact': 'Nothing exactly at this moment — tap a diamond on the track to walk to the nearest key',
  'kf.noneYet': 'No keys yet — ✚ in the item menu keys a position first',
  'loft.noLoft': 'No loft on this segment — ⇧C lofts it, then ⌥⌃[ / ⌥⌃] place the peak',
  'loft.alreadyFlat': 'Already flat — pull the violet handle, or ⤴ Loft in the item menu',
  'loft.max': 'Max loft 10 m — pull the violet handle back down',
  'loft.flatNow': 'Flat pass · {from}–{to} — pull the violet handle to lift it',
  'vis.noCircleWindow': 'No circle-zone window at the playhead — T starts one',
  'prompt.answerFirst': 'Answer the prompt first — 1 accepts · 0 cancels',
  'play.normalAlready': 'Already at normal speed — +/− step it',
  'play.atOneX': 'Playback 1× — normal speed',
  'branch.shortest': 'Shortest 1 s — the ▸▸ step on the branch row pads it back',
  'branch.longest': 'Longest 600 s — the ◂◂ step on the branch row trims it back',
  'vis.forZones': 'Zone visuals are for zones — ⇧⌥S picks every zone',
  'tree.atRoot': 'Already at the root — tap a child under POSSIBLE NEXT to walk in',
  'tree.isRoot': 'This is the root branch — tap a child under POSSIBLE NEXT to walk in',
  'tree.noKids': 'No sub-branches here — press B to add one',
  'tree.renaming': 'Renaming branch — type the new name · Enter keeps it',
  'tree.rootNoSiblings': 'The root branch has no siblings — press B to add a sub-branch',
  'tree.oneBranch': 'One branch only — “+ Add sub-branch” grows the tree',
  'tree.firstSibling': 'First branch in this fork — ‹ BACK opens the tree, tap the branch you want',
  'tree.lastSibling': 'Last branch in this fork — ‹ BACK opens the tree, tap the branch you want',
  'tree.onlyOneKid': 'Only 1 branch under this one — POSSIBLE NEXT shows it',
  'tree.onlyNKids': 'Only {n} branches under this one — POSSIBLE NEXT shows them',
  'tree.rootNoPriority': 'The root branch has no priority — the path always starts here',
  'tree.alreadyFirst': 'Already first in this fork',
  'tree.alreadyFirstPath': 'Already first — the master path already takes this branch here',
  'tree.alreadyLast': 'Already last in this fork',
  'route.pastedOne': 'Route pasted onto {label}',
  'route.pastedMany': 'Route pasted onto {n} — each from its own spot',
  'app.newHere': 'New here? Tap an item, then drag it on the pitch — let go and the move is already recorded · tap it again for its menu. The file icon holds ❓ Help & Tutorials.',
  'app.defaultScenarioTitle': 'Default scenario',
  'app.scenarioUnconfigured': 'No default scenario is available. Open a scenario from the file menu, or start a new one.',
  'app.scenarioUnavailable': 'Could not load the site’s default scenario, so the pitch is empty. Open a scenario from the file menu, or check that the server is running.',
  'attach.noPlayer': 'No player on the pitch to attach to — open the place menu and pick a benched player',
  'attach.noBall': 'No ball on the pitch to give — open the place menu and place one on the pitch',
  'select.nonePlayers': 'No {who} on the pitch to select — add players from the ADD PLAYER menu',
  'select.noneItems': 'No {what} on the pitch to select — add one from the + menu',
  // The flight verbs, on the item menu. The label states the state it will
  // LEAVE BEHIND, so the entry is not a blind toggle.
  'itm.loftOn': '⤴ Loft {from}–{to}',
  'itm.loftOff': '⤵ No loft {from}–{to}',
  'itm.loftMany': '⤴ Loft ({n} balls)',
  'itm.loftSome': '⤴ Loft the {n} that can',
  'itm.bendCycle': '↝ Bend {from}–{to}: {now}',
  'itm.bendCycleMany': "↝ Bend all {n}",
  // When only some members have a flight to bend, the label says how many it will
  // move — and reuses the verb's own skip sentence for the rest.
  'itm.bendCycleSome': '↝ Bend the {n} that can',
  'itm.lift': '↑ {t} +0.5 m ({h} m)',
  'itm.lower': '↓ {t} −0.5 m ({h} m)',
  'itm.liftMany': "↑ all {n} +0.5 m",
  'itm.lowerMany': "↓ all {n} −0.5 m",
  // When the clamp leaves some of the group where it is, the label says how many
  // this verb will actually step — the same {n} of {m} the ↻ toast already uses.
  'itm.liftSome': '↑ {n} of {m} +0.5 m',
  'itm.lowerSome': '↓ {n} of {m} −0.5 m',
  // The height the WHOLE group already sits at — appended only when every member
  // shares it, so the number is as true as the count.
  'itm.manyAt': ' ({h} m)',
  'height.floor': 'Already on the ground — ↑ lifts it',
  'height.ceiling': '10 m is the ceiling — ↓ brings it down',
  'height.floorMany': "Every one is on the ground — ↑ lifts them",
  'height.ceilingMany': "All at the 10 m ceiling — ↓ brings them down",
  // A refusal that names the door: while an item follows another one its height
  // is the other one's, so the way to lift it is to let it go — and the entry
  // that lets it go is in the item's own menu, not on a keyboard.
  'height.follows': 'It follows another item right now — its height is theirs · its own menu has the entry that lets it go',
  // HIDE / SHOW THE BALL, as an entry in the ball’s own menu. It used to be ⇧V
  // or a trip to the Ball panel: on a phone the chord does not exist and the
  // panel is a step away from the thing you are already holding.
  'itm.ballHide': '👻 Hide this ball',
  'itm.ballShow': '👁 Show this ball',
  'scenario.nowPrivate': '“{name}” is now private',
  'scenario.nowPublic': '“{name}” is now public',

  // THE PLAYBACK BUTTONS on the viewport, and the feedback modal.
  'play.speedTitle': '{label} — hold or right-click for playback speed ({speed}×)',
  'play.speed': 'Playback speed',
  'fb.title': 'Send Feedback',
  'fb.type': 'Type',
  'fb.cat.suggestion': 'Suggestion',
  'fb.cat.bug': 'Bug Report',
  'fb.cat.question': 'Question',
  'fb.cat.other': 'Other',
  'fb.message': 'Your message',
  'fb.placeholder': 'Tell us what you think — feature ideas, bugs, questions, anything...',
  'fb.sent': '✓ Thank you! Feedback sent.',
  'fb.failed': '✗ Failed to send. Please try again.',
  'fb.close': 'Close',
  'fb.send': 'Send',
  'fb.sending': 'Sending...',

  // THE PUBLIC SCENARIO BROWSER — a whole modal that had never been translated.
  'pub.titleOpen': 'Browse Open Scenarios',
  'pub.titlePublic': 'Browse Public Scenarios',
  'pub.searchPlaceholder': 'Search by name… (leave empty to browse all)',
  'pub.search': 'Search',
  'pub.clear': 'Clear',
  'pub.loading': 'Loading…',
  'pub.none': 'No public scenarios found.',
  'pub.load': 'Load',
  'pub.showing': 'Showing {from}–{to} of {total} scenarios',
  'pub.showingOne': 'Showing {from}–{to} of 1 scenario',
  'pub.prev': '‹ Prev',
  'pub.next': 'Next ›',
  'pub.loadListFailed': 'Failed to load scenarios',
  'pub.loadOneFailed': 'Failed to load scenario',
  'pub.movementKeys': '{n} movement key(s)',
  'pub.emptyTimeline': 'empty timeline',

  // ── The Zone Visuals panel (tap a zone's visual window on the strip).
  'zv.title': 'Zone Visuals',
  'zv.close': 'Close zone visuals',
  'zv.radius': 'Radius — {m} m',
  'zv.editShape': '✎ Edit Shape (add / move / remove points)',
  'zv.closed': 'Closed',
  'zv.open': 'Open',
  'zv.vertexHint': 'Vertex attachments — drag a vertex onto a player, or Link then tap them',
  'zv.notAttached': 'not attached',
  'zv.link': 'Link',
  'zv.linkTitle': 'Attach V{n} by click: Link, then click a player or the ball on the pitch — or just drag the V{n} dot onto them',
  'zv.free': 'Free V{n} — stop following {who}',
  'zv.opacity': 'Opacity — {p}%',
  'zv.thickness': 'Thickness — {n}',

  // ── The announcements that follow a pitch verb. A state change you cannot
  // read is not a state change, so the words a gesture answers with live in the
  // dictionary — refusals included, because explaining the way out is the part
  // that has to be understood.
  'toast.arrowsNeedItem': 'Select an item first — its menu offers ➜ Arrows on / off',
  'toast.arrowsAlreadyOn': 'Arrows already showing at {t} — nothing changed',
  'toast.arrowsAlreadyOff': 'No arrows to hide at {t} — nothing changed',
  'toast.arrowsOnMany': 'Arrows on for {n} at {t}',
  'toast.arrowsOffMany': 'Arrows off for {n} at {t}',
  'toast.alreadyThatWay': ' ({n} already that way)',
  'toast.heightUpMany': "Lifted {n} items by {m} m",
  'toast.heightUpOne': "Lifted {n} item by {m} m",
  'toast.heightDownMany': "Lowered {n} items by {m} m",
  'toast.heightDownOne': "Lowered {n} item by {m} m",
  'toast.heightSkipped': " ({n} could not move)",
  'toast.arrowsOnOne': 'Arrows on — {name} at {t}',
  'toast.arrowsOffOne': 'Arrows off — {name} at {t}',
  'toast.undoSuffix': ' · the ↩ button undoes',
  'toast.alreadyActionMany': 'All {n} already {action} at {t} — nothing changed',
  'toast.alreadyActionOne': 'Already {action} at {t} — nothing changed',
  'toast.repeatSameInstant': 'Already {action} at {t} — step the playhead on, or pick another action',
  'toast.attachedTo': 'Attached to {name} · {d} m apart · {t} — hold the ball again to release it',
  'toast.keyNeedItem': 'Select an item first — its row in the strip has a ✚ for what you want to key',
  'toast.keyAllFollow': 'All {n} follow a carrier here — keying writes dead spots · the ✚ on the Attachment track opens Release',
  'toast.keyAllFollowOne': 'This one is following a carrier here — keying writes a dead spot · the ✚ on the Attachment track opens Release',
  'toast.keyMany': '{n} keys at {t}',
  'toast.keyFollowSuffix': ' ({n} follow a carrier — the ✚ on the Attachment track opens Release)',
  'toast.keyFollowSuffixOne': ' ({n} follows a carrier — the ✚ on the Attachment track opens Release)',
  'toast.keyOne': '{name} keyed at {t}',
  'toast.noPosPlayer': 'This player has no position to key yet.',
  'toast.noPosItem': 'This item has no position to key yet.',
  'toast.followsCarrier': '{name} follows {who} here — a position key would be a dead spot · the ✚ on the Attachment track opens Release',
  'toast.sNeedItem': 'Select an item first — S lands its key on the nearest second another item already keys',
  'toast.sNoOwnKey': 'No movement key on this item — the ✚ on its row in the strip keys one at the playhead',
  "toast.sNothingElse": "Nothing else is keyed — S lands this moment on ANOTHER item's second · drag the ◆ in its row to move it in time",
  'toast.momentDeleted': 'Moment at {t} deleted — arrival + claim together · the ↩ button brings both back',
  'toast.keyDeleted': 'Key at {t} deleted · the ↩ button brings it back',
  'toast.nudgeKey': 'Key → {t}',
  'toast.nudgeNote': ' — {note}',
  'toast.nudgeMoment': ' · moment moved together',
  'toast.nudgeGroup': ' · {n} in group',
  'toast.nudgeStepSuffix': ' · ◂ 0.25 / 0.25 ▸ steps it',
  'toast.sameSecond': 'same second as {who} · {t}',
  'toast.mirrorWhole': 'Mirrored the whole branch — {n} of them {axis} · “⇄ Mirror…” does it from the menu',
  'toast.mirrorOne': 'Mirrored {n} item {axis}',
  'toast.mirrorMany': 'Mirrored {n} items {axis}',
  'toast.mirrorAxisFlank': 'flank ↔ flank',
  'toast.mirrorAxisEnd': 'end ↔ end',
  'toast.mirrorFlankHint': ' — ⇄ Mirror end turns it the other way',
  'toast.mirrorEndHint': ' — ⇄ Mirror flank turns it the other way',
  // "· Esc stops" described a MODE that does not exist. These entries are one-shot:
  // the card stays open, and tapping ⇄ Copy again lays the next one. A phone reads
  // this sentence, so it names the thumb's door instead of a chord.
  'toast.mirrorPlacedHalf': '{label} mirrored to the other half — the other mirror entry flips it the other way · tap ⇄ Copy again for another',
  'toast.mirrorPlacedSide': '{label} mirrored to the other side — the other mirror entry flips it the other way · tap ⇄ Copy again for another',
  'toast.lineNeedTrail': 'Place two first — they set the spacing · tap two, then ⧉ Next in line repeats it',
  'toast.lineSameSpot': 'Those two sit on the same spot — place a second one elsewhere to set the spacing',
  'toast.lineTurned': '{label} turned 90° — ⧉ Next in line continues the new line · ⧉ Turn 90° walks a square',
  'toast.lineStepped': '{label} {m} m on — ⧉ Next in line again · ⧉ Turn 90° makes a grid',
  'toast.mirrorNothingPitch': 'Nothing on the pitch to mirror',
  'toast.mirrorNeedSelect': 'Nothing selected to mirror — select an item, or open its Action menu and use ⇄ Mirror',
  'toast.dupPlayerNo': 'Players cannot be duplicated — copy one (📋 in its menu) and paste it onto another slot.',
  'toast.placeNeedArm': 'Place one first — the + menu arms an item, then tap the pitch',
  'toast.placeBallOne': 'Balls go one at a time — drag one onto a player to hand it over',
  // The place tool IS sticky, so this one needs an exit — and the exit a thumb has
  // is the same one bench.armedNext already names: the armed button, again.
  'toast.placedMore': '{type} placed — tap again for more{hint} · the same button again (or Esc) stops',
  'toast.placedHintLine': ' · ⧉ Next in line repeats this spacing',
  'toast.placedHintMirror': ' · the + menu mirrors it to the other half',
  'kindOne.cone': 'Cone', 'kindOne.target': 'Target', 'kindOne.zone': 'Zone', 'kindOne.ball': 'Ball',
  'kindMany.cones': 'cones', 'kindMany.targets': 'targets', 'kindMany.zones': 'zones',
  'kindMany.balls': 'balls', 'kindMany.playersHome': 'home players', 'kindMany.playersAway': 'away players',
  'toast.groupLeft': '{name} left group',
  'toast.groupJoined': '{name} grouped',
  'toast.groupMany': ' — {n} in group · drag one moves all · long-press one for mirror / duplicate / delete all',
  'toast.groupOne': ' · long-press it again — or double-click — for its actions',
  "toast.ballNoGroup": "Balls can't join groups — group of {n} kept · drag a member to move them all",
  'toast.autoOff': 'AUTO is off — that move will not play. The AUTO button in the strip turns it back on.',
  'toast.landed': '↳ {name}',
  'toast.landedPath': '↳ {name} — {path}',
  'toast.landedKids': ' · tap a child under POSSIBLE NEXT',
  'toast.landedAddBranch': ' · “+ Add sub-branch” grows the tree',
  'toast.landedUpMany': ' · tap the branch above to step out',
  'toast.landedUpOne': ' · tap the branch above to step back out',
  'toast.landedWalkIn': ' · tap a child to walk in',
  'toast.branchRemoved': 'Branch “{name}” removed — the ↩ button brings it back',
  'toast.clipTight': 'Already tight — last key at {t} · ✚ in the item menu keys one later',
  'toast.clipRestores': ' · the ↩ button restores {old}',
  'ripple.movedLaterOne': '1 key moved later after {t}',
  'ripple.movedLater': '{n} keys moved later after {t}',
  'ripple.movedEarlierOne': '1 key moved earlier after {t}',
  'ripple.movedEarlier': '{n} keys moved earlier after {t}',
  'ripple.stayed': ' · {n} inherited stayed',
  'vertex.at': '{x}, {y} m',
  'vertex.shiftFine': ' · ⇧ = 0.1 m fine',
  'select.cycleHint': ' · N next · ⇧N back',
  'select.cameraFocused': ' · camera focused on it',
  'select.cameraHint': ' · ⌥N jumps the camera too',
  'toast.stretchSlower': 'Branch slower — {old} → {new}',
  'toast.stretchNothing': 'Nothing to stretch — every key sits at 0:00',
  'toast.stretchFloor': 'Already at the 1 s floor — stretch it first, or drag the ruler edge to trim the window',
  'toast.stretchFaster': 'Branch faster — {old} → {new}',
  'toast.revertSuffix': ' · the ↩ button reverts',
  'toast.rootNoPriority': 'The root branch has no priority — the path always starts here',
  'toast.alreadyFirstChosen': 'Already first in this fork',
  'toast.alreadyFirstPath': 'Already first — the master path already takes this branch here',
  'toast.alreadyLastFork': 'Already last in this fork',
  'toast.priorityMoved': 'Priority {dir} — now #{rank} of {n}',
  'toast.dirUp': 'up', 'toast.dirDown': 'down',
  'toast.chosenUnchanged': ' · chosen path unchanged',
  'toast.pathFollowsPriority': ' · master path follows priority here',
  'toast.timelineSame': '“{name}” already holds that exact timeline — nothing changed',
  'toast.rootNoSiblings': 'The root branch has no siblings — “+ Add sub-branch” continues from its end',
  'toast.noSiblings': 'No siblings here — “+ Add sub-branch” continues from this end',
  'toast.branchDuplicated': 'Duplicated “{src}” — now editing “{name}” · the ↩ button removes it',
  // One wording, several call sites: the same refusal from a keyboard chord and
  // from a menu or toolbar button must not read two different ways.
  'toast.needZone': 'Zone visuals are for zones — select a zone',
  'toast.needBallLoft': 'Loft is for the ball — select it first',
  'toast.needBallHide': 'Select a ball first — ⇧V hides or shows it (N cycles the pitch)',
  'toast.hideOnlyBall': 'Only balls can hide — ⇧V works on a selected ball · V switches views',
  'toast.needBallAttach': 'Select a ball, zone or player first — ⌥A attaches to the nearest partner',
  'toast.needItemSelectAll': 'Select an item first — its menu offers ⧉ Select all',
  'toast.needItemHeight': 'Select an item first — Ctrl+↑/↓ edits its key height',
  'toast.needItemBend': 'Select an item first — Ctrl+[ / Ctrl+] bend its path',
  'toast.needItemSlide': 'Select an item first — Ctrl+←/→ slides its spot along the pitch',
  'toast.needItemRepeat': 'Select a player or item first — ↻ Again lives in the item menu',
  'toast.ballNoActionX': 'Balls and zones have no action keys — X needs a player, cone or target in the group',
  'toast.ballNoActionShift': 'Balls and zones have no action keys — ↻ Again needs a player, cone or target in the group',
  'toast.groupOneRemove': 'Only one item in the group — the toolbar − removes it',
  'toast.noArrowOn': 'No arrow ON in this group at that time — G turns them on first',
  'toast.rootTrunk': 'The root branch cannot be deleted — it is the trunk every branch hangs from',
  'toast.branchDeleted': 'Branch deleted — the ↩ button brings it back',
  'toast.nothingPlaced': 'Nothing placed yet — Shift+1…8 first',
  'toast.nothingOnPitch': 'Nothing on the pitch to select — ⇧1-6 arms an item to place',
  'toast.alreadyFree': 'Already free at this moment — nothing to detach',
  'toast.needPosKey': 'Nothing to measure from — press K to key a position first',
  'toast.needSelectFly': 'Nothing selected — tap or click an item (or press N), then F or double-tap to fly there',
  // Timing refusals. The door must not be the control that just failed: the
  // nudge refusal names the FULL-SECOND step, and the no-room refusal names the
  // pointer handle that makes room.
  'toast.rippleSeamBlocked': 'Blocked by the inherited seam at {t} — nothing moved',
  'seam.keysReadOnly': 'Every key here is the one this branch inherited at the seam — read-only · ✚ in the item menu keys one you can move',
  'seam.windowsReadOnly': 'Every window here is the one this branch inherited at the seam — read-only · ✚ in the item menu keys one you can change',
  'toast.groupSlidAll': 'All {n} slid {d} m — {who} now {x}, {z} m',
  'toast.groupSlidOne': '{who} now {x}, {z} m',
  'toast.rippleKeyBlocked': 'Blocked — a key sits at {t} · drag the ◆ on the strip to move it',
  'toast.rippleNothingAfter': 'Nothing after {t} — park the cut before a key',
  'toast.nudgeKeyBlocked': 'Blocked — a key already sits at {t} · drag the ◆ past it, or press 1 s ▸▸',
  'toast.noRoomToStep': 'The branch runs 0–{t} — no room to step there · drag the ruler edge to make room',
  "toast.edgeCross": "Window edge can't cross its neighbour at {t}",
  'toast.edgeStepped': 'Window edge {from} → {to} · drag the edge, or ◂ 0.25 / 0.25 ▸',

  'zone.edit': '✎ EDIT ZONE',
  'zone.exitEditHint': 'Finish shaping — + Add, ⇱ Move and − Remove shape the zone',
  'zone.add': '+ Add',
  'zone.remove': '− Remove',
  'zone.addHint': 'Tap the pitch to add · press a corner to move it — drop it on a player and the zone follows them',
  'zone.removeHint': 'Tap a corner to remove it.',
  'zone.linkBar': 'Tap a player or the ball to attach this point · ✕ cancels (Esc on a keyboard)',
  'zone.linkCancel': 'Cancel linking this point',

};

const pt = {
  'lang.name': 'Português',
  'lang.flag': '🇧🇷',

  'menu.new': '＋ Novo',
  'menu.save': '💾 Salvar',
  'menu.saveAs': '💾 Salvar Como…',
  'menu.load': '📂 Abrir e gerenciar cenários',
  'menu.loadPublic': '🌍 Carregar dos Públicos',
  'scenario.manageHint': 'Toque num cartão para abrir · ✎ renomear · 🌍 partilhar · 🗑 apagar',
  'menu.account': 'Conta:',
  'menu.upgrade': '⬆ Melhorar',
  'menu.upgradePrice': '⬆ Melhorar — {price}/mês',
  'account.upgradePrice': '⬆ Melhorar para Premium — {price}/mês',
  'menu.logout': '⎋ Sair',
  'menu.login': '🔑 Entrar / Criar conta',
  'menu.language': 'Idioma',
  'menu.mobileApps': 'Baixe o app',
  'menu.androidApp': 'App Android',
  'menu.iosApp': 'App iOS',
  'menu.iosComingSoon': 'Em breve',
  'menu.account.open': '👤 Conta…',

  'account.title': 'Conta',
  'account.nameTitle': 'Nome de exibição',
  'account.save': 'Salvar',
  'account.nameDone': 'Nome atualizado',
  'account.passwordTitle': 'Alterar senha',
  'account.currentPw': 'Senha atual',
  'account.newPw': 'Nova senha (mín. 6)',
  'account.confirmPw': 'Confirmar nova senha',
  'account.changePw': 'Alterar senha',
  'account.shortPw': 'A nova senha deve ter pelo menos 6 caracteres',
  'account.pwMismatch': 'As novas senhas não coincidem',
  'account.pwDone': 'Senha alterada',
  'account.subscription': 'Assinatura',
  'account.premiumOn': 'Premium ⭐',
  'account.freePlan': 'Plano gratuito',
  'account.manage': 'Gerenciar assinatura',
  'account.upgrade': '⬆ Melhorar para Premium',
  'account.signOut': '⎋ Sair',
  'account.deleteTitle': 'Excluir conta',
  'account.deleteWarn': 'Sua conta e dados são excluídos definitivamente após um período de carência de 30 dias. Assinaturas da loja devem ser canceladas na Apple/Google.',
  'account.deletePw': 'Senha atual',
  'account.deleteConfirm': 'Digite DELETE para confirmar',
  'account.deleteBtn': 'Solicitar exclusão',
  'account.deleteCancel': 'Cancelar',
  'account.deleteDone': 'Exclusão solicitada — programada para',
  'menu.teams': '👥 Times…',
  'teams.title': 'Times',
  'teams.new': '+ Novo time',
  'teams.none': 'Nenhum time nesta conta ainda.',
  'confirm.cancel': 'Cancelar',
  'confirm.deleteScenario': 'Excluir cenário',
  'confirm.deleteScenarioBody': '“{name}” e todos os ramos dele são removidos definitivamente. O desfazer não alcança um arquivo salvo.',
  'confirm.deleteTeam': 'Excluir time',
  'confirm.deleteTeamBody': '“{name}” — elenco, cores, escudo e texturas de uniforme são removidos definitivamente. Os cenários que você já criou mantêm suas posições.',
  'confirm.discardTitle': 'Alterações não salvas',
  'confirm.discardBody': 'Sair deste time e perder as alterações que você não salvou?',
  'confirm.discard': 'Descartar alterações',
  'name.saveCopy': 'Salvar cópia',
  'name.saveAsHint': 'O original fica intacto — isto vira um novo cenário na sua conta.',
  'name.renameScenario': 'Renomear cenário',
  'name.renameHint': 'O nome que aparece em Carregar cenário e no menu Arquivo.',
  'scenario.rename': 'Renomear',
  'scenario.makePublic': 'Tornar público',
  'scenario.makePrivate': 'Tornar privado',
  'scenario.loadTitle': 'Carregar cenário',
  'scenario.refresh': 'Atualizar',
  'scenario.refreshHint': 'Reler os cenários salvos na sua conta',
  'scenario.loading': 'Carregando…',
  'scenario.noneYet': 'Nada salvo nesta conta ainda.',
  'scenario.newOne': 'Novo cenário',
  'scenario.publicBadge': 'público',
  'login.signIn': 'Entrar',
  'login.register': 'Criar conta',
  'login.email': 'E-mail',
  'login.password': 'Senha',
  'login.newPassword': 'Nova senha',
  'login.orgName': 'Conta / Nome da organização',
  'login.orgPlaceholder': 'ex.: Barcelona FC',
  'login.forgot': 'Esqueceu a senha?',
  'login.forgotTitle': 'Esqueci a senha',
  'login.forgotBody': 'Digite seu e-mail e enviaremos um link de redefinição.',
  'login.sendLink': 'Enviar link',
  'login.sending': 'Enviando…',
  'login.back': 'Voltar para entrar',
  'login.resetTitle': 'Redefinir senha',
  'login.resetBody': 'Digite sua nova senha abaixo.',
  'login.reset': 'Redefinir senha',
  'login.resetting': 'Redefinindo…',
  'login.create': 'Criar conta',
  'login.creating': 'Criando…',
  'login.signingIn': 'Entrando…',
  'login.emailRequired': 'E-mail é obrigatório',
  'login.nameRequired': 'Nome é obrigatório',
  'login.pwRequired': 'Nova senha é obrigatória',
  'login.pwShort': 'A senha precisa ter pelo menos 6 caracteres',
  'login.resetSent': 'Se existir uma conta para esse e-mail, um link de redefinição foi enviado.',
  'login.guides': 'Ler os guias',
  'login.close': 'Fechar',
  'info.ok': 'OK',
  'info.continue': 'Continuar',
  'info.adFree': 'Sem anúncios com o Premium',
  'teams.playersCount': '{n} jogadores',
  'teams.shared': 'compartilhado',
  'teams.selectHome': 'Escolher time mandante',
  'teams.selectAway': 'Escolher time visitante',
  'teams.delete': 'Excluir time',
  'teams.save': '💾 Salvar time',
  'teams.unsaved': 'alterações não salvas',
  'teams.name': 'Nome do time',
  'teams.shirt': 'Camisa',
  'teams.shorts': 'Calção',
  'teams.socks': 'Meias',
  'teams.badge': 'Escudo',
  'teams.textures': 'Texturas do uniforme (camisa/calção/meias personalizados)',
  'teams.logo': 'Enviar escudo',
  'teams.player': 'Nome do jogador',
  'teams.number': 'Número',
  'teams.position': 'Posição',
  'teams.skin': 'Pele / rosto',
  'teams.hair': 'Cabelo',
  'teams.applyHome': 'Aplicar ao Mandante',
  'teams.applyAway': 'Aplicar ao Visitante',
  'teams.saved': 'Time salvo',
  'teams.hint': 'Escolha um time para editar. Aplique-o a um lado para usá-lo no campo.',
  'teams.numberColor': 'Cor do número',
  'teams.numberColorHint': 'O número nas costas da camisa — as cores do uniforme vêm das texturas.',
  'teams.templates': 'Molduras do uniforme',
  'teams.templatesHint': 'Baixe o layout de impressão, crie sobre ele e envie de volta como a textura correspondente.',
  'teams.download': '↓ {name}',
  'teams.customTex': 'personalizada',
  'teams.defaultTex': 'padrão',
  'teams.addPlayer': '+ Adicionar jogador',
  'teams.removePlayer': 'Remover este jogador',
  'teams.squadCount': '{n} de {max}',
  'teams.squadFull': 'Elenco completo — {max} jogadores é o máximo',
  'teams.playerAdded': '{name} adicionado — {n} no elenco',
  'teams.playerRemoved': '{name} removido — {n} restantes · o botão ↩ desfaz',
  'teams.squadMin': 'O elenco precisa de pelo menos um jogador',
  'look.title': 'Editar aparência',
  'look.loading': 'Carregando a figura…',
  'look.hint': 'Escolha pele e cabelo — a figura veste o uniforme deste time.',
  'look.edit': '✎ Editar aparência',
  'look.editAria': 'Editar a aparência de {name}',
  'look.current': 'Atual: {skin} · {hair}',
  'look.apply': 'Aplicar',
  'look.cancel': 'Cancelar',
  'look.none': 'Nenhum estilo carregado',
  'look.dragRotate': 'Arraste a figura para girar',
  'look.noPreview': 'Pré-visualização indisponível — os estilos continuam valendo.',
  'look.swatchAria': 'Escolher {label}',
  'look.needSave': 'Aplicado — pressione Salvar para manter',
  'menu.help': '❓ Ajuda e tutoriais',
  'help.title': 'Ajuda e tutoriais',
  'help.scSpace': 'toca o caminho escolhido / pausa',
  'help.scPlayTo': 'tocar até o próximo ramo',
  'help.scBranchPlay': 'reproduz / pausa só o ramo atual',
  'help.scCam': 'câmera: padrão · topo · baixa',
  'help.scPlace': 'arma qualquer objeto colocável',
  'help.scLineRepeat': 'coloca mais um no mesmo passo (depois de dois cliques)',
  'help.scTurnPlace': 'vira a fila 90° no mesmo espaçamento',
  'help.tFollow': 'arraste um jogador SOBRE um ADVERSÁRIO — ele o marca: toque no campo onde eles devem ficar, ou escolha o lugar e a distância',
  'help.tGroupMark': 'arraste um GRUPO SOBRE um ADVERSÁRIO — todos o marcam, cada um de onde está · ou abra o menu do grupo: 🎯 Marcar um adversário… · 📏 um mostrador define todas as distâncias',
  'help.tRide': 'arraste um cone, alvo, zona ou bola SOBRE um jogador — passa a segui-lo; solte sobre o campo para desvincular',
  'help.tHandles': 'toque numa transição de movimento na linha do tempo para ajustar a curva ou a altura do voo',
  'help.scMirrorPlace': 'espelha o último colocado para o outro campo',
  'help.scMirrorPlaceZ': 'espelha para o outro lado',
  'help.scPlacePlayer': 'arma o próximo reserva (casa / fora)',
  'help.scReArm': 'rearma o último objeto colocado',
  'help.scCycle': 'percorre a seleção (⌥: a câmera salta para o item)',
  'help.scgPlayback': 'Reprodução',
  'help.scgPlacement': 'Colocar',
  'help.scgTiming': 'Tempo',
  'help.scgItems': 'Itens',
  'help.scgBranches': 'Ramos',
  'help.scgEditing': 'Edição',
  'help.scFocus': 'câmera até a seleção',
  'help.scView': 'painel timeline ⇄ ramificações',
  'help.scHide': 'oculta painéis — campo inteiro',
  'help.scHideBall': 'esconde / mostra a bola selecionada',
  'help.scArrows': 'mover o cursor (Shift = 1 s)',
  'help.scJump': 'pular para a chave anterior / seguinte',
  'help.scNudge': 'deslocar a chave mais próxima no tempo',
  'help.guides': 'guias',
  'help.guideLoading': 'Carregando o guia…',
  'help.guideFallback': 'Não foi possível carregar o guia aqui — mostrando o site completo da documentação.',
  'help.guideOpenSite': 'site completo ↗',
  'help.guideOpenSiteHint': 'Abrir o site de documentação no navegador — inclui os vídeos passo a passo',
  'help.videos': 'vídeos',
  'help.videosHint': 'Passos a passo em vídeo — hospedados no YouTube',
  'help.videoNote': 'Passos a passo curtos, hospedados no YouTube. Só carregam quando você dá play.',
  'help.videoPlay': 'Assistir {title}',
  'help.videoWatch': 'no YouTube ↗',
  'help.scLandSame': 'encaixa esta chave no segundo mais próximo de outro item · ou ⇢ Encaixar em… dentro do editor da chave',
  'help.scHeight': 'altura da chave ±0,5 m (⇧: 0,1)',
  'help.scKey': 'registrar posição atual no cursor',
  'help.scEnterEdit': 'editar keyframe/segmento no cursor',
  'help.coreLoop': 'O ciclo: selecione um item (N cicla) · , . percorre as chaves · Enter edita o que está sob o cursor · Esc volta',
  'help.scAction': 'seletor de ação para a seleção',
  'help.scRepeatAction': 'aplica a ação que você acabou de usar (grupo = todos os membros)',
  'help.scAuto': 'alterna gravação AUTO de keys',
  'help.scAttach': 'bola/zona/jogador ↔ parceiro mais próximo (⇧: solta)',
  'help.scShot': 'captura — partilhe ou guarde a vista atual',
  'help.scRec': 'gravar vídeo — ⇧P de novo para parar',
  'help.scSeg': 'curva o segmento no cursor',
  'help.scMirror': 'espelha a seleção lado a lado (grupo · M e depois ⇧M gira 180°)',
  'help.scMirrorEnd': 'espelha ponta a ponta, sobre a linha do meio-campo',
  'help.scMirrorAll': 'espelha TODOS os itens do ramo, lado a lado',
  'help.scMirrorAllEnd': 'espelha todos os itens ponta a ponta, sobre a linha do meio-campo',
  'help.scSelectKind': 'seleciona todos os itens do mesmo tipo (todos os cones, um time)',
  'help.scBendAmount': 'curvatura ±0,1 (⇧: 0,02)',
  'help.scLoft': 'altera o loft da bola (arco)',
  'help.scLoftDial': 'altura do loft ±0,5 m',
  'help.scPeakDial': 'moveia o PICO do chute pelo trajeto (⇧ = fino)',
  'help.scBend': 'arraste a bolinha laranja para curvar',
  'help.scArch': 'arraste a roxa para erguer o passe',
  'help.scArrow': 'alterna a seta de movimento',
  'help.scArrowColor': 'alterna a cor da seta',
  'help.scZone': 'janela visual da zona · T de novo agarra o vértice 1 (grupo = todas as zonas)',
  'help.scRadius': 'raio da zona ±0,5 m · qualquer item: desloca o ponto ±0,5 m (⇧: 0,1)',
  'help.scVertexNudge': 'mover vértice capturado 0,25 m',
  'help.scVertexAdd': 'adiciona um vértice na aresta agarrada (Del remove)',
  'help.scZoneDel': 'exclui a janela da zona no cursor (grupo = todas as zonas)',
  'help.scBranch': 'adiciona sub-ramo ao ramo atual',
  'help.scRename': 'renomeia o ramo atual (foca o nome)',
  'help.scClip': 'recorta o ramo até o último keyframe',
  'help.scSibling': 'adiciona ramo irmão',
  'help.scEnter': 'abre o timeline do ramo atual',
  'help.scDelBranch': 'exclui o ramo atual (raiz protegida)',
  'help.scDupBranch': 'duplica ramo + timeline',
  'help.scPick': 'abre o N-ésimo sub-ramo (⌥↑ pai)',
  'help.scSibs': 'vai ao ramo irmão anterior / seguinte',
  'help.scWalk': 'vai ao pai / primeiro filho do ramo',
  'help.scPriority': 'sobe / desce a prioridade do ramo',
  'help.scHelp': 'abre ajuda e atalhos',
  'help.scEsc': 'fecha prompt · editor · grupo · seleção',
  'help.scTap': 'toque duplo num item: a câmera voa até ele · toque duplo no gramado: de volta a todo o lance · na barra mestra: reproduza a partir dali',
  'help.scParent': 'salta para o ramo pai',
  'help.scGroup': 'agrupe jogadores e objetos — arraste um, todos movem',
  'help.scgTouch': 'Toque (celular / tablet)',
  'help.tTap': 'selecione · arraste para mover (AUTO grava a key)',
  'help.tLane': 'toque numa chave, ou na barra abaixo: o editor daquela chave / transição — o lado da chave: Altura · ◂ ▸ ▴ ▾ ajustam o ponto 0,5 m · ◂◂ 1 s a 1 s ▸▸ ajustam o tempo · ⇢ Encaixar no instante de outro item · Os passos movem… (só este momento, ou este e tudo depois) · Excluir a chave — o lado da transição: Estilo Reta / Esquerda / Direita · Curva · no voo da bola também Arco e Pico do arco',
  'help.tHold': 'segure um item não selecionado: adiciona-o ao grupo',
  'help.tMasterHold': 'a barra mestre: deixe o dedo PRESSIONADO sobre um trecho para tocar só aquele ramo',
  'help.tMenu': 'toque de novo num item selecionado: o menu dele — só os verbos que ESTE item pode fazer: ✚ registrar aqui · ➜ setas · ↝ curvar · ⤴ elevar · ↑↓ altura · 🎯 marcar · ✕ parar de marcar · 📏 ajustar as distâncias · 📎 vincular · 👻 esconder a bola · ↻ outra vez · ⧉ Selecionar um grupo… · ⧉ Selecionar esta linha · ⧉ Selecionar todos deste tipo · ⧉ Próximo na fila · ⧉ Virar a fila · ⇄ espelhar (um, o lado, o campo inteiro) · ✕ excluir esta ação · 📋 copiar · 📌 colar · ⧉ duplicar · 🗑 excluir todos (ou segure-o)',
  'help.tStay': 'o cartão fica aberto depois de ✚ registrar aqui, ↻ de novo, ⇄ espelhar e registrar uma ação — mova a régua e toque de novo; o ✚ diz o novo instante',
  'help.tBall': 'segure a bola: seleciona · segure de novo para o menu dela — 📎 vincular é uma das opções',
  'help.tZone': 'segure uma zona: o editor de forma · arraste um canto para movê-lo · segure de novo para pegar o vértice 1 · + Adicionar um canto (toque no campo), − Remover um (toque num canto) · toque num jogador ou na bola para vincular esse ponto',
  'help.tRuler': 'arraste a régua para mover no tempo — ela para numa chave quando você chega perto · segure-a: o tempo deste ramo — mais lento · mais rápido · ↔ abrir ou fechar um vão depois do cursor · terminar na última chave',
  'help.tKeyDel': 'segure uma chave na faixa: exclua-a — o gêmeo do clique duplo, para o polegar',
  'help.tKeyMove': 'arraste uma chave pela faixa: ela muda de instante — e se alinha com outra chave quando chega perto',
  'help.tUndo': 'o ↩ no topo do campo desfaz a última mudança · o ↩ dentro de uma mensagem desfaz só aquela · ↪ refaz',
  'help.tKeyAdd': 'segure um trecho vazio de uma faixa: a chave daquela faixa, gravada no instante do toque',
  'help.tRename': 'segure o nome do ramo no cabeçalho da faixa: renomeie-o',
  'help.tPitch': 'segure um ponto vazio do campo: a vista — só o que ESTE cenário tem: 🎥 de cima · da linha · padrão · ➜ setas para todos os itens · ◼ formas de todas as zonas · 🎯 parar todas as marcações · 📏 todas as distâncias · ⤴⤵ todos os voos para cima ou para baixo',
  'help.tBranch': 'a árvore de ramos à esquerda: toque num ramo para selecioná-lo · toque de novo — ou ✎ — para abrir a linha do tempo dele · ⧉ duplicar um ramo — linha do tempo, nome e lugar, aberta na hora · ⧉ copiar a linha do tempo deste ramo · ⎘ colá-la noutro ramo · + um sub-ramo · + um irmão · ✂ aparar · ▲▼ ordem · ✕ remover — o PRIMEIRO ramo não tem irmão nem pode ser removido',
  'help.tEdge': 'arraste a borda direita da régua: estique ou corte a janela',
  'help.tSpeed': 'toque no selo × do cursor: alterna a velocidade de reprodução',
  'help.tPlace': 'segure o campo armado: coloca uma linha de itens',
  'help.tLineRepeat': 'coloca mais um no mesmo passo — toca de novo para andar a fila, o cartão fica aberto',
  'help.tGroup': 'o cartão pode reunir a LINHA deste jogador, ou todo o plantel — e então ↑ · ↝ · 🗑 actuam em todos ao mesmo tempo',
  'help.tAuto': 'o botão no cabeçalho da faixa: ligado, mover qualquer coisa grava uma chave de posição no segundo atual · desligado, você move as coisas sem deixar chaves',
  'help.tToolbar': 'o + de items: arma um cone, alvo, bola ou zona e toque no campo para colocá-lo · um ✓ marca o tipo já armado — toque de novo para cancelar · o + de um plantel: troque de equipe, escolha um desenho ou arma um jogador reserva · o − remove o item selecionado',
  'help.scSpeed': 'velocidade de reprodução',
  'help.scSpeedReset': 'velocidade volta a 1×',
  'help.scStretch': 'estica / comprime o tempo do ramo',
  'help.scPadBranch': 'aumenta / reduz o comprimento do ramo (chaves ficam)',
  'help.scRipple': 'desliza todas as chaves após o cursor (⌥arraste · ⌥⇧ = 1 s)',
  'help.scCopy': 'copiar / colar (rota + estado)',
  'help.scSaveOpen': 'salvar cenário / abrir navegador',
  'help.scNew': 'novo cenário (Ctrl+Z desfaz)',
  'help.scBranchCopy': 'copia / cola o tempo do ramo',
  'help.scDup': 'duplicar item',
  'help.scDelete': 'excluir seleção',
  'help.scDelGroup': 'exclui o grupo inteiro',
  'help.scUndo': 'desfazer / refazer',

  'add.ball': '⚽ Bola',
  'add.cone': '🟠 Cone',
  'add.target': '🎯 Alvo',
  'add.polyZone': '▱ Zona Poligonal',
  'add.circleZone': '⭕ Zona Circular',
  'add.dynamicZone': '◈ Zona Dinâmica',
  'add.duplicate': '⧉ Duplicar selecionado',
  'tb.file': 'Arquivo',
  'tb.addHome': 'Adicionar um jogador da casa',
  'tb.addAway': 'Adicionar um jogador visitante',
  'tb.placingHome': 'Colocando um jogador da casa — toque no campo · toque de novo no nome para cancelar',
  'tb.placingAway': 'Colocando um jogador visitante — toque no campo · toque de novo no nome para cancelar',
  'tb.addItem': 'Adicionar um item',
  'tb.armedItem': '{what} armado — toque no campo · toque de novo para cancelar',
  'tb.removeItem': 'Remover o item selecionado',
  'tb.removeItemFirst': 'Remover o item selecionado — selecione algo antes',
  'tb.homeLogo': 'Escudo do time da casa',
  'tb.awayLogo': 'Escudo do time visitante',
  'tb.publicOffline': 'Conecte-se à internet para ver cenários públicos',
  'tb.duplicateHint': 'Duplica o item selecionado, deslocado — toque de novo para outro',
  'tb.duplicatePlayer': 'Jogadores ocupam vagas fixas do elenco — copie um e cole em outra vaga',
  'tb.duplicateNeedItem': 'Duplicar — selecione antes um cone, alvo, zona ou bola',
  // Os quatro verbos de alinhamento saíram da barra e vivem agora no menu de
  // ação do próprio item (itm.nextLineStep / itm.turnLine / itm.mirrorHalf /
  // itm.mirrorSide), onde a distância aparece escrita e o alvo é inequívoco.

  'modal.welcome.title': 'Bem-vindo ao KickViz',
  'modal.welcome.msg': "Bem-vindo! Toda conta — incluída a Grátis — tem acesso a TODAS as funções: times, salvar e carregar cenários, cenários públicos, compartilhamento e muito mais.\n\nPara manter o serviço, contas Grátis veem um anúncio rápido ao salvar ou carregar um cenário.\n\nO Premium remove todos os anúncios — faça upgrade quando quiser pelo menu File.",
  'modal.gotIt': 'Entendi',
  'modal.ad.saveMsg': 'Obrigado por usar o KickViz! Aqui vai um anúncio rápido — seu cenário será salvo logo em seguida.',
  'modal.ad.loadMsg': 'Obrigado por usar o KickViz! Aqui vai um anúncio rápido — depois você poderá carregar seu cenário.',
  'modal.ad.loadPublicMsg': 'Obrigado por usar o KickViz! Aqui vai um anúncio rápido — depois você poderá navegar pelos cenários públicos.',
  'prompt.saveAsCopy': 'Nome para a cópia do novo cenário:',
  'view.2d': '2D', 'view.3d': '3D',
  'view.orbit': 'Órbita',
  'view.pan': 'Mover',
  'view.zoomTo': 'Aproximar do item selecionado — tecla: F · toque duplo no touch',
  'view.names': 'Nomes',
  'view.namesTitle': 'Mostrar o nome sobre cada jogador',
  'branch.from': 'VEM DE',
  'branch.current': 'ATUAL · IRMÃOS',
  'branch.possible': 'PRÓXIMOS POSSÍVEIS',
  'branch.rootNothingBefore': 'Raiz — nada antes dela',
  'branch.nothingBefore': 'Nada antes deste ramo',
  'branch.addSub': 'Adicionar sub-ramo',
  'branch.addSubHint': 'Continue do fim deste ramo — tecla: B',
  'branch.addSibling': 'Adicionar ramo irmão',
  'branch.addSiblingHint': 'Criar um novo ramo que continua a partir do mesmo ponto que este',
  'branch.duplicate': 'Duplicar este ramo',
  'branch.duplicateHint': 'Uma cópia deste ramo — a linha do tempo, o nome e o lugar — aberta e selecionada para você já editá-la',
  'branch.copy': 'Copiar a linha do tempo deste ramo',
  'branch.paste': 'Colar a linha do tempo copiada neste ramo',
  'branch.pasteEmpty': 'Copie primeiro a linha do tempo de um ramo — botão ⧉ na linha dele',
  'branch.pasteSame': 'Este é o ramo que você copiou — escolha outro para colar',
  'branch.copiedToast': 'Linha do tempo copiada de',
  'branch.pastedToast': 'Linha do tempo colada em “{name}” — sub-ramos rederivados do novo fim · o botão ↩ desfaz',
  'branch.edit': 'Abrir a timeline deste ramo',
  'branch.up': 'Subir prioridade',
  'branch.down': 'Descer prioridade',
  'branch.remove': 'Remover este ramo',
  'branch.masterPlay': 'Reproduzir tudo',
  'branch.playToNext': 'Reproduzir até o próximo ramo',
  'branch.order': 'Prioridade',
  'branch.orderHint': 'Prioridade entre os irmãos — 1 é a continuação padrão',
  'branch.name': 'Nome do ramo',
  'branch.defaultTag': 'PADRÃO',
  'branch.siblingNote': 'A ordem 1 é a continuação padrão quando esta divisão é alcançada.',
  'branch.noChildren': 'Ainda sem sub-ramos — adicione um para dividir a história no fim desta timeline',
  'branch.back': 'VOLTAR',
  'branch.backTitle': 'Voltar à árvore do cenário',
  'branch.lockedKey': 'Herdado do ramo anterior — somente leitura',
  'branch.lockedKeyToast': 'Este ramo começa onde o anterior terminou — a primeira chave é somente leitura. Arraste a régua (ou pressione →) para sair das 00:00 primeiro.',
  'branch.lockedDragToast': 'Este ramo começa onde o anterior terminou — afaste o cursor das 00:00 e arraste',
  'branch.addedToast': 'Sub-ramo adicionado: “{name}” — você já está nele · “Adicionar sub-ramo” cria outro sob “{parent}”',
  'branch.siblingAddedToast': 'Ramo irmão adicionado: “{name}” — você já está nele · “+ Adicionar ramo irmão” cria outra',
  'branch.samePoint': 'o mesmo ponto',
  'branch.rowTitle': 'Selecione · um duplo clique, ou um segundo toque, abre a linha do tempo',
  'branch.chosen': 'Sua escolha nesta divisão — faz parte do caminho que a linha do tempo mestra reproduz',
  'branch.subBranches': 'sub-ramos',
  'branch.inTimeline': 'Aberto na linha de tempo',
  'branch.resize': 'Arraste para redimensionar o painel da árvore do cenário',
  'branch.clipTime': 'Cortar tempo — define a duração desta linha de tempo até a sua última chave',
  'branch.clippedTo': 'Linha de tempo cortada em',
  'branch.clipNothing': 'Este ramo ainda não tem chaves para cortar',
  'branch.masterPlaying': 'Reproduzir tudo',
  'master.title': 'Linha de tempo mestre — todo o cenário',
  'master.hint': 'Um segmento por ramo. Arraste o marcador para percorrer todo o cenário: ele move a árvore e normaliza cada ramo pelo seu próprio comprimento (sem tempos). Toque duplo ou clique duplo num segmento para reproduzir a partir dele — segure ⇧ no clique duplo para reproduzir só esse ramo (numa tela de toque, deixe o dedo pressionado sobre o segmento).',
  'master.defaultPick': 'O padrão (ordem 1) — você ainda não escolheu um caminho aqui · abra a árvore para escolher',
  'master.yourPick': 'Sua escolha neste ponto — o caminho mestre segue ela · as escolhas valem só nesta sessão',
  'ball.title': 'Bola',
  'ball.colour': 'Cor',
  'ball.visible': 'Visível',
  'ball.close': 'Fechar opções da bola',
  'ball.hiddenHint': 'Oculta — não é desenhada nem tocável · marque “Visível” acima para trazê-la de volta',
  'ball.attachHint': '📎 Vincular a… está no menu da bola · arraste a bola sobre um jogador para passar',

 // What the ↩ button and the undo toast name the change they are about to put
 // back. These are DICTIONARY KEYS, not English words: the label travels on the
 // undo stack and is read out in whatever language the coach is using, so a
 // Portuguese screen can never say "Desfazer: position key". It also means
 // switching language mid-session cannot split one gesture into two undo steps,
 // because the stack compares keys, not display text.
  'undoVerb.edit': 'alteração',
  'undoVerb.positionKey': 'chave de posição',
  'undoVerb.attachKey': 'chave de anexação',
  'undoVerb.visualKey': 'chave de visuais',
  'undoVerb.actionKey': 'chave de ação',
  'undoVerb.keyHeight': 'altura da chave',
  'undoVerb.keyPosition': 'posição da chave',
  'undoVerb.arrowKey': 'chave de seta',
  'undoVerb.transition': 'transição',
  'undoVerb.arrowColour': 'cor da seta',
  'undoVerb.zoneShape': 'forma da zona',
  'undoVerb.addBranch': 'adicionar ramo',
  'undoVerb.deleteBranch': 'excluir ramo',
  'undoVerb.clipBranchTime': 'recortar tempo do ramo',
  'undoVerb.renameBranch': 'renomear ramo',
  'undoVerb.branchOrder': 'ordem dos ramos',
  'undoVerb.branchPriority': 'prioridade do ramo',
  'undoVerb.pasteBranchTimeline': 'colar a linha do tempo do ramo',
  'undoVerb.addSiblingBranch': 'adicionar ramo irmão',
  'undoVerb.duplicateBranch': 'duplicar ramo',
  'undoVerb.branchDuration': 'duração do ramo',
  'undoVerb.placeFormation': 'colocar formação',
  'undoVerb.itemHeight': 'altura do item',
  'undoVerb.zoneColour': 'cor da zona',
  'undoVerb.ballProperties': 'propriedades da bola',
  'undoVerb.zoneOpacity': 'opacidade da zona',
  'undoVerb.zoneMode': 'modo da zona',
  'undoVerb.zoneRadius': 'raio da zona',
  'undoVerb.zoneOpenClosed': 'zona aberta/fechada',
  'undoVerb.zoneThickness': 'espessura da zona',
  'undoVerb.zoneVertices': 'vértices da zona',
  'undoVerb.zoneVertexLink': 'vínculo de vértice da zona',
  'undoVerb.pasteRoute': 'colar a rota',
  'undoVerb.pasteItem': 'colar item',
  'undoVerb.newScenario': 'novo cenário',
  'undoVerb.deleteKey': 'excluir chave',
  'undoVerb.deleteMoment': 'excluir momento',
  'undoVerb.archPeak': 'pico do arco',
  'undoVerb.attachBall': 'vincular bola',
  'undoVerb.attachZone': 'vincular zona',
  'undoVerb.attachObj': 'vincular item',
  'undoVerb.attachGroup': 'vincular grupo',
  'undoVerb.bendAmount': 'curvatura',
  'undoVerb.bendPath': 'curvar caminho',
  'undoVerb.deleteGroup': 'excluir grupo',
  'undoVerb.deleteZoneWindow': 'excluir janela da zona',
  'undoVerb.deleteZoneWindows': 'excluir janelas da zona',
  'undoVerb.zoneVisualScene': 'formas da zona',
  'undoVerb.detach': 'desvincular',
  'undoVerb.duplicateGroup': 'duplicar grupo',
  'undoVerb.duplicateItem': 'duplicar item',
  'undoVerb.groupArrowColour': 'cor das setas do grupo',
  'undoVerb.groupArrows': 'setas do grupo',
  'undoVerb.groupHeight': "altura do grupo",
  'undoVerb.groupBendPath': 'curvar caminho do grupo',
  'undoVerb.groupPositionKeys': 'chaves de posição do grupo',
  'undoVerb.loftHeight': 'altura do lançamento',
  'undoVerb.loftToggle': 'alternar lançamento',
  'undoVerb.loftGroup': 'elevar grupo',
  'undoVerb.markPlayer': 'marcar jogador',
  'undoVerb.markGroupGap': 'distâncias dos marcadores',
  'undoVerb.markGroup': 'marcar grupo',
  'undoVerb.unmarkGroup': 'desmarcar grupo',
  'undoVerb.mirrorEnd': 'espelhar ponta',
  'undoVerb.mirrorFlank': 'espelhar lado',
  'undoVerb.moveKeyInTime': 'mover chave no tempo',
  'undoVerb.pass': 'passe',
  'undoVerb.rippleKeys': 'chaves em cascata',
  'undoVerb.setAction': 'definir ação',
  'undoVerb.setActions': 'definir ações',
  'undoVerb.stretchTimeline': 'esticar a linha do tempo',
  'undoVerb.zoneVertexAdd': 'adicionar vértice da zona',
  'undoVerb.zoneVertexRemove': 'remover vértice da zona',
  'undo.title': 'Desfazer a última alteração',
  'vp.undoWhat': 'Desfazer: {what}',
  'vp.undoHintOne': '{what} · 1 passo atrás{limit}',
  'vp.undoHintMany': '{what} · {n} passos atrás{limit}',
  'vp.undoLimit': ' (o limite — as edições mais antigas se perderam)',
  'vp.redoWhat': 'Refazer: {what}',
  'vp.redoHintOne': '{what} · 1 passo à frente',
  'vp.redoHintMany': '{what} · {n} passos à frente',
  'redo.title': 'Refazer o que você desfez',

  'tl.timeline': 'LINHA DO TEMPO',
  'tl.pause': 'Pausar',
  'tl.durationHint': 'Comprimento da linha do tempo em segundos — clique para editar',
  'tl.hint': 'Role ou clique na régua para mover no tempo · arraste a borda para redimensionar',
  'tl.keyMovement': 'Marcar movimento — fixa onde este item está no cursor',
  'tl.keyMovementPlayer': 'Marcar movimento — fixa onde este jogador está no cursor',
  'tl.keyAction': 'Marcar ação — adiciona uma ação no cursor',
  'cap.button': 'Captura',
  'cap.hint': 'Capture uma imagem ou grave o campo em vídeo',
  'cap.screenshot': 'Captura de tela',
  'cap.record': 'Gravar vídeo',
  'cap.stop': 'Parar gravação',
  'cap.saving': 'Salvando…',
  'cap.recordingBody': 'Gravando o campo',
  'cap.recordingToast': 'Gravando — toque ● no botão da câmera de novo para parar',
  'seg.currentColour': 'Cor atual — toque para mudar',
  'seg.defaultColour': 'Definir a cor padrão da seta',
  'seg.defaultColourAria': 'Definir cor padrão',
  'teams.enterNext': 'Enter — próximo jogador',
  'teams.pickTitle': 'Selecionar um time',
  'zv.bindHint': 'Arraste um vértice direto sobre um jogador ou a bola e ele o segue. Solte o vértice preso de volta na zona para liberá-lo.',
  'ad.label': 'Publicidade',
  'ad.close': 'Fechar publicidade',
  'tl.keyGraphic': 'Marcar setas — liga ou desliga as setas do trajeto no cursor',
  'tl.keyAttachment': 'Marcar anexação — prende isto a um jogador no cursor',
  'tl.keyVisual': 'Marcar visuais — inicia uma janela de visuais da zona no cursor',
  'tl.posKey': 'Keyframe de posição às {t} — toque para definir a altura · arraste para mover no tempo',
  'tl.keyHoldDelete': 'Segure a chave para excluí-la',
  'tl.noPlayers': 'Nenhum jogador ainda.',
  'tl.addPlayersHint': 'Use ADICIONAR JOGADOR CASA / FORA para posicionar jogadores.',

  'itm.item': 'ITEM',
  'itm.ball': 'Bola',
  'itm.cone': 'Cone',
  'itm.target': 'Alvo',
  'itm.zone': 'Zona',
  'itm.player': 'Jogador',
  'itm.remove': 'Remover',
  'itm.copy': 'Copiar',
  'itm.paste': 'Colar',
  'itm.attach': 'Anexar',
  'itm.detach': 'Soltar',
  'itm.height': 'Altura',
  'itm.arcType': 'Trajetória',
  'itm.color': 'Cor',
  'itm.attachedTo': 'Anexado a:',
  'itm.arrowsShow': '➜ Setas ligadas ({t})',
  'itm.arrowColour': '➜ Cor da seta…',
  'itm.arrowColourAll': '➜ Colorir as {n} setas iguais…',
  'itm.arrowsHide': '➜ Setas desligadas ({t})',
  'itm.arrowsShowMany': '➜ Setas ligadas nos {n} ({t})',
  'itm.arrowsHideMany': '➜ Setas desligadas nos {n} ({t})',
  'itm.arrowsSomeOn': ' — {k} já ligadas',
  'itm.arrowsNoLane': ' — {n} não tem faixa de seta',
  'itm.bendRun': '↝ Curvar esta corrida com precisão…',
  'itm.bendAll': '↝ Curvar as {n} corridas iguais…',
  'itm.bendPass': '↝ Curvar este passe com precisão…',
  'itm.copyIt': '📋 Copiar',
  'itm.onlyThis': ' — só este item',
  'itm.allOfThese': ' — todos os {n}',
  'itm.pasteHere': '📌 Colar {name} aqui',
  'itm.duplicateIt': '⧉ Duplicar',
  'itm.nextLineStep': '⧉ Próximo da fila ({m} m)',
  'itm.turnLine': '⧉ Virar a linha 90° ({m} m)',
  'itm.mirrorHalf': '⇄ Copiar outra metade',
  'itm.mirrorSide': '⇄ Copiar outro lado',
  'itm.mirrorFlank': '⇄ Espelhar ala',
  'itm.mirrorEnd': '⇄ Espelhar ponta ↔ ponta',
  'itm.mirrorPitch': '⇄ Campo todo ({n})',
  'itm.mirrorMenu': '⇄ Espelhar… ({n})',
  'itm.mirrorMenuTitle': 'Virar do avesso — por onde?',
  'itm.mirrorMenuHint': 'Copiar põe um item NOVO do outro lado · espelhar move o que já está lá · toque um e as opções ficam, então um par leva dois toques',
  'itm.selectAllKind': '⧉ Selecionar todos {kind} ({n})',
  'itm.selectLine': '⧉ Selecionar esta linha ({n})',
  'itm.selectGroupMenu': '⧉ Selecionar um grupo… ({n})',
  'itm.selectGroupTitle': 'Qual grupo?',
  'itm.selectGroupHint': 'a linha são os jogadores na mesma fila deste · o lado é todo o plantel · toque num e o cartão abre no grupo',
  'itm.deleteAll': '🗑 Excluir todos {n}',
  'itm.menuHint': 'Escolha uma ação — ou os verbos do item no topo deste menu.',

  // SEGUIR: um jogador sombra de outro. As próprias teclas de movimento ficam
  // onde foram criadas; o deslocamento do líder, medido a partir do momento em
  // que o acompanhamento foi pedido, soma-se como um deslocamento extra.
  'itm.markAsk': '🎯 Marcar um adversário…',
  'itm.markAskMany': '🎯 Marcar um adversário… ({n} jogadores)',
  'itm.markGapMany': '📏 Ajustar todas as distâncias… ({n} marcadores)',
  'itm.keyHere': '✚ Marcar aqui ({t})',
  'itm.keyMany': '✚ Marcar todos os {n} aqui ({t})',
  'itm.keyFollow': ' — {n} seguem um portador',
  'itm.keyFollowOne': ' — {n} segue um portador',
  'itm.attachAsk': '📎 Vincular a…',
  'itm.attachChange': '📎 Em {who}',
  'itm.attachMany': '📎 Vincular a… ({n} itens)',
  'itm.markChange': '🎯 Marca {who}',
  'mark.title': 'Quem {who} marca?',
  'mark.changeTitle': '{who} marca {leader} — mudar o marcado?',
  'mark.pickHint': 'Toque em um ADVERSÁRIO no campo — marcar é entre times',
  'mark.stop': 'Parar de marcar ({t})',
  'mark.self': '{who} não pode marcar a si mesmo — escolha um adversário',
  'mark.cycle': '{who} já marca {leader} — um dos dois precisa parar primeiro',
  'mark.teammate': 'Marcar é entre times — {who} é do seu time',
  'mark.started': '{who} marca {leader} · posição: {where} · {d} m · desde {t}',
  'mark.turning': '{who} sai de {from} para {where} contornando {leader} · chega em {t}',
  'mark.stopped': '{who} para de marcar em {t} · fica com o terreno que ganhou e segue daí',
  'mark.pickTitle': 'Marcar {who} — de onde?',
  'mark.positionTitle': 'Posição da marcação',
  'mark.compassHint': 'Toque ou arraste dentro do círculo para posicionar o marcador. O controle define o alcance (0,5–20 m). Frente é o lado do gol deles.',
  'mark.rangeAria': 'Alcance do círculo em metros',
  'mark.placeAria': 'Posição de marcação — arraste; esquerda e direita ajustam o ângulo, cima e baixo ajustam a distância',
  'mark.distAria': 'Distância de marcação em metros',
  'mark.pickConfirm': 'Aplicar',
  'mark.pickOther': 'Trocar jogador',
  'mark.pickStop': 'Parar de marcar',
  'mark.pickDelete': 'Excluir chave',
  'mark.deleted': 'Chave de marcação removida em {t} · {who} volta para onde a corrida ao redor dele já o coloca',
  'attach.deleteKey': '✕ Remover esta chave de vínculo ({t})',
  'attach.keyDeleted': 'Chave de vínculo removida em {t} · a {what} volta para onde o próprio trajeto já a coloca',
  'mark.stopMenu': '✕ Parar de marcar {who} ({t})',
  'mark.sameSpot': "Já marca {leader} em {t} — {where}, a {d} m · escolha outro lugar ou distância e toque em “Marcar daqui”",
  'mark.dir.front': 'frente',
  'mark.dir.frontRight': 'frente-direita',
  'mark.dir.right': 'direita',
  'mark.dir.backRight': 'trás-direita',
  'mark.dir.back': 'trás',
  'mark.dir.backLeft': 'trás-esquerda',
  'mark.dir.left': 'esquerda',
  'mark.dir.frontLeft': 'frente-esquerda',
  'mark.dragged': 'Marcado de {where}, a {d} m de {leader} — arraste de novo para mover a marca',
  'mark.dragNowhere': '{leader} está bem ali — uma marca precisa de um espaço para ficar · solte um passo antes',
  'mark.groupSet': '{n} marcando {leader} — cada um de onde está',
  'mark.groupSetSome': '{n} marcando {leader}, {skipped} de fora — mesmo lado, marcando de volta ou em cima',
  'mark.groupNone': 'Ninguém neste grupo pode marcar {leader} — mesmo lado, marcando de volta ou em cima dele',
  'mark.groupAskMany': 'Quem esses {n} marcam?',
  'mark.groupPickHint': 'Toque em um ADVERSÁRIO no campo — cada um marca de onde está · em {t}',
  'mark.groupOne': 'Só um jogador neste grupo — use Marcar nele diretamente',
  'mark.stopMenuMany': '✕ Parar de marcar ({n} jogadores, {t})',
  'mark.groupStopped': '{n} param de marcar em {t} · cada um fica com o terreno que ganhou',
  'mark.gapAskMany': 'Quão longe esses {n} pressionam?',
  'mark.gapSetMany': '{n} marcadores agora a {d} m · {where}',
  'mark.gapSkipped': ' ({n} sem marcar ninguém aqui)',
  'mark.gapFew': 'Só um destes está marcando alguém — use 🎯 nele diretamente',
  'mark.groupNoneStop': 'Ninguém neste grupo está marcando alguém',
  'undoVerb.markDrag': 'posição da marca',
  // Tirar um jogador do time solta o que ele carregava e quem o seguia.
  'bench.benched': '{who} saiu do time — {what} caíram em {t} · o botão ↩ traz tudo de volta',
  'bench.markerOne': 'um marcador',
  'bench.markerMany': '{n} marcadores',
  'bench.carriedOne': 'o item carregado',
  'bench.carriedMany': '{n} itens carregados',

  // ANEXAR / PASSE — os verbos de carregar bola e zona, e a escolha do voo.
  'attach.follows': '{what} agora segue {who} em {t} · o botão ↩ desfaz',
  'attach.release': 'Soltar aqui ({t})',
  'attach.released': '{what} solto em {t} — fica por conta própria · o botão ↩ traz a posse de volta',
  'attach.title': 'Anexar {name}?',
  'attach.changeTitle': '{name} segue {who} — passar para outro?',
  'attach.pickHint': 'Toque no novo portador no campo · Soltar encerra o acompanhamento em {t}',
  'attach.pickAny': 'Escolha jogador ou bola',
  'attach.groupTitle': 'Vincular {n} itens a…?',
  'attach.groupChangeTitle': '{n} itens seguem outros — passe-os para outro?',
  'attach.groupRelease': 'Soltar todos ({n}) em {t}',
  'attach.groupFollows': '{n} passam a seguir {who} em {t} · o botão ↩ desfaz',
  'attach.groupReleased': '{n} soltos em {t} — ficam por conta própria · o botão ↩ traz o vínculo de volta',
  'attach.groupNone': 'Nada neste grupo pode ser vinculado — jogadores usam Marcar',
  'attach.pickPlayer': 'Escolha jogador',
  'attach.ballReleased': 'Bola solta',
  'attach.ballTo': 'Bola anexada a {who}',
  'attach.zoneAlready': 'A zona já segue {who}',
  'attach.zoneNobody': 'A zona não segue ninguém — solte-a sobre um jogador para anexá-la',
  'attach.zoneTo': 'A zona agora segue {who}',
  'attach.zoneOff': 'Zona solta — fica onde está no campo',
  'attach.objAlready': '{what} já segue {who}',
  'attach.objNobody': '{what} não segue ninguém — solte-o sobre um jogador para anexá-lo',
  'attach.objTo': '{what} agora segue {who}',
  'attach.objOff': '{what} solto — fica onde está no campo',
  'detach.fromOne': 'Desanexada de {who} — está selecionada agora, arraste para onde quiser',
  'detach.fromMany': '{n} desanexadas de {who}',
  'zoneVertex.bound': 'V{n} → {who} — a zona se deforma conforme eles se movem',
  'zoneVertex.freed': 'V{n} liberada — fica onde você a colocou',
  'pass.title': 'Passar para {who}?',
  'pass.attachTitle': 'Anexar a {who}?',
  'pass.detachTitle': 'Soltar?',
  'pass.flightHint': 'Solto agora · escolha quanto tempo dura o voo',
  'pass.quick': 'Rápido · 0,5 s',
  'pass.normal': 'Normal · 1 s',
  'pass.lofted': 'Elevado · 2 s',
  'pass.loftSuffix': ", por alto",
  'pass.moveOnly': 'Só mover',
  'pass.to': 'Passe para {who} — recebido {flight} s depois{how} · arraste a chave da recepção para ajustar',
  'pass.stretched': ' · ramo estendido até {t} para o voo',
  'pass.sameCarrier': 'A bola já segue {who} — solte sobre outro para passar, ou no campo livre para liberar',
  'pass.hasBall': '{who} já está com a bola — solte sobre outro jogador',
  'pass.giveTitle': 'Dar a bola a {who}?',
  'pass.giveHint': '{carrier} solta agora · escolha quanto tempo dura o voo',
  'pass.takeHint': '{who} assume o controle',

  // VÉRTICES DA ZONA — o editor de forma. As teclas citam os atalhos; o toque
  // cita o gesto no campo, porque celular não tem Ctrl e o botão ↩ está na tela.
  'vertex.nudged': 'Vértice {n} movido {step} m · arraste no campo para modelar livremente',
  'vertex.noWindow': 'Nenhuma janela de visual no cursor — ✚ na faixa Visual da zona cria uma',
  'vertex.minTwo': 'Uma zona mantém ao menos 2 vértices — esta já é uma linha',
  'vertex.removed': 'Vértice {n} removido — {count} restantes · Ctrl+Z traz de volta',
  'vertex.added': 'Vértice {n} adicionado no meio da aresta — Ctrl+←→ move · ⇧A adiciona outro',
  'vertex.grabbed': 'Vértice 1 capturado — arraste-o no campo · + Adicionar põe outro · − Remover tira um',
  'vertex.grabbedOf': 'Vértice 1 de {count} capturado — arraste-o no campo · + Adicionar põe outro · − Remover tira um',
  'vertex.grabFirst': 'Capture um vértice antes — T duas vezes captura o vértice 1 · ⇧A adiciona um na aresta',
  'vertex.addedTouch': 'Vértice {n} adicionado — {count} na forma · arraste no campo para posicionar',
  'vertex.removedTouch': 'Vértice {n} removido — {count} restantes · o botão ↩ devolve',
  'view.title': 'Vista — o campo',
  'view.hint': 'De onde você quer olhar?',
  'view.top': '🎥 De cima',
  'view.low': '🎥 Da linha',
  'view.reset': '🎥 Voltar à vista padrão',
  'view.arrowsOn': '➜ Mostrar todas as setas — {n} em campo',
  'view.arrowsOff': '➜ Esconder todas as setas ({n} ligadas)',
  'view.shapesOn': '◼ Mostrar a forma de todas as zonas ({n} zonas)',
  'view.shapesOff': '◼ Esconder a forma de todas as zonas ({n} ligadas)',
  'view.unmarkAll': '🎯 Parar todas as marcações ({n} jogadores)',
  'view.loftOn': '⤴ Elevar todos os voos ({n} bolas)',
  'view.loftOff': '⤵ Baixar todos os voos ({n} bolas)',
  'view.gapAll': '📏 Ajustar todas as distâncias ({n} marcadores)',
  'view.autoOff': 'AUTO desligado — arrastar move a pose base, sem chaves',
  'view.autoOn': 'AUTO ligado — cada arrastão cria uma chave no cursor',
  'view.branches': 'Vista de ramificações — abra uma ramificação para voltar à linha de tempo',
  'view.timeline': 'Linha de tempo — ‹ VOLTAR volta à árvore do cenário',
  'view.ballHidden': 'Bola escondida — não é desenhada · este mesmo menu a traz de volta, ou toque na linha dela na faixa',
  'view.ballShown': 'Bola visível de novo',
  'toast.hideGone': 'Essa bola não está mais no campo — nada para esconder ou mostrar',
  'view.panelsBack': 'Painéis de volta — ▼ os esconde para ver o campo inteiro',
  'view.panelsHidden': 'Painéis escondidos — ▲ os traz de volta · a barra fica',
  'view.panelsShowAria': 'Mostrar os painéis da linha de tempo',
  'view.panelsHideAria': 'Esconder os painéis da linha de tempo',
  'speed.at': 'Reprodução a {r}× — toque no {r}× junto ao tempo para mudar',
  'speed.top': 'Velocidade máxima 4× — toque no 4× para descer',
  'speed.floor': 'Mais devagar possível: 0,5× — toque no 0,5× para subir',
  'tl.speedChipTitle': 'Velocidade de reprodução — clique para alternar 0,5× → 1× → 2× → 4×',
  'tl.rowSelected': 'Selecionado — ⇄ Espelhar está no menu de ações · toque em outra linha para agrupar · toque de novo para desmarcar',
  'tl.rowUnselected': 'Toque para selecionar este jogador',
  'tl.scrubHint': 'Arraste a régua para mover no tempo — ela para numa chave quando você chega perto · segure-a para o menu do ramo · arraste a borda direita para esticar o tempo',
  'tl.branchName': 'Nome do ramo',
  'tl.stretchHint': 'Arraste para esticar ou encurtar este ramo — encaixa em ¼ s · nunca passa da última chave · o botão ↩ devolve o comprimento',
  'tl.resizeHandle': 'Arraste para redimensionar',
  'tl.autoKeyHint': 'Auto-chave: ligado, mover qualquer coisa grava uma chave de posição no segundo atual',
  'tl.playBranch': 'Reproduzir este ramo',
  'tl.pauseBranch': 'Pausar este ramo',
  'tl.playBranchHint': 'Reproduzir este ramo — ▶ Reproduzir tudo corre o caminho inteiro',
  'tl.visualKey': 'Keyframe visual às {t} — toque para editar a forma · arraste para mover no tempo',
  'tl.pauseBranchHint': 'Pausar este ramo — ▶ Reproduzir tudo corre o caminho inteiro',
  'tl.midAttach': 'Anexar: {what} às {t} — toque: escolher outro · arraste: mover · toque duplo: excluir',
  'tl.midAction': 'Ação: {what} às {t} — toque: escolher outra ação · arraste: mover · toque duplo: excluir',
  'tl.keyMark': 'Marcação — registre a quem este jogador marca no cursor',
  'tl.tnMovement': 'Faixa de movimento',
  'tl.tnAction': 'Faixa de ação',
  'tl.tnGraphic': 'Faixa de setas',
  'tl.tnMark': 'Faixa de marcação',
  'tl.tnVisual': 'Faixa de visuais',
  'tl.tnAttachment': 'Faixa de anexação',
  'tl.laneHold': ' — segure a faixa para adicionar uma chave aqui',
  'tl.segBar': 'Transição {a} → {b}: {style}{arch} — toque para curvar o trajeto',
  'tl.arrowBar': 'Setas do trajeto {a} → {b} — toque para mudar a cor',
  'tl.zoneVisBar': 'Visuais da zona {a} → {b} — toque para editar raio, forma e cantos',
  'tl.branchRename': '{name} — segure o nome para renomeá-lo',
  'tl.markKey': '🎯 Marcando {who} às {t} — toque para mudar o ponto · arraste para mover no tempo',
  'tl.markKeyOff': '🎯 Marca desfeita às {t} — toque para marcar de novo · arraste para mover no tempo',
  'tl.actionKey': '{what} às {t} — toque para escolher outra ação · arraste para mover no tempo',
  'tl.graphicKey': 'Setas {value} às {t} — toque para ligar ou desligar · arraste para mover no tempo',
  'tl.on': 'ligadas',
  'tl.off': 'desligadas',
  'tl.detached': 'Desanexado',
  'timing.title': 'Tempo — este ramo · 0–{t}',
  'timing.desc': 'Tudo aqui muda QUANDO as coisas acontecem, nunca ONDE. Arraste a borda da régua para mudar a janela.',
  'timing.slower': '⤢ Mais lento — cada chave ×1,25',
  'timing.quicker': '⤡ Mais rápido — cada chave ×0,8',
  'timing.clip': '✂ Terminar na última chave ({t})',
  'timing.rippleOpen': '↔ Abrir um vão depois de {t} (+0,5 s)',
  'timing.rippleClose': '↔ Fechar o vão depois de {t} (−0,5 s)',
  'act.title': 'Ação — {what}',
  'act.section.actions': 'Ações',
  'act.section.time': 'Tempo',
  'act.section.visual': 'Visual',
  'act.now': '{what} · agora',
  'act.nowSome': '{what} · {n} de {m} agora',
  'act.item': 'Item',
  'form.group': 'Colocar uma formação',
  'form.place': 'Colocar {name} — o time inteiro, a partir do goleiro',
  'form.none': 'Este time ainda não tem jogadores — adicione um na lista abaixo',
  'form.already': 'Já está no {name} — nada para colocar',
  'form.placed': '{name} — {n} colocados',
  'form.placedShort': '{name} — {n} colocados, {empty} vagas livres',
  'act.plain': 'Ação',
  'act.groupMore': ' +{n} mais',
  'act.titleAll': 'Ação — {n} de uma vez',
  'act.titleSome': 'Ação — {n} de {total} (bolas e zonas não têm ações)',
  'kf.deleteActionHere': '✕ Excluir esta ação ({t})',
  'edit.key': 'Chave',
  'edit.stepsMove': 'Os passos movem…',
  'edit.stepsHere': 'Só este momento',
  'edit.stepsAfter': 'Este + tudo depois',
  'edit.deleteKey': 'Excluir a chave',
  'edit.otherItem': 'outro item',
  'edit.landOn': '⇢ Encaixar em {t} — {who}',
  'edit.landOnHint': 'Move este momento para o instante em que {who} registra a chave — os dois chegam juntos',
  'edit.transition': 'Transição',
  'edit.style': 'Estilo',
  'edit.straight': 'Reta',
  'edit.left': 'Esquerda',
  'edit.right': 'Direita',
  'edit.curve': 'Curva',
  'edit.arch': 'Arco',
  'edit.archPeak': 'Pico do arco',
  'edit.curveHint': 'Puxe a alça violeta no campo para curvar para o outro lado',
  'edit.archHint': 'Puxe a alça violeta para subir o loft, ou achatá-lo',
  'edit.archPeakHint': 'Deslize o pico do voo ao longo da trajetória',
  'edit.spotAlongHint': 'Move este ponto 0,5 m ao longo do campo · o passo fino é 0,1 m',
  'edit.spotAcrossHint': 'Move este ponto 0,5 m através do campo · o passo fino é 0,1 m',
  'edit.timeEarlier1': 'Move este momento 1 s para trás',
  'edit.timeEarlierQ': 'Move este momento 0,25 s para trás',
  'edit.timeLaterQ': 'Move este momento 0,25 s para a frente',
  'edit.timeLater1': 'Move este momento 1 s para a frente — a ramificação se ajusta',
  'edit.arrowColour': 'Cor da seta',
  'view.follow': 'Seguir',
  'view.followTitle': 'Seguir um item no campo — toque no item para segui-lo · ⌥N só foca a câmera',
  'view.pickHint': 'Toque no jogador, bola ou item no campo para segui-lo.',
  'vp.loading': 'Carregando cena…',

  // COPIAR / COLAR / ROTA — os verbos de área de transferência e o grupo excluído.
  'side.home': 'mandante',
  'side.away': 'visitante',
  'clip.deletedN': '{n} excluídos — o botão ↩ traz de volta',
  'clip.onlyOne': 'Só há um {what} no campo — segure outro para adicioná-lo ao grupo',
  'clip.whatPlayer': 'jogador {side}',
  'clip.copied': 'Copiado {name}{route} — 📌 Cole no menu de outro {type}',
  'clip.route': ' + a corrida',
  'clip.routeSame': 'A rota já roda igual em {label} — nada mudou',
  'clip.stateSame': '{label} já tem exatamente esse estado — nada mudou',
  'clip.pasted': 'Colado em {label}',

  // ALTURA / PICO / CURVA DA CHAVE — os marcadores de voo.
  'key.heightAt': 'Chave @ {t}: altura {h} m{hint}',
  'key.heightHint': ' — puxe a alça violeta para moldar o voo',
  'key.peakAt': 'Pico a {pct}% do voo · {from}–{to} — ⌥⌃[ ] move · ⌥[ ] a altura',
  'key.bendAt': 'Curva {pct}% · {from}–{to} — C inverte o lado',
  'key.zoneRadius': 'Raio da zona {r} m a partir de {t}',
  'key.arrowDeleted': 'Janela de seta de {t} excluída — o botão ↩ traz de volta',

  // O PASSADOR DE TEMPO DO EDITOR DE CHAVE — recusas explicam o caminho.
  'kf.startZero': 'Um ramo começa em 0 — este momento está a apenas {t} do início',
  'kf.rippleStretched': 'Tudo depois de {t} também se moveu · ramo estendido até {end}',
  'kf.blockedAt': 'Bloqueado — já existe uma chave em {t} · avance além dela',
  'kf.blockedDrag': 'Bloqueado — já existe uma chave em {t} · solte ao lado',
  'kf.movedStretched': 'Momento movido para {t} · ramo estendido para caber',

  // CURVA (C) E ELEVAÇÃO (⇧C) — o que o ciclo acabou de fazer, dito em voz alta.
  'bend.noneGroup': 'Nenhum membro tem segmento de movimento no cursor — toque em ✚ numa Faixa de movimento para marcar um',
  'bend.noneHere': 'Nenhum segmento de movimento no cursor — K marca uma posição primeiro',
  'bend.cycled': 'Curvas alternadas — {n} do grupo{skipped}',
  'bend.skipped': ' ({n} sem segmento aqui)',
  'act.noLaneSkipped': ' ({n} sem chaves de ação)',
  'bend.cycledTo': '{from}–{to}: {name}',
  'loft.on': 'Elevação {m} m · {from}–{to} — puxe a alça violeta',
  'loft.off': 'Passe raso · {from}–{to}',
  'loft.onGroup': '{n} trajetórias agora sobem {m} m{skipped}',
  'loft.offGroup': '{n} trajetórias voltaram ao chão{skipped}',
  'loft.skipped': ' ({n} fora de voo)',
  'loft.noneGroup': 'Nenhuma bola deste grupo tem trajetória no cursor — elevar escreve entre duas chaves de posição',

  // OS VERBOS DE SETA (G / ⇧G). LIGADA/DESLIGADA vão na frase: o adjetivo
  // concorda com o substantivo, e um slot não faz isso.
  'arrow.on': 'Seta ligada em {t} — {name}',
  'arrow.off': 'Seta desligada em {t} — {name}',
  'arrow.groupOn': 'Setas ligadas em {t} — {n}{blocked}',
  'arrow.groupOff': 'Setas desligadas em {t} — {n}{blocked}',
  'arrow.blockedSeam': ' ({n} presas na costura)',
  'arrow.groupColour': 'Setas {c} — {n} do grupo{skipped}',
  'arrow.skippedColour': ' ({n} sem seta para colorir)',
  'arrow.onRed': 'Seta ligada em vermelho a partir de {t} — ⇧G alterna a cor',
  'arrow.colourSet': 'Seta {c} a partir de {t}',
  'color.red': 'vermelha', 'color.orange': 'laranja', 'color.yellow': 'amarela',
  'color.green': 'verde', 'color.blue': 'azul', 'color.purple': 'roxa',
  'color.default': 'cor padrão',

  // ARMAR UMA COLOCAÇÃO — o caminho de toque mais usado do app. "Colocação
  // armada:" em vez de "{name} armado": o adjetivo teria de concordar com um
  // substantivo que só conhecemos em tempo de execução.
  'itm.circleZone': 'Zona circular',
  'itm.dynamicZone': 'Zona dinâmica',
  'place.cancelled': 'Colocação cancelada: {name}',
  'place.armed': 'Colocação armada: {name} — toque no campo · siga tocando para mais · o mesmo botão de novo (ou Esc) cancela',
  'place.armedAgain': 'Colocação rearmada: {name} — toque no campo · siga tocando para mais · o mesmo botão de novo (ou Esc) cancela',
  'bench.disarmed': 'Colocação desarmada: {who} — ⇧{key} arma o próximo do banco',
  'bench.armedClick': 'Colocação armada: {who} — clique no campo · ⇧{key} de novo cancela',
  'bench.noBenchHome': 'Todos os jogadores do mandante já estão em campo — o banco está vazio',
  'bench.noBenchAway': 'Todos os jogadores do visitante já estão em campo — o banco está vazio',
  'bench.playerN': 'Jogador {n}',
  'bench.armedNext': '{who} é o próximo — toque no campo · o mesmo botão de novo (ou Esc) cancela',

  // QUANDO O SERVIDOR DIZ NÃO. O motivo chega nas palavras do servidor, mas a
  // frase em volta pertence ao idioma do treinador.
  'scenario.saveFailed': 'Falha ao salvar: {why}',
  'scenario.saveAsFailed': 'Falha ao salvar como novo: {why}',
  'scenario.renameFailed': 'Falha ao renomear: {why}',
  'scenario.deleteFailed': 'Falha ao excluir: {why}',
  'scenario.deletedNamed': '“{name}” excluído',
  'scenario.shareFailed': 'Falha ao atualizar o compartilhamento: {why}',
  'scenario.checkoutFailed': 'Falha no pagamento: {why}',
  'scenario.checkoutPopupBlocked': 'Permita pop-ups para abrir o pagamento seguro sem sair do campo.',
  'scenario.portalFailed': 'Não foi possível abrir o portal de cobrança: {why}',
  'scenario.portalPopupBlocked': 'Permita pop-ups para gerenciar a cobrança sem sair do campo.',
  'scenario.unknownError': 'erro desconhecido',
  'vis.windowDeleted': 'Janela visual em {t} excluída — T cria uma nova',
  'vis.windowsDeletedN': '{n} janelas visuais excluídas — ✚ na faixa Visual cria uma nova',
  'vis.windowsDeletedOne': '{n} janela visual excluída — ✚ na faixa Visual cria uma nova',
  'vis.lockedAtSeam': ' · {n} presas na costura',
  'vis.noZonesScene': 'Nenhuma zona no campo — uma sobreposição de forma precisa de uma zona',
  'vis.windowsOpenedScene': '{n} formas de zona exibidas em {t}',
  'group.deletedStill': '{name} excluído — {n} continuam em grupo · segure um deles para excluir todos · o botão ↩ desfaz',

  // AS ÚLTIMAS MENSAGENS LITERAIS — os verbos que ainda respondiam em inglês.
  'team.switchTo': '{name} → {side}',
  'vis.windowOpenGrab': 'Janela de visual em {t} — editor aberto · segure a zona de novo — ou clique duas vezes — para pegar o vértice 1',
  'vis.windowOpen': 'Janela de visual em {t} — editor aberto · ✎ Editar Forma mostra os pontos · T de novo pega o vértice 1',
  'vis.windowGroup': 'Janela de visual em {t} para {n} zonas — editor aberto para esta',
  'lane.move': 'chave de movimento', 'lane.arrow': 'chave de seta', 'lane.shape': 'chave de forma',
  'lane.attach': 'chave de vínculo', 'lane.action': 'chave de ação',
  'lane.keyAt': '{lane} em {t} — Enter edita',
  'branch.lastKeyAt': 'A última chave está em {t} — “Cortar tempo” na linha do ramo aparar exatamente até ela',
  'branch.nowDur': 'Ramo agora 0–{t} — {grow}⌥+/− escala as CHAVES · o botão ↩ desfaz',
  'branch.growNote': 'espaço para marcar depois · ',
  'branch.priority': 'Prioridade {dir} — agora #{n} de {m}{choice} · o botão ↩ desfaz',
  'branch.dirUp': 'para cima', 'branch.dirDown': 'para baixo',
  'branch.choiceKept': ' · caminho escolhido inalterado',
  'branch.choiceFollows': ' · o caminho mestre segue a prioridade aqui',
  'branch.edgeDrag': 'Ramo {before} → {now} — arrasto da borda · o botão ↩ devolve o comprimento',
  'attach.nothingAttached': 'Nada segue {name} ainda — a bússola MARK na faixa dele pergunta a quem seguir',
  'attach.detached': 'Vínculo solto em {t} — o botão ↩ traz de volta',
  'prompt.answered': 'Pedido respondido — {type} mais próximo · {d} m de distância',
  'pass.tuneCatch': 'Passe para {name} — arraste o ◆ da Faixa de anexação para mover a recepção',
  'pass.stretchedFor': ' · ramo estendido até {t} para o voo',
  'group.allSelected': 'Todos os {n} {label} selecionados — arraste um e movem-se todos · o menu espelha / duplica o bloco',
  'clip.duplicatedN': '{n} itens duplicados — {names} · em grupo, arraste um e movem-se todos',
  'clip.duplicatedOne': 'Duplicado como {names} — toque em ⧉ Duplicar de novo para pôr outro a {d} m',
  'clip.skippedPlayers': ' · {n} jogador(es) ignorados — usam copiar/colar',

  // AS RECUSAS. Todas elas já apontavam a saída — eram as últimas frases do app
  // que só quem lê inglês conseguia ouvir.
  'branch.onlyNow': 'Só o ramo agora — Space toca o caminho inteiro de novo',
  'act.ballsNoRepeat': 'Bolas e zonas não têm chaves de ação — ↻ Outra vez precisa de um jogador, cone ou alvo',
  'act.ballsNoPick': 'Bolas e zonas não têm chaves de ação — X é para jogadores, cones e alvos',
  'act.noActionYet': 'Nenhuma ação usada ainda — o ✚ da faixa de Ação cria uma',
  'act.againAt': '{action} de novo em {t}',
  'act.againGroupBalls': ' — {n} de {m} (bolas e zonas não têm ações)',
  'act.againGroupAll': ' — todos os {n}',
  'itm.repeat': '↻ Outra vez: {action}',
  'bench.squadComplete': 'Equipe completa — todos os jogadores do elenco estão em campo',
  'kf.noPosKey': 'Nenhuma chave de posição neste momento ou antes — toque num losango da faixa para chegar a uma',
  'kf.noPosKeyMany': 'Nenhum destes tem chave de posição neste momento — toque num losango da faixa para chegar a uma',
  'kf.noSegment': 'Nenhum segmento de movimento sob o cursor — ✚ no menu do item marca uma posição primeiro',
  'kf.noBallSegment': 'Nenhum segmento de bola sob o cursor — ✚ no menu do item marca uma posição primeiro',
  'kf.walkBackNone': 'Nenhuma chave antes deste momento — a próxima está à frente · ✚ no menu do item marca uma aqui',
  'kf.walkFwdNone': 'Nenhuma chave depois deste momento — ✚ no menu do item marca uma aqui',
  'kf.nothingExact': 'Nada exatamente neste momento — toque num losango da faixa para andar até a chave mais próxima',
  'kf.noneYet': 'Ainda sem chaves — ✚ no menu do item marca uma posição primeiro',
  'loft.noLoft': 'Sem elevação neste segmento — ⇧C eleva, depois ⌥⌃[ / ⌥⌃] colocam o pico',
  'loft.alreadyFlat': 'Já está raso — puxe a alça violeta, ou ⤴ Elevar no menu do item',
  'loft.max': 'Elevação máxima 10 m — puxe a alça violeta para baixo',
  'loft.flatNow': 'Passe raso · {from}–{to} — puxe a alça violeta para elevá-lo',
  'vis.noCircleWindow': 'Nenhuma janela de zona circular no cursor — T cria uma',
  'prompt.answerFirst': 'Responda ao pedido primeiro — 1 aceita · 0 cancela',
  'play.normalAlready': 'Já está na velocidade normal — +/− ajustam',
  'play.atOneX': 'Reprodução 1× — velocidade normal',
  'branch.shortest': 'Mínimo 1 s — o passo ▸▸ na linha do ramo devolve folga',
  'branch.longest': 'Máximo 600 s — o passo ◂◂ na linha do ramo apara de volta',
  'vis.forZones': 'Visuais de zona são para zonas — ⇧⌥S seleciona todas as zonas',
  'tree.atRoot': 'Já está na raiz — toque num filho em PRÓXIMOS POSSÍVEIS para entrar',
  'tree.isRoot': 'Este é o ramo raiz — toque num filho em PRÓXIMOS POSSÍVEIS para entrar',
  'tree.noKids': 'Nenhum sub-ramo aqui — pressione B para adicionar um',
  'tree.renaming': 'Renomeando o ramo — digite o novo nome · Enter confirma',
  'tree.rootNoSiblings': 'O ramo raiz não tem irmãos — pressione B para adicionar um sub-ramo',
  'tree.oneBranch': 'Um ramo só — “+ Adicionar sub-ramo” faz a árvore crescer',
  'tree.firstSibling': 'O primeiro ramo desta divisão — ‹ VOLTAR abre a árvore, toque no ramo que quiser',
  'tree.lastSibling': 'O último ramo desta divisão — ‹ VOLTAR abre a árvore, toque no ramo que quiser',
  'tree.onlyOneKid': 'Só 1 ramo abaixo deste — PRÓXIMOS POSSÍVEIS mostra ele',
  'tree.onlyNKids': 'Só {n} ramos abaixo deste — PRÓXIMOS POSSÍVEIS mostra eles',
  'tree.rootNoPriority': 'O ramo raiz não tem prioridade — o caminho sempre começa aqui',
  'tree.alreadyFirst': 'Já é o primeiro desta divisão',
  'tree.alreadyFirstPath': 'Já é o primeiro — a linha de tempo mestra já pega este ramo aqui',
  'tree.alreadyLast': 'Já é o último desta divisão',
  'route.pastedOne': 'Percurso colado em {label}',
  'route.pastedMany': 'Percurso colado em {n} — cada um em seu próprio lugar',
  'app.newHere': 'Começando agora? Toque num item e arraste no campo — ao soltar, o movimento já fica registrado · toque de novo para ver o menu dele. O ícone de arquivo guarda ❓ Ajuda e tutoriais.',
  'app.defaultScenarioTitle': 'Cenário padrão',
  'app.scenarioUnconfigured': 'Nenhum cenário padrão está disponível. Abra um cenário pelo menu de arquivo ou crie um novo.',
  'app.scenarioUnavailable': 'Não foi possível carregar o cenário padrão do site, então o campo está vazio. Abra um cenário pelo menu de arquivo ou verifique se o servidor está ativo.',
  'attach.noPlayer': 'Nenhum jogador em campo para vincular — abra o menu de colocação e escolha um do banco',
  'attach.noBall': 'Nenhuma bola em campo para dar — abra o menu de colocação e coloque uma no campo',
  'select.nonePlayers': 'Sem {who} em campo para selecionar — adicione jogadores no menu JOGADOR',
  'select.noneItems': 'Sem {what} em campo para selecionar — adicione um no menu +',
  'itm.loftOn': '⤴ Elevar {from}–{to}',
  'itm.loftOff': '⤵ Sem elevação {from}–{to}',
  'itm.loftMany': '⤴ Elevar ({n} bolas)',
  'itm.loftSome': '⤴ Elevar {n} que pode',
  'itm.bendCycle': '↝ Curva {from}–{to}: {now}',
  'itm.bendCycleMany': "↝ Curvar todos os {n}",
  'itm.bendCycleSome': '↝ Curvar os {n} que podem',
  'itm.lift': '↑ {t} +0,5 m ({h} m)',
  'itm.lower': '↓ {t} −0,5 m ({h} m)',
  'itm.liftMany': "↑ todos os {n} +0,5 m",
  'itm.lowerMany': "↓ todos os {n} −0,5 m",
  'itm.liftSome': '↑ {n} dos {m} +0,5 m',
  'itm.lowerSome': '↓ {n} dos {m} −0,5 m',
  'itm.manyAt': ' ({h} m)',
  'height.floor': 'Já está no chão — ↑ para subir',
  'height.ceiling': 'O teto é 10 m — ↓ para descer',
  'height.floorMany': "Todos estão no chão — ↑ para subir",
  'height.ceilingMany': "Todos no teto de 10 m — ↓ para descer",
  'height.follows': 'Agora segue outro item — a altura é a do outro · o menu do próprio item tem a opção que o solta',
  'itm.ballHide': '👻 Esconder esta bola',
  'itm.ballShow': '👁 Mostrar esta bola',
  'scenario.nowPrivate': '“{name}” agora é privado',
  'scenario.nowPublic': '“{name}” agora é público',

  // OS BOTÕES DE REPRODUÇÃO do campo, e o modal de opinião.
  'play.speedTitle': '{label} — segure ou clique com o botão direito para a velocidade ({speed}×)',
  'play.speed': 'Velocidade do replay',
  'fb.title': 'Enviar opinião',
  'fb.type': 'Tipo',
  'fb.cat.suggestion': 'Sugestão',
  'fb.cat.bug': 'Reportar erro',
  'fb.cat.question': 'Dúvida',
  'fb.cat.other': 'Outro',
  'fb.message': 'Sua mensagem',
  'fb.placeholder': 'Conte o que você acha — ideias, erros, dúvidas, qualquer coisa...',
  'fb.sent': '✓ Obrigado! Opinião enviada.',
  'fb.failed': '✗ Falha ao enviar. Tente de novo.',
  'fb.close': 'Fechar',
  'fb.send': 'Enviar',
  'fb.sending': 'Enviando...',

  // O NAVEGADOR DE CENÁRIOS PÚBLICOS — um modal inteiro nunca traduzido.
  'pub.titleOpen': 'Explorar cenários abertos',
  'pub.titlePublic': 'Explorar cenários públicos',
  'pub.searchPlaceholder': 'Buscar por nome… (deixe vazio para ver todos)',
  'pub.search': 'Buscar',
  'pub.clear': 'Limpar',
  'pub.loading': 'Carregando…',
  'pub.none': 'Nenhum cenário público encontrado.',
  'pub.load': 'Carregar',
  'pub.showing': 'Mostrando {from}–{to} de {total} cenários',
  'pub.showingOne': 'Mostrando {from}–{to} de 1 cenário',
  'pub.prev': '‹ Anterior',
  'pub.next': 'Próximo ›',
  'pub.loadListFailed': 'Falha ao carregar cenários',
  'pub.loadOneFailed': 'Falha ao carregar o cenário',
  'pub.movementKeys': '{n} chave(s) de movimento',
  'pub.emptyTimeline': 'linha do tempo vazia',
  'zv.title': 'Visuais da zona',
  'zv.close': 'Fechar os visuais da zona',
  'zv.radius': 'Raio — {m} m',
  'zv.editShape': '✎ Editar a forma (adicionar / mover / remover pontos)',
  'zv.closed': 'Fechada',
  'zv.open': 'Aberta',
  'zv.vertexHint': 'Vértices anexados — arraste um vértice até um jogador, ou toque em Link e depois nele',
  'zv.notAttached': 'sem anexar',
  'zv.link': 'Link',
  'zv.linkTitle': 'Anexar V{n} pelo clique: Link e depois clique num jogador ou na bola — ou simplesmente arraste o ponto V{n} até eles',
  'zv.free': 'Liberar V{n} — parar de seguir {who}',
  'zv.opacity': 'Opacidade — {p}%',
  'zv.thickness': 'Espessura — {n}',
  'toast.arrowsNeedItem': 'Selecione um item primeiro — o menu dele tem ➜ Setas ligadas / desligadas',
  'toast.arrowsAlreadyOn': 'Setas já aparecem em {t} — nada mudou',
  'toast.arrowsAlreadyOff': 'Sem setas para esconder em {t} — nada mudou',
  'toast.arrowsOnMany': 'Setas ligadas para {n} em {t}',
  'toast.arrowsOffMany': 'Setas desligadas para {n} em {t}',
  'toast.alreadyThatWay': ' ({n} já estavam assim)',
  'toast.heightUpMany': "{n} itens elevados {m} m",
  'toast.heightUpOne': "{n} item elevado {m} m",
  'toast.heightDownMany': "{n} itens rebaixados {m} m",
  'toast.heightDownOne': "{n} item rebaixado {m} m",
  'toast.heightSkipped': " ({n} não puderam se mover)",
  'toast.arrowsOnOne': 'Setas ligadas — {name} em {t}',
  'toast.arrowsOffOne': 'Setas desligadas — {name} em {t}',
  'toast.undoSuffix': ' · o botão ↩ desfaz',
  'toast.alreadyActionMany': 'Todos os {n} já estão {action} em {t} — nada mudou',
  'toast.alreadyActionOne': 'Já está {action} em {t} — nada mudou',
  'toast.repeatSameInstant': 'Já está {action} em {t} — avance o cursor ou escolha outra ação',
  'toast.attachedTo': 'Anexado a {name} · {d} m de distância · {t} — segure a bola de novo para soltá-la',
  'toast.keyNeedItem': 'Selecione um item primeiro — a faixa dele na timeline tem um ✚ para o que você quer marcar',
  'toast.keyAllFollow': 'Todos os {n} seguem um portador aqui — marcar cria pontos mortos · o ✚ da Faixa de anexação abre “Soltar aqui”',
  'toast.keyAllFollowOne': 'Este está seguindo um portador aqui — marcar cria um ponto morto · o ✚ da Faixa de anexação abre “Soltar aqui”',
  'toast.keyMany': '{n} marcas em {t}',
  'toast.keyFollowSuffix': ' ({n} seguem um portador — o ✚ da Faixa de anexação abre “Soltar aqui”)',
  'toast.keyFollowSuffixOne': ' ({n} segue um portador — o ✚ da Faixa de anexação abre “Soltar aqui”)',
  'toast.keyOne': '{name} marcado em {t}',
  'toast.noPosPlayer': 'Este jogador ainda não tem posição para marcar.',
  'toast.noPosItem': 'Este item ainda não tem posição para marcar.',
  'toast.followsCarrier': '{name} segue {who} aqui — uma chave de posição seria um ponto morto · o ✚ da Faixa de anexação abre “Soltar aqui”',
  'toast.sNeedItem': 'Selecione um item primeiro — S encosta a chave dele no segundo mais próximo que outro item já marca',
  'toast.sNoOwnKey': 'Este item não tem chave de movimento — o ✚ na faixa dele na timeline marca uma no cursor',
  'toast.sNothingElse': 'Nada mais está marcado — S encosta este momento no segundo de OUTRO item · arraste o ◆ da faixa para movê-lo no tempo',
  'toast.momentDeleted': 'Momento em {t} apagado — chegada + posse juntos · o botão ↩ devolve os dois',
  'toast.keyDeleted': 'Chave em {t} apagada · o botão ↩ a devolve',
  'toast.nudgeKey': 'Chave → {t}',
  'toast.nudgeNote': ' — {note}',
  'toast.nudgeMoment': ' · o momento inteiro se moveu junto',
  'toast.nudgeGroup': ' · {n} no grupo',
  'toast.nudgeStepSuffix': ' · os botões ◂ 0.25 / 0.25 ▸ movem',
  'toast.sameSecond': 'no mesmo segundo que {who} · {t}',
  'toast.mirrorWhole': 'Rama inteira espelhada — {n} no total {axis} · “⇄ Espelhar…” faz isso no menu',
  'toast.mirrorOne': '{n} item espelhado {axis}',
  'toast.mirrorMany': '{n} itens espelhados {axis}',
  'toast.mirrorAxisFlank': 'ala ↔ ala',
  'toast.mirrorAxisEnd': 'gol ↔ gol',
  'toast.mirrorFlankHint': ' — ⇄ Espelhar ponta vira do outro jeito',
  'toast.mirrorEndHint': ' — ⇄ Espelhar ala vira do outro jeito',
  'toast.mirrorPlacedHalf': '{label} espelhado para a outra metade — a outra entrada de espelho inverte ao contrário · toque ⇄ Copiar de novo para outro',
  'toast.mirrorPlacedSide': '{label} espelhado para o outro lado — a outra entrada de espelho inverte ao contrário · toque ⇄ Copiar de novo para outro',
  'toast.lineNeedTrail': 'Coloque dois primeiro — eles definem o espaçamento · toque em dois e ⧉ Próximo da fila repete',
  'toast.lineSameSpot': 'Os dois estão no mesmo lugar — coloque o segundo em outro lugar para definir o espaçamento',
  'toast.lineTurned': '{label} virou 90° — ⧉ Próximo da fila segue a nova linha · ⧉ Virar a linha 90° desenha um quadrado',
  'toast.lineStepped': '{label} {m} m à frente — ⧉ Próximo da fila de novo · ⧉ Virar a linha 90° faz uma grade',
  'toast.mirrorNothingPitch': 'Não há nada no campo para espelhar',
  'toast.mirrorNeedSelect': 'Nada selecionado para espelhar — selecione um item, ou abra o menu Ação e use ⇄ Espelhar',
  'toast.dupPlayerNo': 'Jogadores não podem ser duplicados — copie um (📋 no menu dele) e cole em outra vaga.',
  'toast.placeNeedArm': 'Coloque um primeiro — o menu + arma um item, depois toque no campo',
  'toast.placeBallOne': 'Bolas vão uma por vez — arraste uma até um jogador para entregá-la',
  'toast.placedMore': '{type} colocado — toque de novo para mais{hint} · o mesmo botão de novo (ou Esc) cancela',
  'toast.placedHintLine': ' · ⧉ Próximo na fila repete este espaçamento',
  'toast.placedHintMirror': ' · o menu + espelha para a outra metade',
  'kindOne.cone': 'Cone', 'kindOne.target': 'Alvo', 'kindOne.zone': 'Zona', 'kindOne.ball': 'Bola',
  'kindMany.cones': 'cones', 'kindMany.targets': 'alvos', 'kindMany.zones': 'zonas',
  'kindMany.balls': 'bolas', 'kindMany.playersHome': 'jogadores do casa', 'kindMany.playersAway': 'jogadores do visitante',
  'toast.groupLeft': '{name} saiu do grupo',
  'toast.groupJoined': '{name} no grupo',
  'toast.groupMany': ' — {n} no grupo · arraste um para mover todos · segure um para espelhar / duplicar / apagar todos',
  'toast.groupOne': ' · segure de novo — ou clique duas vezes — para suas ações',
  'toast.ballNoGroup': 'Bolas não entram em grupos — grupo de {n} mantido · arraste um membro para mover todos',
  'toast.autoOff': 'AUTO está desligado — esse movimento não vai tocar. O botão AUTO na faixa liga de novo.',
  'toast.landed': '↳ {name}',
  'toast.landedPath': '↳ {name} — {path}',
  'toast.landedKids': ' · toque num filho em PRÓXIMOS POSSÍVEIS',
  'toast.landedAddBranch': ' · “+ Adicionar sub-ramo” faz a árvore crescer',
  'toast.landedUpMany': ' · toque no ramo acima para sair',
  'toast.landedUpOne': ' · toque no ramo acima para voltar',
  'toast.landedWalkIn': ' · toque num filho para entrar',
  'toast.branchRemoved': 'Ramo “{name}” removido — o botão ↩ traz de volta',
  'toast.clipTight': 'Já está justo — última chave em {t} · ✚ no menu do item marca uma depois',
  'toast.clipRestores': ' · o botão ↩ restaura {old}',
  'ripple.movedLaterOne': '1 chave movida para depois de {t}',
  'ripple.movedLater': '{n} chaves movidas para depois de {t}',
  'ripple.movedEarlierOne': '1 chave movida para antes de {t}',
  'ripple.movedEarlier': '{n} chaves movidas para antes de {t}',
  'ripple.stayed': ' · {n} herdadas ficaram',
  'vertex.at': '{x}, {y} m',
  'vertex.shiftFine': ' · ⇧ = 0,1 m fino',
  'select.cycleHint': ' · N próxima · ⇧N anterior',
  'select.cameraFocused': ' · câmera focada nele',
  'select.cameraHint': ' · ⌥N também move a câmera',
  'toast.stretchSlower': 'Ramo mais lento — {old} → {new}',
  'toast.stretchNothing': 'Nada para esticar — todas as chaves estão em 0:00',
  'toast.stretchFloor': 'Já está no piso de 1 s — estique primeiro, ou arraste a borda da régua para aparar a janela',
  'toast.stretchFaster': 'Ramo mais rápido — {old} → {new}',
  'toast.revertSuffix': ' · o botão ↩ reverte',
  'toast.rootNoPriority': 'O ramo raiz não tem prioridade — o caminho sempre começa aqui',
  'toast.alreadyFirstChosen': 'Já é o primeiro neste garfo',
  'toast.alreadyFirstPath': 'Já é o primeiro — o caminho mestre já pega este ramo aqui',
  'toast.alreadyLastFork': 'Já é o último neste garfo',
  'toast.priorityMoved': 'Prioridade {dir} — agora #{rank} de {n}',
  'toast.dirUp': 'para cima', 'toast.dirDown': 'para baixo',
  'toast.chosenUnchanged': ' · caminho escolhido inalterado',
  'toast.pathFollowsPriority': ' · o caminho mestre segue a prioridade aqui',
  'toast.timelineSame': '“{name}” já tem exatamente essa linha de tempo — nada mudou',
  'toast.rootNoSiblings': 'O ramo raiz não tem irmãos — “+ Adicionar sub-ramo” continua do fim dele',
  'toast.noSiblings': 'Sem irmãos aqui — “+ Adicionar sub-ramo” continua deste fim',
  'toast.branchDuplicated': '“{src}” duplicado — editando “{name}” · o botão ↩ remove',
  'toast.needZone': 'Visuais de zona são para zonas — selecione uma zona',
  'toast.needBallLoft': 'Elevação é para a bola — selecione-a primeiro',
  'toast.needBallHide': 'Selecione uma bola primeiro — ⇧V esconde ou mostra (N alterna a visão)',
  'toast.hideOnlyBall': 'Só bolas podem esconder — ⇧V vale para uma bola selecionada · V troca a visão',
  'toast.needBallAttach': 'Selecione uma bola, zona ou jogador primeiro — ⌥A prende ao parceiro mais próximo',
  'toast.needItemSelectAll': 'Selecione um item primeiro — o menu dele tem ⧉ Selecionar todos',
  'toast.needItemHeight': 'Selecione um item primeiro — Ctrl+↑/↓ edita a altura da chave',
  'toast.needItemBend': 'Selecione um item primeiro — Ctrl+[ / Ctrl+] curvam a trajetória',
  'toast.needItemSlide': 'Selecione um item primeiro — Ctrl+←/→ desliza o ponto no campo',
  'toast.needItemRepeat': 'Selecione um jogador ou item primeiro — ↻ Outra vez está no menu do item',
  'toast.ballNoActionX': 'Bolas e zonas não têm chaves de ação — X precisa de um jogador, cone ou alvo no grupo',
  'toast.ballNoActionShift': 'Bolas e zonas não têm chaves de ação — ↻ Outra vez precisa de um jogador, cone ou alvo no grupo',
  'toast.groupOneRemove': 'Só um item no grupo — o botão − da barra remove',
  'toast.noArrowOn': 'Nenhuma seta LIGADA neste grupo nesse momento — G liga primeiro',
  'toast.rootTrunk': 'O ramo raiz não pode ser excluído — é o tronco de onde pendem todos',
  'toast.branchDeleted': 'Ramo excluído — o botão ↩ traz de volta',
  'toast.nothingPlaced': 'Nada colocado ainda — Shift+1…8 primeiro',
  'toast.nothingOnPitch': 'Nada no campo para selecionar — ⇧1-6 arma um item para colocar',
  'toast.alreadyFree': 'Já está livre neste momento — nada para soltar',
  'toast.needPosKey': 'Nada como referência — pressione K para marcar uma posição primeiro',
  'toast.needSelectFly': 'Nada selecionado — toque ou clique num item (ou pressione N) e depois F ou um toque duplo para voar até lá',
  'toast.rippleSeamBlocked': 'Bloqueado pela costura herdada em {t} — nada se moveu',
  'seam.keysReadOnly': 'Toda chave aqui é a que esta ramificação herdou na costura — só leitura · ✚ no menu do item marca uma que você pode mover',
  'seam.windowsReadOnly': 'Toda janela aqui é a que esta ramificação herdou na costura — só leitura · ✚ no menu do item marca uma que você pode mudar',
  'toast.groupSlidAll': 'Todos os {n} deslizaram {d} m — {who} agora em {x}, {z} m',
  'toast.groupSlidOne': '{who} agora em {x}, {z} m',
  'toast.rippleKeyBlocked': 'Bloqueado — uma chave está em {t} · arraste o ◆ da linha do tempo para movê-la',
  'toast.rippleNothingAfter': 'Nada depois de {t} — pare o corte antes de uma chave',
  'toast.nudgeKeyBlocked': 'Bloqueado — já há uma chave em {t} · arraste o ◆ para além dela, ou 1 s ▸▸',
  'toast.noRoomToStep': 'O ramo vai de 0–{t} — não há espaço para esse passo · arraste a borda da régua para abrir espaço',
  "toast.edgeCross": "A borda da janela não pode cruzar a vizinha em {t}",
  'toast.edgeStepped': 'Borda da janela {from} → {to} · arraste a borda, ou ◂ 0.25 / 0.25 ▸',

  'zone.edit': '✎ EDITAR ZONA',
  'zone.exitEditHint': 'Terminar de moldar — + Adicionar, ⇱ Mover e − Remover moldam a zona',
  'zone.add': '+ Adicionar',
  'zone.remove': '− Remover',
  'zone.addHint': 'Toque no campo para adicionar · pressione um canto para movê-lo — solte-o sobre um jogador e a zona o segue',
  'zone.removeHint': 'Toque um canto para removê-lo.',
  'zone.linkBar': 'Toque num jogador ou na bola para vincular este ponto · ✕ cancela (Esc no teclado)',
  'zone.linkCancel': 'Cancelar o vínculo deste ponto',

};

const es = {
  'lang.name': 'Español',
  'lang.flag': '🇪🇸',

  'menu.new': '＋ Nuevo',
  'menu.save': '💾 Guardar',
  'menu.saveAs': '💾 Guardar Como…',
  'menu.load': '📂 Abrir y gestionar escenarios',
  'menu.loadPublic': '🌍 Cargar de Públicos',
  'scenario.manageHint': 'Toca una tarjeta para abrir · ✎ renombrar · 🌍 compartir · 🗑 eliminar',
  'menu.account': 'Cuenta:',
  'menu.upgrade': '⬆ Mejorar',
  'menu.upgradePrice': '⬆ Mejorar — {price}/mes',
  'account.upgradePrice': '⬆ Mejorar a Premium — {price}/mes',
  'menu.logout': '⎋ Salir',
  'menu.login': '🔑 Entrar / Crear cuenta',
  'menu.language': 'Idioma',
  'menu.mobileApps': 'Descarga la app',
  'menu.androidApp': 'App Android',
  'menu.iosApp': 'App iOS',
  'menu.iosComingSoon': 'Próximamente',
  'menu.account.open': '👤 Cuenta…',

  'account.title': 'Cuenta',
  'account.nameTitle': 'Nombre mostrado',
  'account.save': 'Guardar',
  'account.nameDone': 'Nombre actualizado',
  'account.passwordTitle': 'Cambiar contraseña',
  'account.currentPw': 'Contraseña actual',
  'account.newPw': 'Nueva contraseña (mín. 6)',
  'account.confirmPw': 'Confirmar nueva contraseña',
  'account.changePw': 'Cambiar contraseña',
  'account.shortPw': 'La nueva contraseña debe tener al menos 6 caracteres',
  'account.pwMismatch': 'Las nuevas contraseñas no coinciden',
  'account.pwDone': 'Contraseña cambiada',
  'account.subscription': 'Suscripción',
  'account.premiumOn': 'Premium ⭐',
  'account.freePlan': 'Plan gratuito',
  'account.manage': 'Gestionar suscripción',
  'account.upgrade': '⬆ Mejorar a Premium',
  'account.signOut': '⎋ Salir',
  'account.deleteTitle': 'Eliminar cuenta',
  'account.deleteWarn': 'Tu cuenta y tus datos se eliminan definitivamente tras un periodo de gracia de 30 días. Las suscripciones de la tienda deben cancelarse en Apple/Google.',
  'account.deletePw': 'Contraseña actual',
  'account.deleteConfirm': 'Escribe DELETE para confirmar',
  'account.deleteBtn': 'Solicitar eliminación',
  'account.deleteCancel': 'Cancelar',
  'account.deleteDone': 'Eliminación solicitada — programada para el',
  'menu.teams': '👥 Equipos…',
  'teams.title': 'Equipos',
  'teams.new': '+ Nuevo equipo',
  'teams.none': 'Todavía no hay equipos en esta cuenta.',
  'confirm.cancel': 'Cancelar',
  'confirm.deleteScenario': 'Eliminar equipo',
  'confirm.deleteScenarioBody': '“{name}” y todas sus ramas se eliminan definitivamente. Deshacer no alcanza a un archivo guardado.',
  'confirm.deleteTeam': 'Eliminar equipo',
  'confirm.deleteTeamBody': '“{name}” — plantilla, colores, escudo y texturas de uniforme se eliminan definitivamente. Los escenarios que ya creaste mantienen sus posiciones.',
  'confirm.discardTitle': 'Cambios sin guardar',
  'confirm.discardBody': '¿Salir de este equipo y perder los cambios que no guardaste?',
  'confirm.discard': 'Descartar cambios',
  'name.saveCopy': 'Guardar copia',
  'name.saveAsHint': 'El original queda intacto — esto se convierte en un nuevo equipo en tu cuenta.',
  'name.renameScenario': 'Renombrar equipo',
  'name.renameHint': 'El nombre que ves en Cargar equipo y en el menú Archivo.',
  'scenario.rename': 'Renombrar',
  'scenario.makePublic': 'Hacer público',
  'scenario.makePrivate': 'Hacer privado',
  'scenario.loadTitle': 'Cargar equipo',
  'scenario.refresh': 'Actualizar',
  'scenario.refreshHint': 'Volver a leer los equipos guardados en tu cuenta',
  'scenario.loading': 'Cargando…',
  'scenario.noneYet': 'Todavía no hay nada guardado en esta cuenta.',
  'scenario.newOne': 'Nuevo equipo',
  'scenario.publicBadge': 'público',
  'login.signIn': 'Iniciar sesión',
  'login.register': 'Crear cuenta',
  'login.email': 'Correo',
  'login.password': 'Contraseña',
  'login.newPassword': 'Nueva contraseña',
  'login.orgName': 'Cuenta / Nombre de la organización',
  'login.orgPlaceholder': 'p. ej. Barcelona FC',
  'login.forgot': '¿Olvidaste la contraseña?',
  'login.forgotTitle': 'Olvidé la contraseña',
  'login.forgotBody': 'Escribe tu correo y te enviamos un enlace para restablecerla.',
  'login.sendLink': 'Enviar enlace',
  'login.sending': 'Enviando…',
  'login.back': 'Volver a iniciar sesión',
  'login.resetTitle': 'Restablecer contraseña',
  'login.resetBody': 'Escribe tu nueva contraseña abajo.',
  'login.reset': 'Restablecer contraseña',
  'login.resetting': 'Restableciendo…',
  'login.create': 'Crear cuenta',
  'login.creating': 'Creando…',
  'login.signingIn': 'Iniciando sesión…',
  'login.emailRequired': 'El correo es obligatorio',
  'login.nameRequired': 'El nombre es obligatorio',
  'login.pwRequired': 'La nueva contraseña es obligatoria',
  'login.pwShort': 'La contraseña debe tener al menos 6 caracteres',
  'login.resetSent': 'Si existe una cuenta para ese correo, se ha enviado un enlace.',
  'login.guides': 'Leer las guías',
  'login.close': 'Cerrar',
  'info.ok': 'Aceptar',
  'info.continue': 'Continuar',
  'info.adFree': 'Sin anuncios con Premium',
  'teams.playersCount': '{n} jugadores',
  'teams.shared': 'compartido',
  'teams.selectHome': 'Elegir equipo local',
  'teams.selectAway': 'Elegir equipo visitante',
  'teams.delete': 'Eliminar equipo',
  'teams.save': '💾 Guardar equipo',
  'teams.unsaved': 'cambios sin guardar',
  'teams.name': 'Nombre del equipo',
  'teams.shirt': 'Camiseta',
  'teams.shorts': 'Pantalón',
  'teams.socks': 'Calcetas',
  'teams.badge': 'Escudo',
  'teams.textures': 'Texturas del uniforme (camisa/pantalón/calcetas personalizados)',
  'teams.logo': 'Subir escudo',
  'teams.player': 'Nombre del jugador',
  'teams.number': 'Número',
  'teams.position': 'Posición',
  'teams.skin': 'Piel / cara',
  'teams.hair': 'Pelo',
  'teams.applyHome': 'Aplicar a Local',
  'teams.applyAway': 'Aplicar a Visitante',
  'teams.saved': 'Equipo guardado',
  'teams.hint': 'Elige un equipo para editar. Aplícalo a un lado para usarlo en la cancha.',
  'teams.numberColor': 'Color del número',
  'teams.numberColorHint': 'El número en la espalda de la camiseta — los colores del uniforme salen de las texturas.',
  'teams.templates': 'Plantillas del uniforme',
  'teams.templatesHint': 'Descarga el diseño de impresión, crea sobre él y súbelo de vuelta como la textura correspondiente.',
  'teams.download': '↓ {name}',
  'teams.customTex': 'personalizada',
  'teams.defaultTex': 'predeterminada',
  'teams.addPlayer': '+ Añadir jugador',
  'teams.removePlayer': 'Quitar este jugador',
  'teams.squadCount': '{n} de {max}',
  'teams.squadFull': 'Plantilla completa — {max} jugadores es el máximo',
  'teams.playerAdded': '{name} añadido — {n} en la plantilla',
  'teams.playerRemoved': '{name} quitado — {n} quedan · el botón ↩ deshace',
  'teams.squadMin': 'La plantilla necesita al menos un jugador',
  'look.title': 'Editar aspecto',
  'look.loading': 'Cargando la figura…',
  'look.hint': 'Elige piel y peinado — la figura lleva el uniforme de este equipo.',
  'look.edit': '✎ Editar aspecto',
  'look.editAria': 'Editar el aspecto de {name}',
  'look.current': 'Ahora: {skin} · {hair}',
  'look.apply': 'Aplicar',
  'look.cancel': 'Cancelar',
  'look.none': 'Sin estilos cargados',
  'look.dragRotate': 'Arrastra la figura para girarla',
  'look.noPreview': 'Vista previa no disponible — los estilos se aplican igual.',
  'look.swatchAria': 'Elegir {label}',
  'look.needSave': 'Aplicado — pulsa Guardar para conservarlo',
  'menu.help': '❓ Ayuda y tutoriales',
  'help.title': 'Ayuda y tutoriales',
  'help.scSpace': 'reproduce el camino elegido / pausa',
  'help.scPlayTo': 'reproducir hasta la próxima rama',
  'help.scBranchPlay': 'reproduce / pausa solo la rama actual',
  'help.scCam': 'cámara: ángulo · cenital · baja',
  'help.scPlace': 'arma cualquier objeto colocable',
  'help.scLineRepeat': 'coloca otro en el mismo paso (tras dos clics)',
  'help.scTurnPlace': 'gira la fila 90° con el mismo espacio',
  'help.tFollow': 'arrastra un jugador SOBRE un RIVAL — lo marca: toca el campo donde deben situarse, o elige un puesto y la distancia',
  'help.tGroupMark': 'arrastra un GRUPO SOBRE un RIVAL — todos lo marcan, cada uno desde donde está · o abre el menú del grupo: 🎯 Marcar a un rival… · 📏 un dial fija todas las distancias',
  'help.tRide': 'arrastra un cono, objetivo, zona o balón SOBRE un jugador — pasa a seguirlo; suéltalo en el campo para desvincular',
  'help.tHandles': 'toca una transición de movimiento en la línea de tiempo para ajustar su curva o elevación',
  'help.scMirrorPlace': 'refleja el último colocado en la otra mitad',
  'help.scMirrorPlaceZ': 'refleja al otro lado',
  'help.scPlacePlayer': 'arma el próximo suplente (local / visitante)',
  'help.scReArm': 'rearma el último objeto colocado',
  'help.scCycle': 'recorre la selección (⌥: la cámara salta al item)',
  'help.scgPlayback': 'Reproducción',
  'help.scgPlacement': 'Colocar',
  'help.scgTiming': 'Tiempo',
  'help.scgItems': 'Objetos',
  'help.scgBranches': 'Ramas',
  'help.scgEditing': 'Edición',
  'help.scFocus': 'cámara a la selección',
  'help.scView': 'panel línea de tiempo ⇄ ramas',
  'help.scHide': 'oculta paneles — cancha completa',
  'help.scHideBall': 'oculta / muestra el balón seleccionado',
  'help.scArrows': 'mover el cursor (Shift = 1 s)',
  'help.scJump': 'saltar a la clave anterior / siguiente',
  'help.scNudge': 'desplazar la clave más cercana en el tiempo',
  'help.guides': 'guías',
  'help.guideLoading': 'Cargando la guía…',
  'help.guideFallback': 'No se pudo cargar la guía aquí — mostrando el sitio completo de documentación.',
  'help.guideOpenSite': 'sitio completo ↗',
  'help.guideOpenSiteHint': 'Abrir el sitio de documentación en el navegador — incluye los vídeos paso a paso',
  'help.videos': 'vídeos',
  'help.videosHint': 'Vídeos cortos paso a paso — alojados en YouTube',
  'help.videoNote': 'Vídeos cortos paso a paso, alojados en YouTube. Solo se cargan cuando pulsas play.',
  'help.videoPlay': 'Reproducir {title}',
  'help.videoWatch': 'en YouTube ↗',
  'help.scLandSame': 'encaja esta clave en el segundo más cercano de otro item · o ⇢ Encajar en… dentro del editor de la clave',
  'help.scHeight': 'altura de clave ±0,5 m (⇧: 0,1)',
  'help.scKey': 'registrar la posición actual en el cursor',
  'help.scEnterEdit': 'editar keyframe/segmento en el cursor',
  'help.coreLoop': 'El ciclo: selecciona un item (N cicla) · , . recorre las claves · Enter edita lo que hay bajo el cursor · Esc sale',
  'help.scAction': 'selector de acción para la selección',
  'help.scRepeatAction': 'aplica la acción que acabas de usar (un grupo = cada miembro)',
  'help.scAuto': 'alterna grabación AUTO de keys',
  'help.scAttach': 'pelota/zona/jugador ↔ pareja más cercano (⇧: suelta)',
  'help.scShot': 'captura — comparte o guarda la vista actual',
  'help.scRec': 'grabar vídeo — ⇧P de nuevo para detener',
  'help.scSeg': 'curva el segmento en el cursor',
  'help.scMirror': 'refleja la selección banda a banda (grupo · M y luego ⇧M gira 180°)',
  'help.scMirrorEnd': 'refleja extremo a extremo, sobre la línea central',
  'help.scMirrorAll': 'refleja CADA objeto de la rama, banda a banda',
  'help.scMirrorAllEnd': 'refleja todos los objetos extremo a extremo, sobre la línea central',
  'help.scSelectKind': 'selecciona todos los objetos del mismo tipo (todos los conos, un equipo)',
  'help.scBendAmount': 'curvatura ±0,1 (⇧: 0,02)',
  'help.scLoft': 'alterna el perfil del balón (arco)',
  'help.scLoftDial': 'altura del perfil ±0,5 m',
  'help.scPeakDial': 'mueve el PICO del arco por el trayecto (⇧ = fino)',
  'help.scBend': 'arrastra la bola naranja para curvar',
  'help.scArch': 'arrastra la violeta para elevar',
  'help.scArrow': 'alterna la flecha de movimiento',
  'help.scArrowColor': 'cambia el color de la flecha',
  'help.scZone': 'ventana visual de zona · T de nuevo agarra el vértice 1 (un grupo = todas las zonas)',
  'help.scRadius': 'radio de zona ±0,5 m · cualquier item: desliza su punto ±0,5 m (⇧: 0,1)',
  'help.scVertexNudge': 'mueve vértice agarrado 0,25 m',
  'help.scVertexAdd': 'añade un vértice en el borde agarrado (Del lo elimina)',
  'help.scZoneDel': 'elimina la ventana de zona en el cursor (un grupo = todas las zonas)',
  'help.scBranch': 'añade sub-rama a la rama actual',
  'help.scRename': 'renombra la rama actual (enfoca el nombre)',
  'help.scClip': 'recorta la rama al último keyframe',
  'help.scSibling': 'añade rama hermana',
  'help.scEnter': 'abre el timeline de la rama actual',
  'help.scDelBranch': 'elimina la rama actual (raíz protegida)',
  'help.scDupBranch': 'duplica rama + timeline',
  'help.scPick': 'abre la N-ésima sub-rama (⌥↑ padre)',
  'help.scSibs': 'va a la rama hermana anterior / siguiente',
  'help.scWalk': 'va a la rama padre / primera hija',
  'help.scPriority': 'sube / baja prioridad de la rama',
  'help.scHelp': 'abre ayuda y atajos',
  'help.scEsc': 'cierra prompt · editor · grupo · selección',
  'help.scTap': 'doble toque en un item: la cámara vuela hasta él · doble toque en el césped: de vuelta a toda la jugada · en la barra maestra: reproduce desde ahí',
  'help.scParent': 'salta a la rama padre',
  'help.scGroup': 'agrupa jugadores y objetos — arrastra uno, se mueven todos',
  'help.scgTouch': 'Táctil (móvil / tableta)',
  'help.tTap': 'selecciona · arrastra para moverlo (AUTO graba la key)',
  'help.tLane': 'toca una clave, o la barra debajo: el editor de esa clave / transición — el lado de la clave: Altura · ◂ ▸ ▴ ▾ mueven el punto 0,5 m · ◂◂ 1 s a 1 s ▸▸ mueven el tiempo · ⇢ Encajar en el instante de otro item · Los pasos mueven… (solo este momento, o este y todo lo de después) · Borrar la clave — el lado de la transición: Estilo Recta / Izquierda / Derecha · Curva · en el vuelo del balón también Arco y Punto máximo del arco',
  'help.tHold': 'mantén un item no seleccionado: lo añade al grupo',
  'help.tMasterHold': 'la barra maestra: mantén el dedo PULSADO sobre un tramo para reproducir solo esa rama',
  'help.tMenu': 'toca de nuevo un item seleccionado: su menú — solo los verbos que ESTE item puede hacer: ✚ registrar aquí · ➜ flechas · ↝ curvar · ⤴ elevar · ↑↓ altura · 🎯 marcar · ✕ dejar de marcar · 📏 ajustar las distancias · 📎 vincular · 👻 ocultar el balón · ↻ otra vez · ⧉ Seleccionar un grupo… · ⧉ Seleccionar esta línea · ⧉ Seleccionar todos de este tipo · ⧉ Siguiente en la fila · ⧉ Girar la fila · ⇄ reflejar (uno, el lado, todo el campo) · ✕ borrar esta acción · 📋 copiar · 📌 pegar · ⧉ duplicar · 🗑 borrar todos (o mantenlo)',
  'help.tStay': 'la tarjeta sigue abierta tras ✚ registrar aquí, ↻ otra vez, ⇄ reflejar y registrar una acción — mueve la regla y toca de nuevo; la ✚ dice el nuevo instante',
  'help.tBall': 'mantén la pelota: selecciona · mantén de nuevo para su menú — 📎 vincular es una de las opciones',
  'help.tZone': 'mantén una zona: su editor de forma · arrastra una esquina para moverla · mantén de nuevo para tomar el vértice 1 · + Añadir una esquina (toca el campo), − Quitar una (toca una esquina) · toca un jugador o el balón para vincular ese punto',
  'help.tRuler': 'arrastra la regla para moverte en el tiempo — se detiene en una clave cuando te acercas · mantenla: el tiempo de esta rama — más lento · más rápido · ↔ abrir o cerrar un hueco tras el cursor · terminar en la última clave',
  'help.tKeyDel': 'mantén una clave en la pista: elimínala — el gemelo del doble clic, para el pulgar',
  'help.tKeyMove': 'arrastra una clave por su pista: cambia de instante — y se alinea con otra clave cuando se acerca',
  'help.tUndo': 'el ↩ arriba del campo deshace el último cambio · el ↩ dentro de un mensaje deshace solo ese · ↪ lo devuelve',
  'help.tKeyAdd': 'mantén un tramo vacío de una pista: la clave de esa pista, escrita en el instante que tocaste',
  'help.tRename': 'mantén el nombre de la rama en la cabecera de la pista: renómbralo',
  'help.tPitch': 'mantén un punto vacío del campo: la vista — solo lo que ESTA escena tiene: 🎥 desde arriba · desde la banda · predeterminada · ➜ flechas para todos los items · ◼ formas de todas las zonas · 🎯 detener todas las marcas · 📏 todas las distancias · ⤴⤵ todos los vuelos arriba o abajo',
  'help.tBranch': 'el árbol de ramas a la izquierda: toca una rama para seleccionarla · tócala de nuevo — o ✎ — para abrir su línea de tiempo · ⧉ duplicar una rama — línea de tiempo, nombre y lugar, abierta al instante · ⧉ copiar la línea de tiempo de esta rama · ⎘ pegarla en otra rama · + una sub-rama · + una hermana · ✂ recortar · ▲▼ orden · ✕ quitar — la PRIMERA rama no tiene hermana ni se puede quitar',
  'help.tEdge': 'arrastra el borde derecho de la regla: estira o recorta la ventana',
  'help.tSpeed': 'toca la ficha × del cursor: cambia la velocidad de reproducción',
  'help.tPlace': 'mantén la cancha armada: coloca una fila de items',
  'help.tLineRepeat': 'coloca otro en el mismo paso — vuelve a tocar para recorrer la fila, la tarjeta sigue abierta',
  'help.tGroup': 'la tarjeta puede reunir la LÍNEA de este jugador, o todo el equipo — y entonces ↑ · ↝ · 🗑 actúan sobre todos a la vez',
  'help.tAuto': 'el interruptor en la cabecera de la franja: activado, mover cualquier cosa registra una clave de posición en el segundo actual · desactivado, mueves las cosas sin dejar claves',
  'help.tToolbar': 'el + de items: arma un cono, objetivo, balón o zona y toca el campo para colocarlo · una ✓ marca el tipo ya armado — tócalo otra vez para cancelar · el + de un equipo: cambia de equipo, elige una formación o arma un jugador suplente · el − quita el item seleccionado',
  'help.scSpeed': 'velocidad de reproducción',
  'help.scSpeedReset': 'velocidad vuelve a 1×',
  'help.scStretch': 'estira / comprime el tiempo de la rama',
  'help.scPadBranch': 'alarga / recorta la rama (las claves no se mueven)',
  'help.scRipple': 'desliza todas las claves después del cursor (⌥arrastra · ⌥⇧ = 1 s)',
  'help.scCopy': 'copiar / pegar (ruta + estado)',
  'help.scSaveOpen': 'guardar escenario / abrir navegador',
  'help.scNew': 'nuevo escenario (Ctrl+Z deshace)',
  'help.scBranchCopy': 'copia / pega el tiempo de la rama',
  'help.scDup': 'duplicar objeto',
  'help.scDelete': 'eliminar selección',
  'help.scDelGroup': 'elimina todo el grupo',
  'help.scUndo': 'deshacer / rehacer',

  'add.ball': '⚽ Balón',
  'add.cone': '🟠 Cono',
  'add.target': '🎯 Objetivo',
  'add.polyZone': '▱ Zona Poligonal',
  'add.circleZone': '⭕ Zona Circular',
  'add.dynamicZone': '◈ Zona Dinámica',
  'add.duplicate': '⧉ Duplicar seleccionado',
  'tb.file': 'Archivo',
  'tb.addHome': 'Añadir un jugador local',
  'tb.addAway': 'Añadir un jugador visitante',
  'tb.placingHome': 'Colocando un jugador local — toca el campo · toca otra vez el nombre para cancelar',
  'tb.placingAway': 'Colocando un jugador visitante — toca el campo · toca otra vez el nombre para cancelar',
  'tb.addItem': 'Añadir un item',
  'tb.armedItem': '{what} armado — toca el campo · toca otra vez para cancelar',
  'tb.removeItem': 'Quitar el item seleccionado',
  'tb.removeItemFirst': 'Quitar el item seleccionado — selecciona algo antes',
  'tb.homeLogo': 'Escudo del equipo local',
  'tb.awayLogo': 'Escudo del equipo visitante',
  'tb.publicOffline': 'Conéctate a internet para ver escenarios públicos',
  'tb.duplicateHint': 'Duplica el item seleccionado, desplazado — toca otra vez para otro',
  'tb.duplicatePlayer': 'Los jugadores van en fichas fijas de la plantilla — copia uno y pégalo en otra ficha',
  'tb.duplicateNeedItem': 'Duplicar — selecciona antes un cono, objetivo, zona o balón',
  // Los cuatro verbos de alineación salieron de la barra y ahora viven en el
  // menú de acción del propio item (itm.nextLineStep / itm.turnLine /
  // itm.mirrorHalf / itm.mirrorSide), donde la distancia se ve escrita y el
  // objetivo no puede ser ambiguo.

  'modal.welcome.title': 'Bienvenido a KickViz',
  'modal.welcome.msg': "¡Bienvenido! Toda cuenta — incluida la Gratis — tiene acceso a TODAS las funciones: equipos, guardar y cargar escenarios, escenarios públicos, compartir y más.\n\nPara mantener el servicio, las cuentas Gratis ven un anuncio breve al guardar o cargar un escenario.\n\nPremium elimina todos los anuncios — mejora cuando quieras desde el menú File.",
  'modal.gotIt': 'Entendido',
  'modal.ad.saveMsg': '¡Gracias por usar KickViz! Aquí tienes un anuncio breve — tu escenario se guardará justo después.',
  'modal.ad.loadMsg': '¡Gracias por usar KickViz! Aquí tienes un anuncio breve — luego podrás cargar tu escenario.',
  'modal.ad.loadPublicMsg': '¡Gracias por usar KickViz! Aquí tienes un anuncio breve — luego podrás explorar los escenarios públicos.',
  'prompt.saveAsCopy': 'Nombre para la copia del nuevo escenario:',
  'view.2d': '2D',
  'view.3d': '3D',
  'view.orbit': 'Órbita',
  'view.pan': 'Mover',
  'view.zoomTo': 'Acercar al objeto seleccionado — tecla: F · doble toque en touch',
  'view.names': 'Nombres',
  'view.namesTitle': 'Mostrar el nombre sobre cada jugador',
  'branch.from': 'VIENE DE',
  'branch.current': 'ACTUAL · HERMANAS',
  'branch.possible': 'PRÓXIMAS POSIBLES',
  'branch.rootNothingBefore': 'Raíz — nada antes',
  'branch.nothingBefore': 'Nada antes de esta rama',
  'branch.addSub': 'Añadir sub-rama',
  'branch.addSubHint': 'Continúa desde el final de esta rama — tecla: B',
  'branch.addSibling': 'Añadir rama hermana',
  'branch.addSiblingHint': 'Crear una nueva rama que continúa desde el mismo punto que esta',
  'branch.duplicate': 'Duplicar esta rama',
  'branch.duplicateHint': 'Una copia de esta rama —su línea de tiempo, su nombre y su lugar— abierta y seleccionada para que la edites al instante',
  'branch.copy': 'Copiar la línea de tiempo de esta rama',
  'branch.paste': 'Pegar la línea de tiempo copiada en esta rama',
  'branch.pasteEmpty': 'Copia primero la línea de tiempo de una rama — botón ⧉ en su fila',
  'branch.pasteSame': 'Esta es la rama que copiaste — elige otra para pegar',
  'branch.copiedToast': 'Línea de tiempo copiada de',
  'branch.pastedToast': 'Línea de tiempo pegada en “{name}” — sub-ramas re-derivadas de su nuevo final · el botón ↩ deshace',
  'branch.edit': 'Abrir la timeline de esta rama',
  'branch.up': 'Subir prioridad',
  'branch.down': 'Bajar prioridad',
  'branch.remove': 'Eliminar esta rama',
  'branch.masterPlay': 'Reproducir todo',
  'branch.playToNext': 'Reproducir hasta la siguiente rama',
  'branch.order': 'Prioridad',
  'branch.orderHint': 'Prioridad entre hermanas — 1 es la continuación por defecto',
  'branch.name': 'Nombre de la rama',
  'branch.defaultTag': 'POR DEFECTO',
  'branch.siblingNote': 'El orden 1 es la continuación por defecto al llegar a esta división.',
  'branch.noChildren': 'Aún no hay sub-ramas — añade una para dividir la historia al final de esta timeline',
  'branch.back': 'VOLVER',
  'branch.backTitle': 'Volver al árbol del escenario',
  'branch.lockedKey': 'Heredado de la rama anterior — solo lectura',
  'branch.lockedKeyToast': 'Esta rama empieza donde terminó la anterior — su primera clave es solo lectura. Arrastra la regla (o pulsa →) para salir de las 00:00 primero.',
  'branch.lockedDragToast': 'Esta rama empieza donde terminó la anterior — saca el cursor de las 00:00 y arrastra',
  'branch.addedToast': 'Sub-rama añadida: “{name}” — ya la estás editando · “Añadir sub-rama” crea otra bajo “{parent}”',
  'branch.siblingAddedToast': 'Rama hermana añadida: “{name}” — ya la estás editando · “+ Añadir rama hermana” crea otra',
  'branch.samePoint': 'el mismo punto',
  'branch.rowTitle': 'Selecciona · un doble clic, o un segundo toque, abre su línea de tiempo',
  'branch.chosen': 'Tu elección en esta división — forma parte del camino que reproduce la línea de tiempo maestra',
  'branch.subBranches': 'sub-ramas',
  'branch.inTimeline': 'Abierto en la línea de tiempo',
  'branch.resize': 'Arrastra para redimensionar el panel del árbol del escenario',
  'branch.clipTime': 'Recortar tiempo — ajusta la duración de esta línea de tiempo a su última clave',
  'branch.clippedTo': 'Línea de tiempo recortada a',
  'branch.clipNothing': 'Esta rama aún no tiene claves para recortar',
  'branch.masterPlaying': 'Reproducir todo',
  'master.title': 'Línea de tiempo maestra — todo el escenario',
  'master.hint': 'Un segmento por rama. Arrastra el marcador para recorrer todo el escenario: mueve el árbol y normaliza cada rama según su propia duración (sin tiempos). Doble toque o doble clic en un segmento para reproducir la ruta desde ahí — mantén ⇧ al hacer doble clic para reproducir solo esa rama (en pantalla táctil, mantén el dedo pulsado sobre el segmento).',
  'master.defaultPick': 'El predeterminado (orden 1) — aún no has elegido un camino aquí · abre el árbol para elegir',
  'master.yourPick': 'Tu elección en esta bifurcación — el camino maestro la sigue · las elecciones solo valen en esta sesión',
  'ball.title': 'Balón',
  'ball.colour': 'Color',
  'ball.visible': 'Visible',
  'ball.close': 'Cerrar opciones del balón',
  'ball.hiddenHint': 'Oculto — no se dibuja ni se puede tocar · marque “Visible” arriba para devolverlo',
  'ball.attachHint': '📎 Vincular a… está en el menú del balón · arrastra el balón sobre un jugador para pasar',

 // What the ↩ button and the undo toast name the change they are about to put
 // back. These are DICTIONARY KEYS, not English words: the label travels on the
 // undo stack and is read out in whatever language the coach is using, so a
 // Portuguese screen can never say "Desfazer: position key". It also means
 // switching language mid-session cannot split one gesture into two undo steps,
 // because the stack compares keys, not display text.
  'undoVerb.edit': 'cambio',
  'undoVerb.positionKey': 'clave de posición',
  'undoVerb.attachKey': 'clave de anexión',
  'undoVerb.visualKey': 'clave de visuales',
  'undoVerb.actionKey': 'clave de acción',
  'undoVerb.keyHeight': 'altura de la clave',
  'undoVerb.keyPosition': 'posición de la clave',
  'undoVerb.arrowKey': 'clave de flecha',
  'undoVerb.transition': 'transición',
  'undoVerb.arrowColour': 'color de la flecha',
  'undoVerb.zoneShape': 'forma de la zona',
  'undoVerb.addBranch': 'añadir rama',
  'undoVerb.deleteBranch': 'eliminar rama',
  'undoVerb.clipBranchTime': 'recortar tiempo de la rama',
  'undoVerb.renameBranch': 'renombrar rama',
  'undoVerb.branchOrder': 'orden de las ramas',
  'undoVerb.branchPriority': 'prioridad de la rama',
  'undoVerb.pasteBranchTimeline': 'pegar la línea de tiempo de la rama',
  'undoVerb.addSiblingBranch': 'añadir rama hermana',
  'undoVerb.duplicateBranch': 'duplicar rama',
  'undoVerb.branchDuration': 'duración de la rama',
  'undoVerb.placeFormation': 'colocar formación',
  'undoVerb.itemHeight': 'altura del item',
  'undoVerb.zoneColour': 'color de la zona',
  'undoVerb.ballProperties': 'propiedades de la pelota',
  'undoVerb.zoneOpacity': 'opacidad de la zona',
  'undoVerb.zoneMode': 'modo de la zona',
  'undoVerb.zoneRadius': 'radio de la zona',
  'undoVerb.zoneOpenClosed': 'zona abierta/cerrada',
  'undoVerb.zoneThickness': 'grosor de la zona',
  'undoVerb.zoneVertices': 'vértices de la zona',
  'undoVerb.zoneVertexLink': 'vínculo de vértice de la zona',
  'undoVerb.pasteRoute': 'pegar la ruta',
  'undoVerb.pasteItem': 'pegar item',
  'undoVerb.newScenario': 'nuevo escenario',
  'undoVerb.deleteKey': 'eliminar clave',
  'undoVerb.deleteMoment': 'eliminar momento',
  'undoVerb.archPeak': 'pico del arco',
  'undoVerb.attachBall': 'vincular pelota',
  'undoVerb.attachZone': 'vincular zona',
  'undoVerb.attachObj': 'vincular objeto',
  'undoVerb.attachGroup': 'vincular grupo',
  'undoVerb.bendAmount': 'curvatura',
  'undoVerb.bendPath': 'curvar recorrido',
  'undoVerb.deleteGroup': 'eliminar grupo',
  'undoVerb.deleteZoneWindow': 'eliminar ventana de la zona',
  'undoVerb.deleteZoneWindows': 'eliminar ventanas de la zona',
  'undoVerb.zoneVisualScene': 'formas de la zona',
  'undoVerb.detach': 'desvincular',
  'undoVerb.duplicateGroup': 'duplicar grupo',
  'undoVerb.duplicateItem': 'duplicar item',
  'undoVerb.groupArrowColour': 'color de las flechas del grupo',
  'undoVerb.groupArrows': 'flechas del grupo',
  'undoVerb.groupHeight': "altura del grupo",
  'undoVerb.groupBendPath': 'curvar recorrido del grupo',
  'undoVerb.groupPositionKeys': 'claves de posición del grupo',
  'undoVerb.loftHeight': 'altura del envío',
  'undoVerb.loftToggle': 'alternar envío',
  'undoVerb.loftGroup': 'elevar grupo',
  'undoVerb.markPlayer': 'marcar jugador',
  'undoVerb.markGroupGap': 'distancias de los marcadores',
  'undoVerb.markGroup': 'marcar grupo',
  'undoVerb.unmarkGroup': 'desmarcar grupo',
  'undoVerb.mirrorEnd': 'reflejar extremo',
  'undoVerb.mirrorFlank': 'reflejar banda',
  'undoVerb.moveKeyInTime': 'mover clave en el tiempo',
  'undoVerb.pass': 'pase',
  'undoVerb.rippleKeys': 'claves en cascada',
  'undoVerb.setAction': 'definir acción',
  'undoVerb.setActions': 'definir acciones',
  'undoVerb.stretchTimeline': 'estirar la línea de tiempo',
  'undoVerb.zoneVertexAdd': 'añadir vértice de la zona',
  'undoVerb.zoneVertexRemove': 'quitar vértice de la zona',
  'undo.title': 'Deshacer el último cambio',
  'vp.undoWhat': 'Deshacer: {what}',
  'vp.undoHintOne': '{what} · 1 paso atrás{limit}',
  'vp.undoHintMany': '{what} · {n} pasos atrás{limit}',
  'vp.undoLimit': ' (el límite — los cambios anteriores se perdieron)',
  'vp.redoWhat': 'Rehacer: {what}',
  'vp.redoHintOne': '{what} · 1 paso por delante',
  'vp.redoHintMany': '{what} · {n} pasos por delante',
  'redo.title': 'Rehacer lo que deshizo',

  'tl.timeline': 'LÍNEA DE TIEMPO',
  'tl.pause': 'Pausar',
  'tl.durationHint': 'Duración de la línea de tiempo en segundos — haz clic para editar',
  'tl.hint': 'Desplázate o haz clic en la regla para moverte en el tiempo · arrastra el borde para redimensionar',
  'tl.keyMovement': 'Registrar movimiento — fija dónde está este item en el cursor',
  'tl.keyMovementPlayer': 'Registrar movimiento — fija dónde está este jugador en el cursor',
  'tl.keyAction': 'Registrar acción — añade una acción en el cursor',
  'cap.button': 'Captura',
  'cap.hint': 'Captura una imagen o graba el campo en vídeo',
  'cap.screenshot': 'Captura de pantalla',
  'cap.record': 'Grabar vídeo',
  'cap.stop': 'Parar grabación',
  'cap.saving': 'Guardando…',
  'cap.recordingBody': 'Grabando el campo',
  'cap.recordingToast': 'Grabando — toca ● el botón de cámara otra vez para parar',
  'seg.currentColour': 'Color actual — tócalo para cambiar',
  'seg.defaultColour': 'Poner el color de flecha por defecto',
  'seg.defaultColourAria': 'Poner color por defecto',
  'teams.enterNext': 'Intro — siguiente jugador',
  'teams.pickTitle': 'Seleccionar un equipo',
  'zv.bindHint': 'Arrastra un vértice directamente sobre un jugador o el balón y lo seguirá. Suelta el vértice atado de vuelta en la zona para liberarlo.',
  'ad.label': 'Publicidad',
  'ad.close': 'Cerrar publicidad',
  'tl.keyGraphic': 'Registrar flechas — enciende o apaga las flechas del trayecto en el cursor',
  'tl.keyAttachment': 'Registrar anexión — une esto a un jugador en el cursor',
  'tl.keyVisual': 'Registrar visuales — inicia una ventana de visuales de la zona en el cursor',
  'tl.posKey': 'Fotograma de posición en {t} — tócalo para fijar la altura · arrastra para moverlo en el tiempo',
  'tl.keyHoldDelete': 'Mantén pulsada la clave para eliminarla',
  'tl.noPlayers': 'Aún no hay jugadores.',
  'tl.addPlayersHint': 'Usa AÑADIR JUGADOR LOCAL / VISITANTE para colocar jugadores.',

  'itm.item': 'OBJETO',
  'itm.ball': 'Balón',
  'itm.cone': 'Cono',
  'itm.target': 'Objetivo',
  'itm.zone': 'Zona',
  'itm.player': 'Jugador',
  'itm.remove': 'Eliminar',
  'itm.copy': 'Copiar',
  'itm.paste': 'Pegar',
  'itm.attach': 'Adjuntar',
  'itm.detach': 'Soltar',
  'itm.height': 'Altura',
  'itm.arcType': 'Trayectoria',
  'itm.color': 'Color',
  'itm.attachedTo': 'Adjunto a:',
  'itm.arrowsShow': '➜ Flechas activas ({t})',
  'itm.arrowColour': '➜ Color de la flecha…',
  'itm.arrowColourAll': '➜ Colorear las {n} flechas iguales…',
  'itm.arrowsHide': '➜ Flechas apagadas ({t})',
  'itm.arrowsShowMany': '➜ Flechas activas en los {n} ({t})',
  'itm.arrowsHideMany': '➜ Flechas apagadas en los {n} ({t})',
  'itm.arrowsSomeOn': ' — {k} ya activas',
  'itm.arrowsNoLane': ' — {n} no tiene franja de flecha',
  'itm.bendRun': '↝ Curvar esta carrera con precisión…',
  'itm.bendAll': '↝ Curvar las {n} carreras iguales…',
  'itm.bendPass': '↝ Curvar este pase con precisión…',
  'itm.copyIt': '📋 Copiar',
  'itm.onlyThis': ' — solo este item',
  'itm.allOfThese': ' — todos los {n}',
  'itm.pasteHere': '📌 Pegar {name} aquí',
  'itm.duplicateIt': '⧉ Duplicar',
  'itm.nextLineStep': '⧉ Siguiente en la fila ({m} m)',
  'itm.turnLine': '⧉ Girar la línea 90° ({m} m)',
  'itm.mirrorHalf': '⇄ Copiar otra mitad',
  'itm.mirrorSide': '⇄ Copiar otro lado',
  'itm.mirrorFlank': '⇄ Reflejar banda',
  'itm.mirrorEnd': '⇄ Reflejar extremo ↔ extremo',
  'itm.mirrorPitch': '⇄ Campo entero ({n})',
  'itm.mirrorMenu': '⇄ Reflejar… ({n})',
  'itm.mirrorMenuTitle': 'Girarlo — ¿por dónde?',
  'itm.mirrorMenuHint': 'Copiar deja un item NUEVO al otro lado · reflejar mueve lo que ya está · toca uno y las opciones se quedan, así un par lleva dos toques',
  'itm.selectAllKind': '⧉ Seleccionar todos {kind} ({n})',
  'itm.selectLine': '⧉ Seleccionar esta línea ({n})',
  'itm.selectGroupMenu': '⧉ Seleccionar un grupo… ({n})',
  'itm.selectGroupTitle': '¿Qué grupo?',
  'itm.selectGroupHint': 'la línea son los jugadores en la misma franja que este · el lado es todo el equipo · toca uno y su tarjeta se abre en el grupo',
  'itm.deleteAll': '🗑 Borrar todos {n}',
  'itm.menuHint': 'Elige una acción — o los verbos del item arriba de este menú.',

  // SEGUIR: un jugador que sombrea a otro. Sus propias teclas de movimiento
  // quedan donde fueron creadas; el desplazamiento del líder, medido desde el
  // momento en que se pidió el seguimiento, se suma como desplazamiento extra.
  'itm.markAsk': '🎯 Marcar a un rival…',
  'itm.markAskMany': '🎯 Marcar a un rival… ({n} jugadores)',
  'itm.markGapMany': '📏 Ajustar todas las distancias… ({n} marcadores)',
  'itm.keyHere': '✚ Clavar aquí ({t})',
  'itm.keyMany': '✚ Clavar los {n} aquí ({t})',
  'itm.keyFollow': ' — {n} siguen a un portador',
  'itm.keyFollowOne': ' — {n} sigue a un portador',
  'itm.attachAsk': '📎 Vincular a…',
  'itm.attachChange': '📎 En {who}',
  'itm.attachMany': '📎 Vincular a… ({n} objetos)',
  'itm.markChange': '🎯 Marca a {who}',
  'mark.title': '¿A quién marca {who}?',
  'mark.changeTitle': '{who} marca a {leader} — ¿cambiar de marcado?',
  'mark.pickHint': 'Toca a un RIVAL en el campo — marcar es entre equipos',
  'mark.stop': 'Dejar de marcar ({t})',
  'mark.self': '{who} no puede marcarse a sí mismo — elige un rival',
  'mark.cycle': '{who} ya marca a {leader} — uno de los dos debe parar primero',
  'mark.teammate': 'Marcar es entre equipos — {who} es de tu equipo',
  'mark.started': '{who} marca a {leader} · posición: {where} · {d} m · desde {t}',
  'mark.turning': '{who} pasa de {from} a {where} rodeando a {leader} · llega en {t}',
  'mark.stopped': '{who} deja de marcar en {t} · se queda con el terreno ganado y continúa desde ahí',
  'mark.pickTitle': '¿Marcar a {who} — desde dónde?',
  'mark.positionTitle': 'Posición de marca',
  'mark.compassHint': 'Toca o arrastra dentro del círculo para colocar al marcador. El control fija el alcance (0,5–20 m). Frente es su lado del arco.',
  'mark.rangeAria': 'Alcance del círculo en metros',
  'mark.placeAria': 'Posición de marcaje — arrastra; izquierda y derecha ajustan el ángulo, arriba y abajo ajustan la distancia',
  'mark.distAria': 'Distancia de marcaje en metros',
  'mark.pickConfirm': 'Aplicar',
  'mark.pickOther': 'Cambiar jugador',
  'mark.pickStop': 'Dejar de marcar',
  'mark.pickDelete': 'Eliminar clave',
  'mark.deleted': 'Clave de marcaje quitada en {t} · {who} vuelve a donde el recorrido a su alrededor ya lo ponga',
  'attach.deleteKey': '✕ Quitar esta clave de vínculo ({t})',
  'attach.keyDeleted': 'Clave de vínculo quitada en {t} · el {what} vuelve a donde su propio recorrido ya lo ponga',
  'mark.stopMenu': '✕ Dejar de marcar a {who} ({t})',
  'mark.sameSpot': "Ya marca a {leader} en {t} — {where}, a {d} m · elige otro puesto o distancia y toca “Marcar desde aquí”",
  'mark.dir.front': 'frente',
  'mark.dir.frontRight': 'frente-derecha',
  'mark.dir.right': 'derecha',
  'mark.dir.backRight': 'detrás-derecha',
  'mark.dir.back': 'detrás',
  'mark.dir.backLeft': 'detrás-izquierda',
  'mark.dir.left': 'izquierda',
  'mark.dir.frontLeft': 'frente-izquierda',
  'mark.dragged': 'Marcado desde {where}, a {d} m de {leader} — arrastra otra vez para mover la marca',
  'mark.dragNowhere': '{leader} está justo ahí — una marca necesita un hueco para pararse · suelta un paso antes',
  'mark.groupSet': '{n} marcan a {leader} — cada uno desde donde está',
  'mark.groupSetSome': '{n} marcan a {leader}, {skipped} fuera — mismo lado, marcan de vuelta o encima',
  'mark.groupNone': 'Nadie en este grupo puede marcar a {leader} — mismo lado, marcan de vuelta o están encima',
  'mark.groupAskMany': '¿A quién marcan estos {n}?',
  'mark.groupPickHint': 'Toca a un RIVAL en el campo — cada uno marca desde donde está · en {t}',
  'mark.groupOne': 'Solo un jugador en este grupo — usa Marcar directamente en él',
  'mark.stopMenuMany': '✕ Dejar de marcar ({n} jugadores, {t})',
  'mark.groupStopped': '{n} dejan de marcar en {t} · cada uno se queda con el terreno ganado',
  'mark.gapAskMany': '¿Cuán lejos presionan estos {n}?',
  'mark.gapSetMany': '{n} marcadores ahora a {d} m · {where}',
  'mark.gapSkipped': ' ({n} sin marcar a nadie aquí)',
  'mark.gapFew': 'Solo uno de estos está marcando a alguien — usa 🎯 en él directamente',
  'mark.groupNoneStop': 'Nadie en este grupo está marcando a nadie',
  'undoVerb.markDrag': 'posición de la marca',
  // Sacar a un jugador del equipo suelta lo que llevaba y a quién seguía.
  'bench.benched': '{who} salió del equipo — {what} cayeron en {t} · el botón ↩ devuelve todo',
  'bench.markerOne': 'un marcador',
  'bench.markerMany': '{n} marcadores',
  'bench.carriedOne': 'el objeto adjunto',
  'bench.carriedMany': '{n} objetos adjuntos',

  // ADJUNTAR / PASE — los verbos de llevar balón y zona, y la elección del vuelo.
  'attach.follows': '{what} ahora sigue a {who} en {t} · el botón ↩ deshace',
  'attach.release': 'Soltar aquí ({t})',
  'attach.released': '{what} soltado en {t} — se queda solo · el botón ↩ devuelve la posesión',
  'attach.title': '¿Adjuntar {name}?',
  'attach.changeTitle': '{name} sigue a {who} — ¿entregarlo?',
  'attach.pickHint': 'Toca en el campo al nuevo portador · Soltar termina el seguimiento en {t}',
  'attach.pickAny': 'Elige jugador o balón',
  'attach.groupTitle': '¿Vincular {n} objetos a…?',
  'attach.groupChangeTitle': '{n} objetos siguen a otros — ¿pasarlos a otro?',
  'attach.groupRelease': 'Soltar todos ({n}) en {t}',
  'attach.groupFollows': '{n} pasan a seguir a {who} en {t} · el botón ↩ deshace',
  'attach.groupReleased': '{n} sueltos en {t} — quedan por su cuenta · el botón ↩ devuelve el vínculo',
  'attach.groupNone': 'Nada en este grupo puede vincularse — los jugadores usan Marcar',
  'attach.pickPlayer': 'Elige jugador',
  'attach.ballReleased': 'Balón soltado',
  'attach.ballTo': 'Balón anexado a {who}',
  'attach.zoneAlready': 'La zona ya sigue a {who}',
  'attach.zoneNobody': 'La zona no sigue a nadie — suéltala sobre un jugador para anexarla',
  'attach.zoneTo': 'La zona ahora sigue a {who}',
  'attach.zoneOff': 'Zona suelta — se queda donde está en el campo',
  'attach.objAlready': '{what} ya sigue a {who}',
  'attach.objNobody': '{what} no sigue a nadie — suéltalo sobre un jugador para anexarlo',
  'attach.objTo': '{what} ahora sigue a {who}',
  'attach.objOff': '{what} suelto — se queda donde está en el campo',
  'detach.fromOne': 'Desanexada de {who} — está seleccionada ahora, arrástrala a donde quieras',
  'detach.fromMany': '{n} desanexadas de {who}',
  'zoneVertex.bound': 'V{n} → {who} — la zona se deforma mientras se mueven',
  'zoneVertex.freed': 'V{n} liberada — se queda donde la pusiste',
  'pass.title': '¿Pasar a {who}?',
  'pass.attachTitle': '¿Adjuntar a {who}?',
  'pass.detachTitle': '¿Soltar?',
  'pass.flightHint': 'Soltado ahora · elige cuánto dura el vuelo',
  'pass.quick': 'Rápido · 0,5 s',
  'pass.normal': 'Normal · 1 s',
  'pass.lofted': 'Alto · 2 s',
  'pass.loftSuffix': ", por alto",
  'pass.moveOnly': 'Solo mover',
  'pass.to': 'Pase a {who} — recibido {flight} s después{how} · arrastra la tecla de recepción para ajustar',
  'pass.stretched': ' · rama extendida a {t} para el vuelo',
  'pass.sameCarrier': 'El balón ya sigue a {who} — suéltalo en otro para pasar, o en campo libre para liberar',
  'pass.hasBall': '{who} ya tiene el balón — suéltalo en otro jugador',
  'pass.giveTitle': '¿Dar el balón a {who}?',
  'pass.giveHint': '{carrier} suelta ahora · elige cuánto dura el vuelo',
  'pass.takeHint': '{who} toma el control',

  // VÉRTICES DE LA ZONA — el editor de forma. El teclado cita los atajos; el
  // toque cita el gesto en el campo, porque un móvil no tiene Ctrl y ↩ está en pantalla.
  'vertex.nudged': 'Vértice {n} movido {step} m · arrástralo en el campo para modelar libremente',
  'vertex.noWindow': 'No hay ventana visual en el cursor — ✚ en la pista Visual de la zona crea una',
  'vertex.minTwo': 'Una zona conserva al menos 2 vértices — esta ya es una línea',
  'vertex.removed': 'Vértice {n} eliminado — {count} restantes · Ctrl+Z lo trae de vuelta',
  'vertex.added': 'Vértice {n} añadido en el punto medio de la arista — Ctrl+←→ lo mueve · ⇧A añade otro',
  'vertex.grabbed': 'Vértice 1 agarrado — arrástralo en el campo · + Añadir pone otro · − Eliminar quita uno',
  'vertex.grabFirst': 'Agarra un vértice primero — T dos veces agarra el vértice 1 · ⇧A añade uno en la arista',
  'vertex.grabbedOf': 'Vértice 1 de {count} agarrado — arrástralo en el campo · + Añadir pone otro · − Eliminar quita uno',
  'vertex.addedTouch': 'Vértice {n} añadido — {count} en la forma · arrástralo en el campo para colocarlo',
  'vertex.removedTouch': 'Vértice {n} eliminado — {count} restantes · el botón ↩ lo devuelve',
  'view.title': 'Vista — el campo',
  'view.hint': '¿Desde dónde quieres mirar?',
  'view.top': '🎥 Desde arriba',
  'view.low': '🎥 Desde la banda',
  'view.reset': '🎥 Volver a la vista predeterminada',
  'view.arrowsOn': '➜ Mostrar todas las flechas — {n} en el campo',
  'view.arrowsOff': '➜ Ocultar todas las flechas ({n} activas)',
  'view.shapesOn': '◼ Mostrar la forma de todas las zonas ({n} zonas)',
  'view.shapesOff': '◼ Ocultar la forma de todas las zonas ({n} activas)',
  'view.unmarkAll': '🎯 Detener todas las marcas ({n} jugadores)',
  'view.loftOn': '⤴ Elevar todos los vuelos ({n} balones)',
  'view.loftOff': '⤵ Bajar todos los vuelos ({n} balones)',
  'view.gapAll': '📏 Ajustar todas las distancias ({n} marcadores)',
  'view.autoOff': 'AUTO apagado — arrastrar mueve la pose base, sin claves',
  'view.autoOn': 'AUTO encendido — cada arrastre clava una clave en el cursor',
  'view.branches': 'Vista de ramas — abre una rama para volver a su línea de tiempo',
  'view.timeline': 'Línea de tiempo — ‹ VOLVER vuelve al árbol del escenario',
  'view.ballHidden': 'Balón oculto — no se dibuja · este mismo menú lo devuelve, o toca su fila en la pista',
  'view.ballShown': 'Balón visible otra vez',
  'toast.hideGone': 'Ese balón ya no está en el campo — nada que ocultar ni mostrar',
  'view.panelsBack': 'Paneles de vuelta — ▼ los oculta para ver todo el campo',
  'view.panelsHidden': 'Paneles ocultos — ▲ los devuelve · la barra se queda',
  'view.panelsShowAria': 'Mostrar los paneles de la línea de tiempo',
  'view.panelsHideAria': 'Ocultar los paneles de la línea de tiempo',
  'speed.at': 'Reproducción a {r}× — toca el {r}× junto al tiempo para cambiarla',
  'speed.top': 'Velocidad máxima 4× — toca el 4× para bajar',
  'speed.floor': 'Lo más lento: 0,5× — toca el 0,5× para subir',
  'tl.speedChipTitle': 'Velocidad de reproducción — clic para alternar 0,5× → 1× → 2× → 4×',
  'tl.rowSelected': 'Seleccionado — ⇄ Reflejar está en su menú de acciones · toca otra fila para agrupar · toca otra vez para desmarcar',
  'tl.rowUnselected': 'Toca para seleccionar este jugador',
  'tl.scrubHint': 'Arrastra la regla para moverte en el tiempo — se detiene en una clave cuando te acercas · mantenla para el menú de la rama · arrastra el borde derecho para estirar el tiempo',
  'tl.branchName': 'Nombre de la rama',
  'tl.stretchHint': 'Arrastra para estirar o acortar esta rama — encaja en ¼ s · nunca pasa de la última clave · el botón ↩ devuelve el largo',
  'tl.resizeHandle': 'Arrastra para redimensionar',
  'tl.autoKeyHint': 'Auto-clave: activado, mover cualquier cosa graba una clave de posición en el segundo actual',
  'tl.playBranch': 'Reproducir esta rama',
  'tl.pauseBranch': 'Pausar esta rama',
  'tl.playBranchHint': 'Reproducir esta rama — ▶ Reproducir todo recorre el camino entero',
  'tl.visualKey': 'Fotograma visual en {t} — toca para editar la forma · arrastra para moverlo en el tiempo',
  'tl.pauseBranchHint': 'Pausar esta rama — ▶ Reproducir todo recorre el camino entero',
  'tl.midAttach': 'Adjuntar: {what} @ {t} — toca: elegir otro · arrastra: mover · doble toque: borrar',
  'tl.midAction': 'Acción: {what} @ {t} — toca: elegir otra acción · arrastra: mover · doble toque: borrar',
  'tl.keyMark': 'Marcado — registra a quién marca este jugador en el cursor',
  'tl.tnMovement': 'Pista de movimiento',
  'tl.tnAction': 'Pista de acción',
  'tl.tnGraphic': 'Pista de flechas',
  'tl.tnMark': 'Pista de marcado',
  'tl.tnVisual': 'Pista de visuales',
  'tl.tnAttachment': 'Pista de anexión',
  'tl.laneHold': ' — mantén pulsada la pista para añadir una clave aquí',
  'tl.segBar': 'Transición {a} → {b}: {style}{arch} — toca para curvar el trayecto',
  'tl.arrowBar': 'Flechas del trayecto {a} → {b} — toca para cambiar el color',
  'tl.zoneVisBar': 'Visuales de la zona {a} → {b} — toca para editar radio, forma y esquinas',
  'tl.branchRename': '{name} — mantén pulsado el nombre para renombrarlo',
  'tl.markKey': '🎯 Marcando a {who} en {t} — toca para cambiar el punto · arrastra para moverlo en el tiempo',
  'tl.markKeyOff': '🎯 Marca deshecha en {t} — toca para marcar otra vez · arrastra para moverlo en el tiempo',
  'tl.actionKey': '{what} en {t} — toca para elegir otra acción · arrastra para moverlo en el tiempo',
  'tl.graphicKey': 'Flechas {value} en {t} — toca para encenderlas o apagarlas · arrastra para moverlo en el tiempo',
  'tl.on': 'encendidas',
  'tl.off': 'apagadas',
  'tl.detached': 'Desvinculado',
  'timing.title': 'Tiempo — esta rama · 0–{t}',
  'timing.desc': 'Todo aquí cambia CUÁNDO ocurren las cosas, nunca DÓNDE. Arrastra el borde de la regla para cambiar la ventana.',
  'timing.slower': '⤢ Más lento — cada clave ×1,25',
  'timing.quicker': '⤡ Más rápido — cada clave ×0,8',
  'timing.clip': '✂ Terminar en la última clave ({t})',
  'timing.rippleOpen': '↔ Abrir un hueco después de {t} (+0,5 s)',
  'timing.rippleClose': '↔ Cerrar el hueco después de {t} (−0,5 s)',
  'act.title': 'Acción — {what}',
  'act.section.actions': 'Acciones',
  'act.section.time': 'Tiempo',
  'act.section.visual': 'Visual',
  'act.now': '{what} · ahora',
  'act.nowSome': '{what} · {n} de {m} ahora',
  'act.item': 'Elemento',
  'form.group': 'Colocar una formación',
  'form.place': 'Colocar {name} — todo el equipo, desde el portero',
  'form.none': 'Este equipo aún no tiene jugadores — añade uno en la lista de abajo',
  'form.already': 'Ya está en {name} — nada que colocar',
  'form.placed': '{name} — {n} colocados',
  'form.placedShort': '{name} — {n} colocados, {empty} huecos libres',
  'act.plain': 'Acción',
  'act.groupMore': ' +{n} más',
  'act.titleAll': 'Acción — {n} a la vez',
  'act.titleSome': 'Acción — {n} de {total} (las bolas y las zonas no tienen acciones)',
  'kf.deleteActionHere': '✕ Borrar esta acción ({t})',
  'edit.key': 'Clave',
  'edit.stepsMove': 'Los pasos mueven…',
  'edit.stepsHere': 'Solo este momento',
  'edit.stepsAfter': 'Este + todo lo de después',
  'edit.deleteKey': 'Borrar la clave',
  'edit.otherItem': 'otro elemento',
  'edit.landOn': '⇢ Encajar en {t} — {who}',
  'edit.landOnHint': 'Mueve este momento al instante en que {who} registra la clave — los dos llegan juntos',
  'edit.transition': 'Transición',
  'edit.style': 'Estilo',
  'edit.straight': 'Recta',
  'edit.left': 'Izquierda',
  'edit.right': 'Derecha',
  'edit.curve': 'Curva',
  'edit.arch': 'Arco',
  'edit.archPeak': 'Punto máximo del arco',
  'edit.curveHint': 'Tira del asa violeta en el campo para doblarlo al otro lado',
  'edit.archHint': 'Tira del asa violeta para elevar el perfil, o aplanarlo',
  'edit.archPeakHint': 'Desliza el punto máximo del vuelo a lo largo de la trayectoria',
  'edit.spotAlongHint': 'Mueve este punto 0,5 m a lo largo del campo · el paso fino es 0,1 m',
  'edit.spotAcrossHint': 'Mueve este punto 0,5 m a lo ancho del campo · el paso fino es 0,1 m',
  'edit.timeEarlier1': 'Mueve este momento 1 s hacia atrás',
  'edit.timeEarlierQ': 'Mueve este momento 0,25 s hacia atrás',
  'edit.timeLaterQ': 'Mueve este momento 0,25 s hacia adelante',
  'edit.timeLater1': 'Mueve este momento 1 s hacia adelante — la rama se ajusta',
  'edit.arrowColour': 'Color de la flecha',
  'view.follow': 'Seguir',
  'view.followTitle': 'Seguir un item en el campo — toca el item para seguirlo · ⌥N solo enfoca la cámara',
  'view.pickHint': 'Toca en el jugador, balón o item del campo para seguirlo.',
  'vp.loading': 'Cargando escena…',

  // COPIAR / PEGAR / RUTA — los verbos del portapapeles y la eliminación de grupo.
  'side.home': 'local',
  'side.away': 'visitante',
  'clip.deletedN': '{n} eliminados — el botón ↩ los devuelve',
  'clip.onlyOne': 'Solo hay un {what} en el campo — mantén otro para añadirlo al grupo',
  'clip.whatPlayer': 'jugador {side}',
  'clip.copied': 'Copiado {name}{route} — 📌 Pégalo en el menú de otro {type}',
  'clip.route': ' + su trayectoria',
  'clip.routeSame': 'La ruta ya funciona igual en {label} — nada cambió',
  'clip.stateSame': '{label} ya tiene exactamente ese estado — nada cambió',
  'clip.pasted': 'Pegado en {label}',

  // ALTURA / PUNTO ÁLGIDO / CURVA DE LA TECLA — los marcadores de vuelo.
  'key.heightAt': 'Tecla @ {t}: altura {h} m{hint}',
  'key.heightHint': ' — tira del asa morada para dar forma al vuelo',
  'key.peakAt': 'Punto álgido al {pct}% del vuelo · {from}–{to} — ⌥⌃[ ] lo mueve · ⌥[ ] la altura',
  'key.bendAt': 'Curva {pct}% · {from}–{to} — C cambia de lado',
  'key.zoneRadius': 'Radio de zona {r} m desde {t}',
  'key.arrowDeleted': 'Ventana de flecha de {t} eliminada — el botón ↩ la trae de vuelta',

  // EL PASADOR DE TIEMPO DEL EDITOR DE TECLA — los rechazos explican la salida.
  'kf.startZero': 'Una rama empieza en 0 — este momento está a solo {t} del inicio',
  'kf.rippleStretched': 'Todo lo después de {t} se movió también · rama extendida a {end}',
  'kf.blockedAt': 'Bloqueado — ya hay una tecla en {t} · salta por encima',
  'kf.blockedDrag': 'Bloqueado — ya hay una tecla en {t} · suéltala al lado',
  'kf.movedStretched': 'Momento movido a {t} · rama extendida para encajar',

  // CURVA (C) Y ELEVACIÓN (⇧C) — lo que el ciclo acaba de hacer, dicho en voz alta.
  'bend.noneGroup': 'Ningún miembro tiene segmento de movimiento en el cursor — toca ✚ en una Pista de movimiento para clavear uno',
  'bend.noneHere': 'Ningún segmento de movimiento en el cursor — K clavea una posición primero',
  'bend.cycled': 'Curvas alternadas — {n} del grupo{skipped}',
  'bend.skipped': ' ({n} sin segmento aquí)',
  'act.noLaneSkipped': ' ({n} sin teclas de acción)',
  'bend.cycledTo': '{from}–{to}: {name}',
  'loft.on': 'Elevación {m} m · {from}–{to} — tira del asa morada',
  'loft.off': 'Pase raso · {from}–{to}',
  'loft.onGroup': '{n} trayectorias ahora suben {m} m{skipped}',
  'loft.offGroup': '{n} trayectorias volvieron al suelo{skipped}',
  'loft.skipped': ' ({n} sin vuelo)',
  'loft.noneGroup': 'Ningún balón de este grupo tiene trayectoria en el cursor — elevar escribe entre dos claves de posición',

  // LOS VERBOS DE FLECHA (G / ⇧G). ACTIVADA/DESACTIVADA van en la frase: el
  // adjetivo concuerda con el sustantivo y un hueco no puede hacerlo.
  'arrow.on': 'Flecha activada en {t} — {name}',
  'arrow.off': 'Flecha desactivada en {t} — {name}',
  'arrow.groupOn': 'Flechas activadas en {t} — {n}{blocked}',
  'arrow.groupOff': 'Flechas desactivadas en {t} — {n}{blocked}',
  'arrow.blockedSeam': ' ({n} bloqueadas en la unión)',
  'arrow.groupColour': 'Flechas {c} — {n} del grupo{skipped}',
  'arrow.skippedColour': ' ({n} sin flecha que colorear)',
  'arrow.onRed': 'Flecha EN ROJO desde {t} — ⇧G cambia el color',
  'arrow.colourSet': 'Flecha {c} desde {t}',
  'color.red': 'roja', 'color.orange': 'naranja', 'color.yellow': 'amarilla',
  'color.green': 'verde', 'color.blue': 'azul', 'color.purple': 'morada',
  'color.default': 'color predeterminado',

  // ARMAR UNA COLOCACIÓN — la ruta táctil más usada de la app. "Colocación
  // armada:" en vez de "{name} armado": el adjetivo tendría que concordar con
  // un sustantivo que solo conocemos al ejecutar.
  'itm.circleZone': 'Zona circular',
  'itm.dynamicZone': 'Zona dinámica',
  'place.cancelled': 'Colocación cancelada: {name}',
  'place.armed': 'Colocación armada: {name} — toca el campo · sigue tocando para más · el mismo botón de nuevo (o Esc) cancela',
  'place.armedAgain': 'Colocación rearmada: {name} — toca el campo · sigue tocando para más · el mismo botón de nuevo (o Esc) cancela',
  'bench.disarmed': 'Colocación desarmada: {who} — ⇧{key} arma al siguiente del banquillo',
  'bench.armedClick': 'Colocación armada: {who} — clic en el campo · ⇧{key} de nuevo cancela',
  'bench.noBenchHome': 'Todos los jugadores del local ya están en el campo — el banquillo está vacío',
  'bench.noBenchAway': 'Todos los jugadores del visitante ya están en el campo — el banquillo está vacío',
  'bench.playerN': 'Jugador {n}',
  'bench.armedNext': '{who} es el siguiente — toca el campo · el mismo botón de nuevo (o Esc) cancela',

  // CUANDO EL SERVIDOR DICE NO. El motivo llega en las palabras del servidor,
  // pero la frase que lo rodea pertenece al idioma del entrenador.
  'scenario.saveFailed': 'Error al guardar: {why}',
  'scenario.saveAsFailed': 'Error al guardar como nuevo: {why}',
  'scenario.renameFailed': 'Error al renombrar: {why}',
  'scenario.deleteFailed': 'Error al eliminar: {why}',
  'scenario.deletedNamed': '“{name}” eliminado',
  'scenario.shareFailed': 'Error al actualizar el uso compartido: {why}',
  'scenario.checkoutFailed': 'Error en el pago: {why}',
  'scenario.checkoutPopupBlocked': 'Permite las ventanas emergentes para pagar sin salir del campo.',
  'scenario.portalFailed': 'Error al abrir el portal de facturación: {why}',
  'scenario.portalPopupBlocked': 'Permite las ventanas emergentes para gestionar los pagos sin salir del campo.',
  'scenario.unknownError': 'error desconocido',
  'vis.windowDeleted': 'Ventana visual en {t} eliminada — T crea una nueva',
  'vis.windowsDeletedN': '{n} ventanas visuales eliminadas — ✚ en la pista Visual crea una nueva',
  'vis.windowsDeletedOne': '{n} ventana visual eliminada — ✚ en la pista Visual crea una nueva',
  'vis.lockedAtSeam': ' · {n} bloqueadas en la unión',
  'vis.noZonesScene': 'No hay zonas en el campo — una superposición de forma necesita una zona',
  'vis.windowsOpenedScene': '{n} formas de zona mostradas en {t}',
  'group.deletedStill': '{name} eliminado — {n} siguen agrupados · mantén pulsado uno para eliminarlos todos · el botón ↩ deshace',

  // LAS ÚLTIMAS MENSAJES LITERALES — los verbos que aún respondían en inglés.
  'team.switchTo': '{name} → {side}',
  'vis.windowOpenGrab': 'Ventana visual en {t} — editor abierto · mantén la zona de nuevo — o doble clic — para tomar el vértice 1',
  'vis.windowOpen': 'Ventana visual en {t} — editor abierto · ✎ Editar Forma muestra los puntos · T de nuevo toma el vértice 1',
  'vis.windowGroup': 'Ventana visual en {t} para {n} zonas — editor abierto para esta',
  'lane.move': 'tecla de movimiento', 'lane.arrow': 'tecla de flecha', 'lane.shape': 'tecla de forma',
  'lane.attach': 'tecla de vínculo', 'lane.action': 'tecla de acción',
  'lane.keyAt': '{lane} en {t} — Enter la edita',
  'branch.lastKeyAt': 'La última tecla está en {t} — “Recortar tiempo” en la fila de la rama recorta justo hasta ella',
  'branch.nowDur': 'Rama ahora 0–{t} — {grow}⌥+/− escala las TECLAS · el botón ↩ deshace',
  'branch.growNote': 'sitio para clavear después · ',
  'branch.priority': 'Prioridad {dir} — ahora #{n} de {m}{choice} · el botón ↩ deshace',
  'branch.dirUp': 'arriba', 'branch.dirDown': 'abajo',
  'branch.choiceKept': ' · camino elegido sin cambio',
  'branch.choiceFollows': ' · el camino maestro sigue la prioridad aquí',
  'branch.edgeDrag': 'Rama {before} → {now} — arrastre del borde · el botón ↩ devuelve el largo',
  'attach.nothingAttached': 'Nada sigue a {name} todavía — la brújula MARK en su carril pregunta a quién seguir',
  'attach.detached': 'Vínculo suelto en {t} — el botón ↩ lo trae de vuelta',
  'prompt.answered': 'Pedido respondido — {type} más cercano · {d} m de distancia',
  'pass.tuneCatch': 'Pase a {name} — arrastra el ◆ de la Pista de anexión para mover la recepción',
  'pass.stretchedFor': ' · rama extendida a {t} para el vuelo',
  'group.allSelected': 'Los {n} {label} seleccionados — arrastra uno y se mueven todos · el menú espeja / duplica el bloque',
  'clip.duplicatedN': '{n} objetos duplicados — {names} · agrupados, arrastra uno y se mueven todos',
  'clip.duplicatedOne': 'Duplicado como {names} — toca ⧉ Duplicar otra vez para poner otro a {d} m',
  'clip.skippedPlayers': ' · {n} jugador(es) omitidos — usan copiar/pegar',

  // LAS NEGACIONES. Todas ya señalaban la salida — eran las últimas frases del
  // app que solo podía oír quien lee inglés.
  'branch.onlyNow': 'Solo la rama ahora — Space reproduce todo el camino otra vez',
  'act.ballsNoRepeat': 'Los balones y las zonas no tienen teclas de acción — ↻ Otra vez necesita un jugador, cono o objetivo',
  'act.ballsNoPick': 'Los balones y las zonas no tienen teclas de acción — X es para jugadores, conos y objetivos',
  'act.noActionYet': 'Ninguna acción usada aún — el ✚ de la pista Acción crea una',
  'act.againAt': '{action} de nuevo en {t}',
  'act.againGroupBalls': ' — {n} de {m} (los balones y las zonas no tienen acciones)',
  'act.againGroupAll': ' — todos los {n}',
  'itm.repeat': '↻ Otra vez: {action}',
  'bench.squadComplete': 'Equipo completo — todos los jugadores de la plantilla están en el campo',
  'kf.noPosKey': 'Ninguna clave de posición en este momento o antes — toca un rombo de la franja para caer en una',
  'kf.noPosKeyMany': 'Ninguno de estos tiene clave de posición en este momento — toca un rombo de la franja para caer en una',
  'kf.noSegment': 'Ningún segmento de movimiento bajo el cursor — ✚ en el menú del item clava una posición primero',
  'kf.noBallSegment': 'Ningún segmento de balón bajo el cursor — ✚ en el menú del item clava una posición primero',
  'kf.walkBackNone': 'Ninguna clave antes de este momento — la siguiente está delante · ✚ en el menú del item clava una aquí',
  'kf.walkFwdNone': 'Ninguna clave después de este momento — ✚ en el menú del item clava una aquí',
  'kf.nothingExact': 'Nada exactamente en este momento — toca un rombo de la franja para andar hasta la clave más cercana',
  'kf.noneYet': 'Aún no hay claves — ✚ en el menú del item clava una posición primero',
  'loft.noLoft': 'Sin elevación en este segmento — ⇧C la eleva, luego ⌥⌃[ / ⌥⌃] ponen el pico',
  'loft.alreadyFlat': 'Ya está raso — tira del asa morada, o ⤴ Elevar en el menú del item',
  'loft.max': 'Elevación máxima 10 m — tira del asa morada hacia abajo',
  'loft.flatNow': 'Pase raso · {from}–{to} — tira del asa morada para elevarlo',
  'vis.noCircleWindow': 'Ninguna ventana de zona circular en el cursor — T crea una',
  'prompt.answerFirst': 'Responde al pedido primero — 1 acepta · 0 cancela',
  'play.normalAlready': 'Ya a velocidad normal — +/− la escalonan',
  'play.atOneX': 'Reproducción 1× — velocidad normal',
  'branch.shortest': 'Mínimo 1 s — el paso ▸▸ en la fila de la rama devuelve margen',
  'branch.longest': 'Máximo 600 s — el paso ◂◂ en la fila de la rama recorta',
  'vis.forZones': 'Los visuales de zona son para zonas — ⇧⌥S selecciona todas las zonas',
  'tree.atRoot': 'Ya está en la raíz — toque una hija en PRÓXIMAS POSIBLES para entrar',
  'tree.isRoot': 'Esta es la rama raíz — toque una hija en PRÓXIMAS POSIBLES para entrar',
  'tree.noKids': 'Ninguna sub-rama aquí — pulsa B para añadir una',
  'tree.renaming': 'Renombrando la rama — escribe el nuevo nombre · Enter lo guarda',
  'tree.rootNoSiblings': 'La rama raíz no tiene hermanas — pulsa B para añadir una sub-rama',
  'tree.oneBranch': 'Una sola rama — “+ Añadir sub-rama” hace crecer el árbol',
  'tree.firstSibling': 'La primera rama de esta división — ‹ VOLVER abre el árbol, toca la rama que quieras',
  'tree.lastSibling': 'La última rama de esta división — ‹ VOLVER abre el árbol, toca la rama que quieras',
  'tree.onlyOneKid': 'Solo 1 rama bajo esta — PRÓXIMAS POSIBLES la muestra',
  'tree.onlyNKids': 'Solo {n} ramas bajo esta — PRÓXIMAS POSIBLES las muestra',
  'tree.rootNoPriority': 'La rama raíz no tiene prioridad — el camino siempre empieza aquí',
  'tree.alreadyFirst': 'Ya es la primera de esta división',
  'tree.alreadyFirstPath': 'Ya es la primera — la línea de tiempo maestra ya toma esta rama aquí',
  'tree.alreadyLast': 'Ya es la última de esta división',
  'route.pastedOne': 'Ruta pegada en {label}',
  'route.pastedMany': 'Ruta pegada en {n} — cada una en su propio sitio',
  'app.newHere': '¿Empiezas ahora? Toca un item y arrástralo en el campo — al soltar, el movimiento ya queda registrado · tócalo de nuevo para ver su menú. El icono de archivo guarda ❓ Ayuda y tutoriales.',
  'app.defaultScenarioTitle': 'Escenario predeterminado',
  'app.scenarioUnconfigured': 'No hay un escenario predeterminado disponible. Abre un escenario desde el menú de archivo o crea uno nuevo.',
  'app.scenarioUnavailable': 'No se pudo cargar el escenario predeterminado del sitio, así que el campo está vacío. Abre un escenario desde el menú de archivo, o comprueba que el servidor esté activo.',
  'attach.noPlayer': 'Ningún jugador en el campo al que vincular — abre el menú de colocación y elige uno del banquillo',
  'attach.noBall': 'Ningún balón en el campo para dar — abre el menú de colocación y coloca uno en el campo',
  'select.nonePlayers': 'Sin {who} en el campo para seleccionar — añade jugadores desde el menú JUGADOR',
  'select.noneItems': 'Sin {what} en el campo para seleccionar — añade uno desde el menú +',
  'itm.loftOn': '⤴ Elevar {from}–{to}',
  'itm.loftOff': '⤵ Sin elevación {from}–{to}',
  'itm.loftMany': '⤴ Elevar ({n} balones)',
  'itm.loftSome': '⤴ Elevar {n} que puede',
  'itm.bendCycle': '↝ Curva {from}–{to}: {now}',
  'itm.bendCycleMany': "↝ Curvar los {n}",
  'itm.bendCycleSome': '↝ Curvar los {n} que pueden',
  'itm.lift': '↑ {t} +0,5 m ({h} m)',
  'itm.lower': '↓ {t} −0,5 m ({h} m)',
  'itm.liftMany': "↑ los {n} +0,5 m",
  'itm.lowerMany': "↓ los {n} −0,5 m",
  'itm.liftSome': '↑ {n} de {m} +0,5 m',
  'itm.lowerSome': '↓ {n} de {m} −0,5 m',
  'itm.manyAt': ' ({h} m)',
  'height.floor': 'Ya está en el suelo — ↑ para subirlo',
  'height.ceiling': 'El techo son 10 m — ↓ para bajarlo',
  'height.floorMany': "Todos en el suelo — ↑ para subirlos",
  'height.ceilingMany': "Todos en el techo de 10 m — ↓ para bajarlos",
  'height.follows': 'Ahora sigue a otro item — su altura es la del otro · su propio menú tiene la opción que lo suelta',
  'itm.ballHide': '👻 Ocultar este balón',
  'itm.ballShow': '👁 Mostrar este balón',
  'scenario.nowPrivate': '“{name}” ahora es privado',
  'scenario.nowPublic': '“{name}” ahora es público',

  // LOS BOTONES DE REPRODUCCIÓN del campo, y el modal de comentarios.
  'play.speedTitle': '{label} — mantén pulsado o clic derecho para la velocidad ({speed}×)',
  'play.speed': 'Velocidad de reproducción',
  'fb.title': 'Enviar comentarios',
  'fb.type': 'Tipo',
  'fb.cat.suggestion': 'Sugerencia',
  'fb.cat.bug': 'Informe de error',
  'fb.cat.question': 'Pregunta',
  'fb.cat.other': 'Otro',
  'fb.message': 'Tu mensaje',
  'fb.placeholder': 'Cuéntanos qué piensas — ideas, errores, preguntas, lo que sea...',
  'fb.sent': '✓ ¡Gracias! Comentario enviado.',
  'fb.failed': '✗ No se pudo enviar. Inténtalo de nuevo.',
  'fb.close': 'Cerrar',
  'fb.send': 'Enviar',
  'fb.sending': 'Enviando...',

  // EL EXPLORADOR DE ESCENARIOS PÚBLICOS — un modal entero sin traducir.
  'pub.titleOpen': 'Explorar escenarios abiertos',
  'pub.titlePublic': 'Explorar escenarios públicos',
  'pub.searchPlaceholder': 'Buscar por nombre… (déjalo vacío para ver todos)',
  'pub.search': 'Buscar',
  'pub.clear': 'Limpiar',
  'pub.loading': 'Cargando…',
  'pub.none': 'No se encontraron escenarios públicos.',
  'pub.load': 'Cargar',
  'pub.showing': 'Mostrando {from}–{to} de {total} escenarios',
  'pub.showingOne': 'Mostrando {from}–{to} de 1 escenario',
  'pub.prev': '‹ Anterior',
  'pub.next': 'Siguiente ›',
  'pub.loadListFailed': 'No se pudieron cargar los escenarios',
  'pub.loadOneFailed': 'No se pudo cargar el escenario',
  'pub.movementKeys': '{n} tecla(s) de movimiento',
  'pub.emptyTimeline': 'línea de tiempo vacía',
  'zv.title': 'Visuales de la zona',
  'zv.close': 'Cerrar los visuales de la zona',
  'zv.radius': 'Radio — {m} m',
  'zv.editShape': '✎ Editar la forma (añadir / mover / eliminar puntos)',
  'zv.closed': 'Cerrada',
  'zv.open': 'Abierta',
  'zv.vertexHint': 'Vértices adjuntos — arrastra un vértice hacia un jugador, o toca Link y luego él',
  'zv.notAttached': 'sin adjuntar',
  'zv.link': 'Link',
  'zv.linkTitle': 'Adjuntar V{n} por clic: Link y luego clic en un jugador o en el balón — o simplemente arrastra el punto V{n} hasta ellos',
  'zv.free': 'Liberar V{n} — dejar de seguir a {who}',
  'zv.opacity': 'Opacidad — {p}%',
  'zv.thickness': 'Grosor — {n}',
  'toast.arrowsNeedItem': 'Selecciona un item primero — su menú ofrece ➜ Flechas activas / apagadas',
  'toast.arrowsAlreadyOn': 'Las flechas ya aparecen en {t} — no cambió nada',
  'toast.arrowsAlreadyOff': 'No hay flechas que ocultar en {t} — no cambió nada',
  'toast.arrowsOnMany': 'Flechas activadas para {n} en {t}',
  'toast.arrowsOffMany': 'Flechas desactivadas para {n} en {t}',
  'toast.alreadyThatWay': ' ({n} ya estaban así)',
  'toast.heightUpMany': "{n} objetos elevados {m} m",
  'toast.heightUpOne': "{n} objeto elevado {m} m",
  'toast.heightDownMany': "{n} objetos bajados {m} m",
  'toast.heightDownOne': "{n} objeto bajado {m} m",
  'toast.heightSkipped': " ({n} no se pudieron mover)",
  'toast.arrowsOnOne': 'Flechas activadas — {name} en {t}',
  'toast.arrowsOffOne': 'Flechas desactivadas — {name} en {t}',
  'toast.undoSuffix': ' · el botón ↩ deshace',
  'toast.alreadyActionMany': 'Los {n} ya están {action} en {t} — no cambió nada',
  'toast.alreadyActionOne': 'Ya está {action} en {t} — no cambió nada',
  'toast.repeatSameInstant': 'Ya está {action} en {t} — avanza el cursor o elige otra acción',
  'toast.attachedTo': 'Adjunto a {name} · {d} m de distancia · {t} — mantén el balón otra vez para soltarlo',
  'toast.keyNeedItem': 'Selecciona un item primero — su fila en la línea de tiempo tiene un ✚ para lo que quieras registrar',
  'toast.keyAllFollow': 'Los {n} siguen a un portador aquí — registrar crea puntos muertos · el ✚ de la Pista de anexión abre “Soltar aquí”',
  'toast.keyAllFollowOne': 'Este está siguiendo a un portador aquí — registrar crea un punto muerto · el ✚ de la Pista de anexión abre “Soltar aquí”',
  'toast.keyMany': '{n} claves en {t}',
  'toast.keyFollowSuffix': ' ({n} siguen a un portador — el ✚ de la Pista de anexión abre “Soltar aquí”)',
  'toast.keyFollowSuffixOne': ' ({n} sigue a un portador — el ✚ de la Pista de anexión abre “Soltar aquí”)',
  'toast.keyOne': '{name} registrado en {t}',
  'toast.noPosPlayer': 'Este jugador aún no tiene posición que registrar.',
  'toast.noPosItem': 'Este item aún no tiene posición que registrar.',
  'toast.followsCarrier': '{name} sigue a {who} aquí — una clave de posición sería un punto muerto · el ✚ de la Pista de anexión abre “Soltar aquí”',
  'toast.sNeedItem': 'Selecciona un item primero — S apoya su clave en el segundo más cercano que otro item ya registra',
  'toast.sNoOwnKey': 'Este item no tiene clave de movimiento — la ✚ de su fila en la línea de tiempo clava una en el cursor',
  'toast.sNothingElse': 'Nada más está registrado — S apoya este momento en el segundo de OTRO item · arrastra el ◆ de su fila para moverlo en el tiempo',
  'toast.momentDeleted': 'Momento en {t} eliminado — llegada + posesión juntas · el botón ↩ devuelve las dos',
  'toast.keyDeleted': 'Clave en {t} eliminada · el botón ↩ la devuelve',
  'toast.nudgeKey': 'Clave → {t}',
  'toast.nudgeNote': ' — {note}',
  'toast.nudgeMoment': ' · el momento entero se movió junto',
  'toast.nudgeGroup': ' · {n} en el grupo',
  'toast.nudgeStepSuffix': ' · los botones ◂ 0.25 / 0.25 ▸ lo mueven',
  'toast.sameSecond': 'en el mismo segundo que {who} · {t}',
  'toast.mirrorWhole': 'Rama entera reflejada — {n} en total {axis} · “⇄ Reflejar…” lo hace desde el menú',
  'toast.mirrorOne': '{n} item reflejado {axis}',
  'toast.mirrorMany': '{n} items reflejados {axis}',
  'toast.mirrorAxisFlank': 'banda ↔ banda',
  'toast.mirrorAxisEnd': 'portería ↔ portería',
  'toast.mirrorFlankHint': ' — ⇄ Reflejar extremo lo gira al revés',
  'toast.mirrorEndHint': ' — ⇄ Reflejar banda lo gira al revés',
  'toast.mirrorPlacedHalf': '{label} reflejado a la otra mitad — la otra entrada de espejo lo invierte al revés · toca ⇄ Copiar otra vez para otro',
  'toast.mirrorPlacedSide': '{label} reflejado al otro lado — la otra entrada de espejo lo invierte al revés · toca ⇄ Copiar otra vez para otro',
  'toast.lineNeedTrail': 'Pon dos primero — marcan la separación · toca dos y ⧉ Siguiente en la fila lo repite',
  'toast.lineSameSpot': 'Los dos están en el mismo sitio — pon el segundo en otro sitio para marcar la separación',
  'toast.lineTurned': '{label} giró 90° — ⧉ Siguiente en la fila sigue la nueva línea · ⧉ Girar la línea 90° dibuja un cuadrado',
  'toast.lineStepped': '{label} {m} m más — ⧉ Siguiente en la fila otra vez · ⧉ Girar la línea 90° hace una rejilla',
  'toast.mirrorNothingPitch': 'No hay nada en el campo que reflejar',
  'toast.mirrorNeedSelect': 'Nada seleccionado que reflejar — selecciona un item, o abre su menú Acción y usa ⇄ Reflejar',
  'toast.dupPlayerNo': 'Los jugadores no se pueden duplicar — copia uno (📋 en su menú) y pégalo en otra plaza.',
  'toast.placeNeedArm': 'Coloca uno primero — el menú + arma un item, luego toca el campo',
  'toast.placeBallOne': 'Los balones van de uno en uno — arrastra uno hasta un jugador para dárselo',
  'toast.placedMore': '{type} colocado — toca otra vez para más{hint} · el mismo botón de nuevo (o Esc) cancela',
  'toast.placedHintLine': ' · ⧉ Siguiente en la fila repite este espaciado',
  'toast.placedHintMirror': ' · el menú + lo refleja a la otra mitad',
  'kindOne.cone': 'Cono', 'kindOne.target': 'Objetivo', 'kindOne.zone': 'Zona', 'kindOne.ball': 'Balón',
  'kindMany.cones': 'conos', 'kindMany.targets': 'objetivos', 'kindMany.zones': 'zonas',
  'kindMany.balls': 'balones', 'kindMany.playersHome': 'jugadores del local', 'kindMany.playersAway': 'jugadores del visitante',
  'toast.groupLeft': '{name} salió del grupo',
  'toast.groupJoined': '{name} en el grupo',
  'toast.groupMany': ' — {n} en el grupo · arrastra uno para moverlos todos · mantén uno para reflejar / duplicar / borrar todos',
  'toast.groupOne': ' · mantenlo otra vez — o doble clic — para sus acciones',
  'toast.ballNoGroup': 'Los balones no entran en grupos — grupo de {n} conservado · arrastra un miembro para moverlos todos',
  'toast.autoOff': 'AUTO está apagado — ese movimiento no se reproducirá. El botón AUTO en la franja lo vuelve a encender.',
  'toast.landed': '↳ {name}',
  'toast.landedPath': '↳ {name} — {path}',
  'toast.landedKids': ' · toque una hija en PRÓXIMAS POSIBLES',
  'toast.landedAddBranch': ' · “+ Añadir sub-rama” hace crecer el árbol',
  'toast.landedUpMany': ' · toque la rama de arriba para salir',
  'toast.landedUpOne': ' · toque la rama de arriba para volver',
  'toast.landedWalkIn': ' · toque una hija para entrar',
  'toast.branchRemoved': 'Rama “{name}” eliminada — el botón ↩ la devuelven',
  'toast.clipTight': 'Ya está justo — última clave en {t} · ✚ en el menú del item clava una después',
  'toast.clipRestores': ' · el botón ↩ restaura {old}',
  'ripple.movedLaterOne': '1 clave movida más tarde después de {t}',
  'ripple.movedLater': '{n} claves movidas más tarde después de {t}',
  'ripple.movedEarlierOne': '1 clave movida más temprano después de {t}',
  'ripple.movedEarlier': '{n} claves movidas más temprano después de {t}',
  'ripple.stayed': ' · {n} heredadas se quedaron',
  'vertex.at': '{x}, {y} m',
  'vertex.shiftFine': ' · ⇧ = 0,1 m fino',
  'select.cycleHint': ' · N siguiente · ⇧N anterior',
  'select.cameraFocused': ' · cámara enfocada en él',
  'select.cameraHint': ' · ⌥N también mueve la cámara',
  'toast.stretchSlower': 'Rama más lenta — {old} → {new}',
  'toast.stretchNothing': 'Nada que estirar — todas las claves están en 0:00',
  'toast.stretchFloor': 'Ya está en el piso de 1 s — estírala primero, o arrastra el borde de la regla para recortar la ventana',
  'toast.stretchFaster': 'Rama más rápida — {old} → {new}',
  'toast.revertSuffix': ' · el botón ↩ revierte',
  'toast.rootNoPriority': 'La rama raíz no tiene prioridad — el camino siempre empieza aquí',
  'toast.alreadyFirstChosen': 'Ya es el primero en esta bifurcación',
  'toast.alreadyFirstPath': 'Ya es el primero — el camino maestro ya toma esta rama aquí',
  'toast.alreadyLastFork': 'Ya es el último en esta bifurcación',
  'toast.priorityMoved': 'Prioridad {dir} — ahora #{rank} de {n}',
  'toast.dirUp': 'arriba', 'toast.dirDown': 'abajo',
  'toast.chosenUnchanged': ' · camino elegido sin cambios',
  'toast.pathFollowsPriority': ' · el camino maestro sigue la prioridad aquí',
  'toast.timelineSame': '“{name}” ya tiene exactamente esa línea de tiempo — no cambió nada',
  'toast.rootNoSiblings': 'La rama raíz no tiene hermanas — “+ Añadir sub-rama” continúa desde su final',
  'toast.noSiblings': 'No hay hermanas aquí — “+ Añadir sub-rama” continúa desde este final',
  'toast.branchDuplicated': '“{src}” duplicada — ahora editas “{name}” · el botón ↩ la elimina',
  'toast.needZone': 'Los visuales de zona son para zonas — selecciona una zona',
  'toast.needBallLoft': 'La elevación es para el balón — selecciónalo primero',
  'toast.needBallHide': 'Selecciona un balón primero — ⇧V lo oculta o muestra (N cambia la vista)',
  'toast.hideOnlyBall': 'Solo los balones se ocultan — ⇧V vale con un balón seleccionado · V cambia la vista',
  'toast.needBallAttach': 'Selecciona un balón, zona o jugador primero — ⌥A engancha al compañero más cercano',
  'toast.needItemSelectAll': 'Selecciona un item primero — su menú ofrece ⧉ Seleccionar todo',
  'toast.needItemHeight': 'Selecciona un item primero — Ctrl+↑/↓ edita la altura de la clave',
  'toast.needItemBend': 'Selecciona un item primero — Ctrl+[ / Ctrl+] doblan su trayectoria',
  'toast.needItemSlide': 'Selecciona un item primero — Ctrl+←/→ desliza su punto por el campo',
  'toast.needItemRepeat': 'Selecciona un jugador o item primero — ↻ Otra vez está en el menú del item',
  'toast.ballNoActionX': 'Los balones y las zonas no tienen claves de acción — X necesita un jugador, cono u objetivo en el grupo',
  'toast.ballNoActionShift': 'Los balones y las zonas no tienen claves de acción — ↻ Otra vez necesita un jugador, cono u objetivo en el grupo',
  'toast.groupOneRemove': 'Solo un item en el grupo — el botón − de la barra lo elimina',
  'toast.noArrowOn': 'Ninguna flecha ENCENDIDA en este grupo en ese momento — G las enciende primero',
  'toast.rootTrunk': 'La rama raíz no se puede borrar — es el tronco del que cuelgan todas',
  'toast.branchDeleted': 'Rama borrada — el botón ↩ la devuelve',
  'toast.nothingPlaced': 'Nada colocado aún — Shift+1…8 primero',
  'toast.nothingOnPitch': 'Nada en el campo para seleccionar — ⇧1-6 arma un item para colocar',
  'toast.alreadyFree': 'Ya está libre en este momento — nada que soltar',
  'toast.needPosKey': 'Nada desde donde medir — pulsa K para clavar una posición primero',
  'toast.needSelectFly': 'Nada seleccionado — toca o clic en un item (o pulsa N) y luego F o un doble toque para volar hasta allí',
  'toast.rippleSeamBlocked': 'Bloqueado por la costura heredada en {t} — nada se movió',
  'seam.keysReadOnly': 'Toda clave aquí es la que esta rama heredó en la unión — solo lectura · ✚ en el menú del item clava una que puedes mover',
  'seam.windowsReadOnly': 'Toda ventana aquí es la que esta rama heredó en la unión — solo lectura · ✚ en el menú del item clava una que puedes cambiar',
  'toast.groupSlidAll': 'Los {n} se deslizaron {d} m — {who} ahora en {x}, {z} m',
  'toast.groupSlidOne': '{who} ahora en {x}, {z} m',
  'toast.rippleKeyBlocked': 'Bloqueado — una clave está en {t} · arrastra el ◆ de la línea de tiempo para moverla',
  'toast.rippleNothingAfter': 'Nada después de {t} — deja el corte antes de una clave',
  'toast.nudgeKeyBlocked': 'Bloqueado — ya hay una clave en {t} · arrastra el ◆ más allá, o 1 s ▸▸',
  'toast.noRoomToStep': 'La rama va de 0–{t} — no hay sitio para ese paso · arrastra el borde de la regla para abrir sitio',
  "toast.edgeCross": "El borde de la ventana no puede cruzar a su vecina en {t}",
  'toast.edgeStepped': 'Borde de ventana {from} → {to} · arrastra el borde, o ◂ 0.25 / 0.25 ▸',

  'zone.edit': '✎ EDITAR ZONA',
  'zone.exitEditHint': 'Terminar de moldear — + Añadir, ⇱ Mover y − Eliminar moldean la zona',
  'zone.add': '+ Añadir',
  'zone.remove': '− Eliminar',
  'zone.addHint': 'Toca el campo para añadir · pulsa una esquina para moverla — suéltala sobre un jugador y la zona lo sigue',
  'zone.removeHint': 'Toca una esquina para eliminarla.',
  'zone.linkBar': 'Toca un jugador o el balón para vincular este punto · ✕ cancela (Esc en un teclado)',
  'zone.linkCancel': 'Cancelar el vínculo de este punto',

};

const DICTS = { en, pt, es };

/**
 * Read one string in one locale — the SAME path `t()` takes, exported so the
 * tests can prove a label exists in every language instead of trusting that it
 * does. A missing key returns the key itself, which is exactly what a parity
 * test looks for.
 */
export function translateKey(code, key, vars) {
  return translate(DICTS[code] || DICTS.en, key, vars);
}

export const LANG_CODES = Object.keys(DICTS);

/**
 * Money, written the way the coach writes numbers.
 *
 * The plans page prints "$1.99". A Portuguese coach reads "$1,99" — a comma
 * is a decimal point there, and "$1.99" reads as one hundred and ninety-nine.
 * One helper so the visualizer cannot disagree with the website about what the
 * same price looks like, and so no component has to remember that a number has
 * a locale at all.
 *
 * Returns null for anything that is not a real number: a price the backend did
 * not send must render as NO price — never as "$NaN", and never as "$0.00".
 */
export function formatPrice(lang, value) {
  // Number('') and Number(null) are 0, not NaN, so a missing price would have
  // rendered as "$0.00". Premium is not free: a wrong number on a price tag is
  // worse than no number at all. Only a finite number gets through.
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return '$' + new Intl.NumberFormat(lang || 'en', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Simple {var} substitution: t('tl.posKey', { t: '0:05' }). */
function translate(dict, key, vars) {
  let str = dict[key] !== undefined ? dict[key] : (en[key] !== undefined ? en[key] : key);
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = String(str).split(`{${k}}`).join(String(v));
    }
  }
  return str;
}

/**
 * The context DEFAULT, not null. A component that reaches for t() outside the
 * provider — a unit test that renders one button on its own, a future surface
 * mounted before the provider — used to die with "Cannot destructure property
 * 't' of null". English is the source language every dictionary is written
 * against, so falling back to it is honest, and it keeps the crash out of the
 * product.
 */
const FALLBACK_LANG = 'en';
const fallbackI18n = {
  t: (key, vars) => translateKey(FALLBACK_LANG, key, vars),
  lang: FALLBACK_LANG,
  setLang: () => {},
  langs: LANG_CODES.map((code) => ({
    code,
    flag: DICTS[code]['lang.flag'] || '🌐',
    name: DICTS[code]['lang.name'] || code,
  })),
  flag: DICTS[FALLBACK_LANG]['lang.flag'] || '🌐',
  otherLang: LANG_CODES.find((c) => c !== FALLBACK_LANG) || 'en',
  otherFlag: (DICTS[LANG_CODES.find((c) => c !== FALLBACK_LANG)] || {})['lang.flag'] || '🌐',
};

const I18nContext = createContext(fallbackI18n);

/** Read the backend `lang` cookie as a fallback ('pt-BR' → 'pt'). */
function cookieLang() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)lang=([^;]+)/);
    if (!m) return null;
    const code = m[1].split('-')[0];
    return DICTS[code] ? code : null;
  } catch (e) { return null; }
}

const SUPPORTED = Object.keys(DICTS);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem('kv2_lang');
      if (SUPPORTED.includes(saved)) return saved;
      const fromCookie = cookieLang();
      if (fromCookie) return fromCookie;
    } catch (e) { /* ignore */ }
    return 'en';
  });

  const setLang = useCallback((code) => {
    const next = SUPPORTED.includes(code) ? code : 'en';
    setLangState(next);
    try { localStorage.setItem('kv2_lang', next); } catch (e) { /* ignore */ }
  }, []);

  const t = useCallback((key, vars) => translate(DICTS[lang], key, vars), [lang]);

  // All available languages — derived from the dictionaries, so adding a
  // language to DICTS automatically adds its pill to the File-menu switcher.
  const langs = useMemo(
    () => Object.keys(DICTS).map((code) => ({
      code,
      flag: DICTS[code]['lang.flag'] || '🌐',
      name: DICTS[code]['lang.name'] || code,
    })),
    []
  );

  const value = useMemo(() => ({
    t,
    price: (value) => formatPrice(lang, value),
    lang,
    setLang,
    langs,
    flag: DICTS[lang]['lang.flag'],
    otherLang: Object.keys(DICTS).find((c) => c !== lang) || 'en',
    otherFlag: (Object.keys(DICTS).find((c) => c !== lang) && DICTS[Object.keys(DICTS).find((c) => c !== lang)]['lang.flag']) || '🌐',
  }), [t, lang, setLang, langs]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  // The default covers a component mounted without the provider; the ?? covers
  // an explicit <I18nContext.Provider value={null}> from an older call site.
  return useContext(I18nContext) || fallbackI18n;
}

export default useI18n;
